import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {X, Search, Play, Pause, Upload, Music, Image as ImageIcon, Type, Folder, Layout, Sparkles, Grid, FileText, Download, ChevronRight, Plus, Minus, Volume2, VolumeX, User, Bell, GripVertical, Trash2, Scissors, Crop, FlipHorizontal, FlipVertical, Eye, Check , AlignLeft , AlignCenter , AlignRight , Bold , Italic , Underline , Palette, Maximize2, Minimize2, Mic, Smile, Crown} from 'lucide-react';
import { uploadToBunnyCDN } from '../services/bunnyUpload';

// Optional dependency for full emoji list + search.
// Install: npm i emoji-picker-react
let EmojiPickerComponent = null;
try {
  // eslint-disable-next-line global-require
  EmojiPickerComponent = require('emoji-picker-react').default;
} catch (_) {
  EmojiPickerComponent = null;
}


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BASE_PIXELS_PER_SECOND = 8;
const MIN_PROJECT_DURATION = 0;
// Extra empty space after the last visual element so you can drag clips further in time
const TIMELINE_PADDING_SECONDS = 30;
// Keep enough empty workspace on the timeline so you can place clips further (e.g. at 6:00)
const TIMELINE_WORKSPACE_MIN_SECONDS = 600;

const TRACK_LABEL_WIDTH = 90;

// Timeline UX: keep the visual minimum width consistent with the minimum duration
// so we never display a clip longer than it actually plays.
const MIN_TEXT_DURATION_SECONDS = 0.2;
const MIN_CLIP_WIDTH_PX = 2;

// Minimum duration (seconds) for timeline clips so resize handles remain usable.
const MIN_TIMELINE_CLIP_DURATION = 0.1;
export default function CreatorStudio({ onClose, allTracks, moodCategories, embedded = false, onStoryPublished, userIsPremium = false, onOpenSubscription }) {
  const { t } = useTranslation();
  const ct = useCallback((key, fallback) => (t ? t(key) : fallback) || fallback, [t]);

  const videoFormats = useMemo(() => ([
    { id: 'mobile-9-16', label: ct('creator.format.mobile', 'Mobile'), description: ct('creator.format.mobileDesc', '1080×1920 (9:16)'), width: 1080, height: 1920 },
    { id: 'landscape-16-9', label: ct('creator.format.landscape', 'Paysage'), description: ct('creator.format.landscapeDesc', '1920×1080 (16:9)'), width: 1920, height: 1080 },
    { id: 'square-1-1', label: ct('creator.format.square', 'Carré'), description: ct('creator.format.squareDesc', '1080×1080 (1:1)'), width: 1080, height: 1080 },
  ]), [ct]);
  // Basic states
  const [selectedTool, setSelectedTool] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importTab, setImportTab] = useState('video');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const useGlobalToolPopovers = true;
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioTracks, setAudioTracks] = useState([]);
  const audioTracksRef = useRef([]);
  useEffect(() => { audioTracksRef.current = audioTracks; }, [audioTracks]);
  const [imageTracks, setImageTracks] = useState([]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Emoji picker (adds emoji as an image track)
  const [emojiQuery, setEmojiQuery] = useState('');

  // Small fallback list (if emoji-picker-react isn't installed)
  const builtInEmojis = useMemo(() => ([
    "😀","😁","😂","🤣","😃","😄","😅","😆","😉","😊","😍","😘","😗","😙","😚","😋","😛","😜","🤪","😝",
    "🧐","🤓","😎","🥳","😏","😒","😞","😔","😟","😕","🙁","☹️","😣","😖","😫","😩","🥺","😢","😭","😤",
    "😠","😡","🤬","🤯","😳","🥵","🥶","😱","😨","😰","😥","😓","🤗","🤔","🫡","🤭","🫢","🤫","🤥",
    "👍","👎","👏","🙌","🫶","🤝","🙏","💪","🔥","✨","⭐","🌟","💥","💯","❤️","🧡","💛","💚","💙","💜",
    "🖤","🤍","🤎","💔","🎉","🎊","🎈","🎁","🎬","🎵","🎶","🎧","📢","📣","📷","🎥","🧠","👀","👋","✅",
    "❌","⚠️","💡","📌","📝","📍","📎","🕒","⏱️","⌛","🚀","🏆","🌈","☀️","🌙","☁️","🌧️","❄️","🍀","🍕",
    "🍔","🍟","🍣","🍰","☕","🍺","🍷","⚽","🏀","🎮","💻","📱"
  ]), []);

  const emojiToDataUrl = useCallback((emoji) => {
    // Use a padded viewBox so the emoji glyph never gets clipped by the SVG bounds.
    // (Some color-emoji fonts draw outside the nominal box.)
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="-64 -64 640 640" preserveAspectRatio="xMidYMid meet">
      <rect width="100%" height="100%" fill="transparent"/>
      <text x="256" y="270" text-anchor="middle" dominant-baseline="middle"
        font-size="420" font-family="Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif">${emoji}</text>
    </svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }, []);

  const addEmojiToTimeline = useCallback((emoji) => {
    const startTime = Math.max(0, Number(currentTimeRef.current || 0));
    const id = `emoji_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const url = emojiToDataUrl(emoji);

    setImageTracks((prev) => ([
      ...prev,
      {
        id,
        type: 'image',
        url,
        name: `Emoji ${emoji}`,
        startTime,
        duration: 3,
        x: 50,
        y: 50,
        w: 18,
        h: 18,
        opacity: 100,
        rotation: 0,
        flipH: false,
        flipV: false
      }
    ]));
  }, [emojiToDataUrl]);
const [textTracks, setTextTracks] = useState([]);
  const [editingTextId, setEditingTextId] = useState(null);

  // Text editing helpers
  const textEditableRefs = useRef({});
  const pendingTextEditsRef = useRef({});

  // Focus the editable div when entering edit mode (single-click or double-click)
  useEffect(() => {
    if (!editingTextId) return;
    const el = textEditableRefs.current?.[editingTextId];
    if (!el) return;
    // Defer to ensure the element is in the DOM and contentEditable is applied
    const t = window.setTimeout(() => {
      try {
        el.focus();
        // Move caret to end
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      } catch (_) {}
    }, 0);
    return () => window.clearTimeout(t);
  }, [editingTextId]);

  // Media library (Canva-like)
  const [videoAssets, setVideoAssets] = useState([]);
  const [imageAssets, setImageAssets] = useState([]);
  const [audioAssets, setAudioAssets] = useState([]);
  const videoAssetsRef = useRef([]);
  const imageAssetsRef = useRef([]);
  const audioAssetsRef = useRef([]);
  useEffect(() => { videoAssetsRef.current = videoAssets; }, [videoAssets]);
  useEffect(() => { imageAssetsRef.current = imageAssets; }, [imageAssets]);
  useEffect(() => { audioAssetsRef.current = audioAssets; }, [audioAssets]);

  const updateVideoAsset = useCallback((id, patch) => {
    setVideoAssets((prev) => prev.map((a) => a.id === id ? { ...a, ...patch } : a));
  }, []);

  const updateImageAsset = useCallback((id, patch) => {
    setImageAssets((prev) => prev.map((a) => a.id === id ? { ...a, ...patch } : a));
  }, []);

  const updateAudioAsset = useCallback((id, patch) => {
    setAudioAssets((prev) => prev.map((a) => a.id === id ? { ...a, ...patch } : a));
  }, []);

  const uploadAssetToBunny = useCallback(async (file, folder, onProgress) => {
    return uploadToBunnyCDN(file, folder, onProgress);
  }, []);

  // Project format (canvas size / aspect ratio)
  const [projectFormat, setProjectFormat] = useState(() => videoFormats.find(f => f.id === 'mobile-9-16') || videoFormats[0]);
  


  const projectAspect = useMemo(() => {
    const w = Number(projectFormat?.width) || 1920;
    const h = Number(projectFormat?.height) || 1080;
    return { w, h, css: `${w} / ${h}`, ratio: w / h };
  }, [projectFormat]);
const [isFormatPickerOpen, setIsFormatPickerOpen] = useState(false);

  // Drag & drop (import)
  const [isDropActive, setIsDropActive] = useState(false);

  const dropzoneStyle = useMemo(() => {
    const ratio = projectAspect.ratio || 16 / 9;
    // Bigger and responsive: landscape uses width, portrait uses height (so it feels tall), square uses width.
    if (Math.abs(ratio - 1) < 0.02) {
      return { width: 'min(600px, 86%)', aspectRatio: `${projectAspect.w} / ${projectAspect.h}` };
    }
    if (ratio > 1) {
      return { width: 'min(760px, 92%)', aspectRatio: `${projectAspect.w} / ${projectAspect.h}` };
    }
    // portrait
    return { height: 'min(72vh, 760px)', aspectRatio: `${projectAspect.w} / ${projectAspect.h}` };
  }, [projectAspect]);


  const videoImportInputRef = useRef(null);
  const mediaImportInputRef = useRef(null); // video + image (used for the central dropzone)
  const imageImportInputRef = useRef(null);
  const audioImportInputRef = useRef(null);
  const [showMusicModal, setShowMusicModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumTrack, setPremiumTrack] = useState(null);
  const previewAudioRef = useRef(null);
  const previewStopTimerRef = useRef(null);
  const [previewTrackId, setPreviewTrackId] = useState(null);
  const [previewIsPlaying, setPreviewIsPlaying] = useState(false);
  const [zoom, setZoom] = useState(100);
  
  const [previewZoom, setPreviewZoom] = useState(100);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  // Voice-over recording
  const [voiceoverArmed, setVoiceoverArmed] = useState(false);
  const [isVoiceoverRecording, setIsVoiceoverRecording] = useState(false);
  const [voiceoverCountdown, setVoiceoverCountdown] = useState(null); // 3..0
  const voiceoverRecorderRef = useRef(null);
  const voiceoverStreamRef = useRef(null);
  const voiceoverChunksRef = useRef([]);
  const voiceoverTrackIdRef = useRef(null);
  const voiceoverStartTimeRef = useRef(0);

  // Export
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('mp4'); // export serveur
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0); // 0..1
  const [exportStatus, setExportStatus] = useState('');
  const [exportUrl, setExportUrl] = useState(null);
  const [isDownloadingExport, setIsDownloadingExport] = useState(false);
  const [exportStageCurrent, setExportStageCurrent] = useState(0);
  const [exportStageProgress, setExportStageProgress] = useState({ upload: 0, render: 0, uploadOut: 0 });
  const uploadOutStartRef = useRef(null);
  const exportBlobRef = useRef(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState('');
  const exportRecorderRef = useRef(null);
  const exportChunksRef = useRef([]);
  const exportAbortRef = useRef(false);
  const exportStartPerfRef = useRef(0);
  const exportDurationRef = useRef(0);

  const exportCanvasRef = useRef(null);
  const exportRafRef = useRef(null);
  const exportImageCacheRef = useRef(new Map());
  const renderPollRef = useRef(null);
  const renderJobRef = useRef(null);



  const previewStageRef = useRef(null);
  const waveformCacheRef = useRef(new Map());
  const activePointerActionRef = useRef(null); // { type, id, startX, startY, startState }


  const suppressNextClickRef = useRef(false);
const [projectDuration, setProjectDuration] = useState(TIMELINE_WORKSPACE_MIN_SECONDS);
  const [musicFilter, setMusicFilter] = useState('all');
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
  const [draggingTrack, setDraggingTrack] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  // When selecting a clip on the timeline, ensure the playhead is inside the clip
  // so the preview actually shows the selected overlay (text/image) or segment.
  const revealClipInPreview = useCallback((startSeconds, durationSeconds) => {
    const start = Math.max(0, Number(startSeconds) || 0);
    const duration = Math.max(0, Number(durationSeconds) || 0);

    // Pause when the user selects something (editor-style behavior)
    setIsPlaying(false);

    // If duration is 0 (shouldn't happen), just jump to start.
    if (duration <= 0) {
      setCurrentTime(start);
      return;
    }

    const end = start + duration;
    if (currentTime < start || currentTime > end) {
      // Jump slightly inside the clip so we never land exactly on an exclusive boundary.
      const epsilon = Math.min(0.05, duration / 2);
      setCurrentTime(Math.min(end - 0.001, start + epsilon));
    }
  }, [currentTime]);
  const [timelineClipboard, setTimelineClipboard] = useState(null); // { type: 'video-segment', data: {...} }
  const [styleClipboard, setStyleClipboard] = useState(null); // { type: 'video-segment', style: {...} }
  const [contextMenu, setContextMenu] = useState(null); // { segmentId, anchorRect?: {left, top, right, bottom, width, height} }
  const [contextMenuPos, setContextMenuPos] = useState(null); // { left, top }
  const contextMenuRef = useRef(null);

  const [contextSubmenu, setContextSubmenu] = useState(null); // 'layer' | 'align' | null


  const draggingTrackRef = useRef(null);
  useEffect(() => {
    draggingTrackRef.current = draggingTrack;
  }, [draggingTrack]);

  // Close context menu on any outside click / scroll / resize
  useEffect(() => {
    const close = () => {
      setContextMenu(null);
      setContextMenuPos(null);
      setContextSubmenu(null);
    };
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close, true);
    return () => {
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close, true);
    };
  }, []);

  // Position the context menu above the clicked element and keep it within the viewport
  useLayoutEffect(() => {
    if (!contextMenu) return;

    const pad = 10;
    const el = contextMenuRef.current;
    const a = contextMenu.anchorRect;

    // If we can't measure yet, keep the seeded position.
    if (!el || !a) return;

    const menuW = el.offsetWidth || 320;
    const menuH = el.offsetHeight || 0;

    // Prefer above the element
    let left = a.left;
    let top = a.top - menuH - 8;

    // If above doesn't fit, place below
    if (top < pad) top = a.bottom + 8;

    // Clamp to viewport
    const maxLeft = window.innerWidth - menuW - pad;
    const maxTop = window.innerHeight - menuH - pad;
    left = Math.min(Math.max(pad, left), Math.max(pad, maxLeft));
    top = Math.min(Math.max(pad, top), Math.max(pad, maxTop));

    setContextMenuPos({ left, top });
  }, [contextMenu]);


  // Global safety-net: ensure track drags always stop even if a pointerup is missed
  useEffect(() => {
    const stop = () => {
      if (draggingTrackRef.current) {
        setDraggingTrack(null);
      }
    };
    document.addEventListener('pointerup', stop, true);
    document.addEventListener('pointercancel', stop, true);
    document.addEventListener('mouseup', stop, true);
    window.addEventListener('blur', stop, true);
    return () => {
      document.removeEventListener('pointerup', stop, true);
      document.removeEventListener('pointercancel', stop, true);
      document.removeEventListener('mouseup', stop, true);
      window.removeEventListener('blur', stop, true);
    };
  }, []);


// Prevent click-through after a drag/resize/crop so the selection doesn't jump to another element.
useEffect(() => {
  const onClickCapture = (e) => {
    if (!suppressNextClickRef.current) return;
    suppressNextClickRef.current = false;
    try { e.preventDefault(); } catch (_) {}
    try { e.stopPropagation(); } catch (_) {}
    try { e.stopImmediatePropagation?.(); } catch (_) {}
  };
  window.addEventListener('click', onClickCapture, true);
  return () => window.removeEventListener('click', onClickCapture, true);
}, []);

  // Video segments - each segment has its own video URL
  const [videoSegments, setVideoSegments] = useState([]);
  // Currently playing segment ID
  const [activeSegmentId, setActiveSegmentId] = useState(null);
  const [activeVideoSrc, setActiveVideoSrc] = useState(null);

  const hasTimelineElements = (videoSegments.length > 0) || (imageTracks.length > 0) || (textTracks.length > 0);

  
  // Video editing states (applied to selected segment)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showOpacitySlider, setShowOpacitySlider] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [showFadeControls, setShowFadeControls] = useState(false);
  const [notification, setNotification] = useState(null);

  // Close tool popovers when clicking outside
  useEffect(() => {
    const onPointerDown = (e) => {
      if (!showVolumeSlider && !showFadeControls && !showOpacitySlider && !isFormatPickerOpen) return;
      const target = e.target;
      if (!target) return;
      const keepOpen = target.closest?.('[data-tool-trigger], [data-tool-popover]');
      if (keepOpen) return;
      setShowVolumeSlider(false);
      setShowFadeControls(false);
      setShowOpacitySlider(false);
      setIsFormatPickerOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [showVolumeSlider, showFadeControls, showOpacitySlider, isFormatPickerOpen]);
  
  // Single preview video element (we swap src depending on the active segment)
  const videoRef = useRef(null);
  const pendingVideoSeekRef = useRef(null);
  const activeVideoSrcRef = useRef(null);

  // Playback helpers (for gaps/images + segment boundaries)
  const isPlayingRef = useRef(false);
  const currentTimeRef = useRef(0);

  const stopVoiceoverRecording = useCallback(async () => {
    try {
      const rec = voiceoverRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        rec.stop();
      }
    } catch (_) {}
  }, []);


  // Grow voiceover clip while recording, and keep it synced with playhead
  useEffect(() => {
    if (!isVoiceoverRecording) return;
    const id = voiceoverTrackIdRef.current;
    if (!id) return;
    const start = voiceoverStartTimeRef.current || 0;
    const dur = Math.max(0, (currentTimeRef.current || 0) - start);
    setAudioTracks(prev => prev.map(t => (t.id === id ? { ...t, duration: dur, originalDuration: dur } : t)));
  }, [currentTime, isVoiceoverRecording]);

  // If user stops playback while recording, stop recording too
  useEffect(() => {
    if (!isVoiceoverRecording) return;
    if (!isPlaying) {
      stopVoiceoverRecording();
    }
  }, [isPlaying, isVoiceoverRecording, stopVoiceoverRecording]);

  const playbackRafRef = useRef(null);
  const playbackLastTsRef = useRef(null);

  const audioRefs = useRef({});
  const timelineRef = useRef(null);
  const visualLanesRef = useRef(null);

  // Keep latest visual tracks in refs for overlap/lane computation inside pointer handlers
  const videoSegmentsRef = useRef([]);
  const imageTracksRef = useRef([]);
  const textTracksRef = useRef([]);
  useEffect(() => { videoSegmentsRef.current = videoSegments; }, [videoSegments]);
  useEffect(() => { imageTracksRef.current = imageTracks; }, [imageTracks]);
  useEffect(() => { textTracksRef.current = textTracks; }, [textTracks]);

  // Overlap test with a small epsilon to avoid float/rounding artifacts that can
  // incorrectly force clips onto new lanes when they are intended to be adjacent.
  // (e.g. end == start or end is off by a few ms because we round to 0.01s in drag)
  const OVERLAP_EPS = 0.02; // 20ms tolerance
  const intervalsOverlap = (aStart, aEnd, bStart, bEnd) => aStart < (bEnd - OVERLAP_EPS) && bStart < (aEnd - OVERLAP_EPS);

  const getVisualItemsSnapshot = useCallback(() => {
    const videos = (videoSegmentsRef.current || []).map((s) => ({
      type: 'video-segment',
      id: s.id,
      lane: s.lane ?? 0,
      start: s.timelineStart ?? 0,
      duration: s.duration ?? 0,
    }));
    const images = (imageTracksRef.current || []).map((t) => ({
      type: 'image',
      id: t.id,
      lane: t.lane ?? 0,
      start: t.startTime ?? 0,
      duration: t.duration ?? 0,
    }));
    const texts = (textTracksRef.current || []).map((t) => ({
      type: 'text',
      id: t.id,
      lane: t.lane ?? 0,
      start: t.startTime ?? 0,
      duration: t.duration ?? 0,
    }));
    return [...videos, ...images, ...texts];
  }, []);

  const findAvailableLane = useCallback(({ start, duration, excludeType, excludeId, preferredLane }) => {
    const items = getVisualItemsSnapshot();
    const end = start + duration;
    const maxLane = Math.max(0, ...items.map((i) => i.lane ?? 0));
    const lanesToTry = [];
    if (typeof preferredLane === 'number' && preferredLane >= 0) lanesToTry.push(preferredLane);
    for (let i = 0; i <= maxLane + 1; i++) if (!lanesToTry.includes(i)) lanesToTry.push(i);

    for (const lane of lanesToTry) {
      const conflicts = items.some((i) => {
        if (i.lane !== lane) return false;
        if (excludeType && excludeId && i.type === excludeType && i.id === excludeId) return false;
        return intervalsOverlap(start, end, i.start, i.start + i.duration);
      });
      if (!conflicts) return lane;
    }
    return maxLane + 1;
  }, [getVisualItemsSnapshot]);
  const timelineContainerRef = useRef(null);
  // Scrub state (playhead drag) - refs avoid lost mouseup/pointerup
  const isScrubbingRef = useRef(false);
  const scrubPointerIdRef = useRef(null);
  const latestScrubTimeRef = useRef(0);

  const menuItems = [
    { id: 'texte', icon: Type, label: ct('creator.menu.text', 'Texte') },
    { id: 'importer', icon: Upload, label: ct('creator.menu.import', 'Importer') },
    { id: 'emojis', icon: Smile, label: ct('creator.menu.emojis', 'Emojis') },
  ];

  // Fermer le tiroir par défaut au montage
  useEffect(() => {
    setDrawerOpen(false);
    setSelectedTool(null);
  }, []);

  const openDrawer = (id) => {
    setSelectedTool((prev) => {
      setDrawerOpen((prevOpen) => (prev === id ? !prevOpen : true));
      return id;
    });
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const togglePreviewFullscreen = () => {
    setIsPreviewFullscreen((v) => !v);
  };

  // Apply video volume when segment is selected
  useEffect(() => {
    if (selectedTrack?.type === 'video-segment') {
      const segment = videoSegments.find(s => s.id === selectedTrack.id);
      if (segment && videoRef.current) {
        videoRef.current.volume = (segment.volume ?? 100) / 100;
      }
    }
  }, [selectedTrack, videoSegments]);

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 2000);
  };

  const renderStages = useMemo(() => ([
    { key: 'upload', label: ct('creator.stages.upload', 'Upload des fichiers') },
    { key: 'render', label: ct('creator.stages.render', 'Rendu serveur') },
    { key: 'uploadOut', label: ct('creator.stages.uploadOut', 'Upload final') }
  ]), [ct]);

  const resetExportStages = useCallback(() => {
    setExportStageCurrent(0);
    setExportStageProgress({ upload: 0, render: 0, uploadOut: 0 });
    uploadOutStartRef.current = null;
  }, []);

  const setStageProgress = useCallback((key, value) => {
    const nextVal = Math.max(0, Math.min(1, Number(value) || 0));
    setExportStageProgress((prev) => {
      const current = prev?.[key] ?? 0;
      if (nextVal <= current) return prev;
      return { ...prev, [key]: nextVal };
    });
  }, []);

  useEffect(() => {
    const { upload = 0, render = 0, uploadOut = 0 } = exportStageProgress || {};
    const overall = Math.max(0, Math.min(1, (upload + render + uploadOut) / 3));
    setExportProgress(overall);
  }, [exportStageProgress]);

  const downloadExportFile = useCallback(async (url) => {
    if (!url) return;
    setIsDownloadingExport(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(ct('creator.export.downloadError', 'Téléchargement impossible.'));
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const ext = exportFormat === 'mp4' ? 'mp4' : 'webm';
      const filename = `vinisys-export-${Date.now()}.${ext}`;
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => {
        try { URL.revokeObjectURL(objectUrl); } catch (_) {}
      }, 1000);
    } catch (err) {
      showNotification(err?.message || ct('creator.export.downloadError', 'Téléchargement impossible.'));
    } finally {
      setIsDownloadingExport(false);
    }
  }, [exportFormat, showNotification]);

  const openFormatPicker = useCallback(() => {
    setIsFormatPickerOpen(true);
  }, []);

  const applyProjectFormat = useCallback((fmt) => {
    setProjectFormat(fmt);
    setIsFormatPickerOpen(false);
    showNotification(ct('creator.format.applied', 'Format : {{format}}').replace('{{format}}', fmt.description));
  }, []);

  const getPixelsPerSecond = useCallback(() => {
    return BASE_PIXELS_PER_SECOND * (zoom / 100);
  }, [zoom]);

  const getPositionFromTime = useCallback((time) => {
    return time * getPixelsPerSecond();
  }, [getPixelsPerSecond]);

  const getTimeFromPosition = useCallback((position) => {
    return position / getPixelsPerSecond();
  }, [getPixelsPerSecond]);

  const getTimelineWidth = useCallback(() => {
    return projectDuration * getPixelsPerSecond();
  }, [projectDuration, getPixelsPerSecond]);

  const extendProjectIfNeeded = useCallback((endTime) => {
    if (endTime > projectDuration - 30) {
      setProjectDuration(Math.ceil(endTime / 60) * 60 + 60);
    }
  }, [projectDuration]);


  // Timeline end based on visual elements (video/images/text). Audio is excluded on purpose so adding music
  // doesn't automatically extend the "work area" if there is no visual content.
  const getVisualTimelineEnd = useCallback(() => {
    const videoEnd = videoSegments.reduce((maxEnd, s) => {
      const start = s.timelineStart ?? 0;
      const dur = s.duration ?? 0;
      return Math.max(maxEnd, start + dur);
    }, 0);

    const imageEnd = imageTracks.reduce((maxEnd, t) => {
      const start = t.startTime ?? 0;
      const dur = t.duration ?? 0;
      return Math.max(maxEnd, start + dur);
    }, 0);

    const textEnd = textTracks.reduce((maxEnd, t) => {
      const start = t.startTime ?? 0;
      const dur = t.duration ?? 0;
      return Math.max(maxEnd, start + dur);
    }, 0);

    return Math.max(videoEnd, imageEnd, textEnd, 0);
  }, [videoSegments, imageTracks, textTracks]);

  

  
  

  
  const montageDuration = useMemo(() => {
    const end = getVisualTimelineEnd();
    return Math.max(0, Math.ceil(end));
  }, [getVisualTimelineEnd, videoSegments, imageTracks, textTracks]);
// Keep timeline workspace large enough for free placement,
  // but compute the *real* montage length from visual elements separately.
  useEffect(() => {
    const visualEnd = getVisualTimelineEnd();

    // Desired workspace: at least a fixed minimum, and always extends beyond the last element.
    const desiredWorkspace = Math.max(
      TIMELINE_WORKSPACE_MIN_SECONDS,
      Math.ceil(visualEnd + TIMELINE_PADDING_SECONDS)
    );

    if (desiredWorkspace > projectDuration + 0.01) {
      setProjectDuration(desiredWorkspace);
    }
  }, [getVisualTimelineEnd, projectDuration]);
const buildExportStream = useCallback(() => {
    // We render a composite (video + images + text) to an offscreen canvas, then capture it as a stream.
    const canvas = document.createElement('canvas');
    const W = Number(projectFormat?.width) || 1920;
    const H = Number(projectFormat?.height) || 1080;
    canvas.width = W;
    canvas.height = H;
    exportCanvasRef.current = canvas;

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx || typeof canvas.captureStream !== 'function') return null;

    const fps = 30;
    const vStream = canvas.captureStream(fps);

    // Prepare image cache (HTMLImageElement) for faster draws
    const getImg = (url) => {
      if (!url) return null;
      const cache = exportImageCacheRef.current;
      if (cache.has(url)) return cache.get(url);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
      cache.set(url, img);
      return img;
    };

    const drawFrame = () => {
      const t = exportStartPerfRef.current ? Math.max(0, (performance.now() - exportStartPerfRef.current) / 1000) : Number(currentTimeRef.current || 0);

      // Background
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);

      // Base video frame if we're inside a segment
      const segNow = (videoSegmentsRef.current || []).find(s => {
        const start = Number(s.timelineStart ?? 0);
        const dur = Number(s.duration ?? 0);
        return t >= start && t <= (start + dur);
      });
      if (segNow && videoRef.current && videoRef.current.readyState >= 2) {
        try {
          ctx.globalAlpha = 1;
          ctx.drawImage(videoRef.current, 0, 0, W, H);
        } catch (_) {}
      }

      // Images active at time t
      imageTracksRef.current.forEach((tr) => {
        const s = Number(tr.startTime || 0);
        const e = s + Number(tr.duration || 0);
        if (t < s || t > e) return;
        const img = getImg(tr.url);
        if (!img || !img.complete) return;

        const x = (Number(tr.x) || 50) / 100 * W;
        const y = (Number(tr.y) || 50) / 100 * H;
        const w = (Number(tr.w) || 35) / 100 * W;
        const h = (Number(tr.h) || 35) / 100 * H;

        ctx.save();
        ctx.globalAlpha = Math.max(0, Math.min(1, (Number(tr.opacity ?? 100) / 100)));
        // Flip support
        const flipH = !!tr.flipH;
        const flipV = !!tr.flipV;
        ctx.translate(x, y);
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
        // Draw centered
        ctx.drawImage(img, -w / 2, -h / 2, w, h);
        ctx.restore();
      });

      // Text active at time t
      textTracksRef.current.forEach((tr) => {
        const s = Number(tr.startTime || 0);
        const e = s + Number(tr.duration || 0);
        if (t < s || t > e) return;

        const x = (Number(tr.x) || 50) / 100 * W;
        const y = (Number(tr.y) || 50) / 100 * H;

        const boxW = (Number(tr.w) || 60) / 100 * W;
        const boxH = (Number(tr.h) || 20) / 100 * H;

        const opacity = Math.max(0, Math.min(1, Number(tr.opacity ?? 100) / 100));

        // Keep the same proportions as in the preview: fontSize is authored in preview pixels,
        // so scale it to the export canvas resolution.
        const previewW = (previewStageRef.current && (previewStageRef.current.clientWidth || previewStageRef.current.getBoundingClientRect?.().width)) || W;
        const previewH = (previewStageRef.current && (previewStageRef.current.clientHeight || previewStageRef.current.getBoundingClientRect?.().height)) || H;
        const scaleX = previewW ? (W / previewW) : 1;
        const scaleY = previewH ? (H / previewH) : 1;
        const fontScale = Math.min(scaleX, scaleY);

        const fontSize = Math.max(10, Number(tr.fontSize || 28) * fontScale);

        const fontFamilyRaw = tr.fontFamily || 'Arial';
        const fontFamily = fontFamilyRaw;
        const fontWeight = tr.fontWeight ?? (tr.bold ? 700 : 500);
        const fontStyle = tr.fontStyle ?? (tr.italic ? 'italic' : 'normal');

        const color = tr.color || '#FFFFFF';
        const align = tr.align || 'center';
        const underline = !!(tr.underline ?? tr.isUnderline);

        const bg = tr.backgroundColor || tr.bgColor || 'transparent';

        ctx.save();
        ctx.globalAlpha = opacity;

        // Background box
        if (bg && bg !== 'transparent') {
          ctx.fillStyle = bg;
          ctx.fillRect(x - boxW / 2, y - boxH / 2, boxW, boxH);
        }

        ctx.fillStyle = color;
        ctx.textAlign = align;
        ctx.textBaseline = 'middle';
        ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

        const text = (tr.text ?? tr.content ?? '').toString();
        const words = text.split(/\s+/).filter(Boolean);
        const lines = [];
        let line = '';
        const maxWidth = Math.max(50, boxW);

        for (const word of words) {
          const test = line ? `${line} ${word}` : word;
          if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = word;
          } else {
            line = test;
          }
        }
        if (line) lines.push(line);

        const lineH = fontSize * 1.2;
        const totalH = lines.length * lineH;
        let yy = y - totalH / 2 + lineH / 2;

        for (const ln of lines) {
          ctx.fillText(ln, x, yy);

          if (underline) {
            const w = ctx.measureText(ln).width;
            const underlineY = yy + fontSize * 0.45;
            ctx.beginPath();
            ctx.moveTo(x - w / 2, underlineY);
            ctx.lineTo(x + w / 2, underlineY);
            ctx.lineWidth = Math.max(1, (fontSize / 14));
            ctx.strokeStyle = color;
            ctx.stroke();
          }

          yy += lineH;
        }

        ctx.restore();
      });

      // (no rAF scheduling here; export uses a fixed FPS timer)
    };

    // Start drawing loop at a fixed FPS (more stable for export than requestAnimationFrame)
    const frameIntervalMs = Math.round(1000 / fps);
    drawFrame();
    exportRafRef.current = window.setInterval(drawFrame, frameIntervalMs);

    
    // Mix audio using WebAudio. Prefer decoding audio buffers (smooth, no buffering hiccups),
    // and fall back to media elements only if decoding fails (e.g. CORS).
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioCtx = AudioCtx ? new AudioCtx() : null;

    if (!audioCtx) {
      const cleanup = () => {
        try { window.clearInterval(exportRafRef.current); } catch (_) {}
        exportRafRef.current = null;
        try { exportCanvasRef.current = null; } catch (_) {}
      };
      return { stream: vStream, cleanup };
    }

    const dest = audioCtx.createMediaStreamDestination();
    const compressor = audioCtx.createDynamicsCompressor();
    compressor.connect(dest);

    const activeNodes = [];

    const safeFetchArrayBuffer = async (url) => {
      const res = await fetch(url);
      return await res.arrayBuffer();
    };

    const scheduleTrack = async (track) => {
      if (!track?.url) return;
      try {
        const ab = await safeFetchArrayBuffer(track.url);
        const buf = await audioCtx.decodeAudioData(ab);
        const srcNode = audioCtx.createBufferSource();
        srcNode.buffer = buf;

        const gain = audioCtx.createGain();
        const vol = (Number(track.volume ?? 100) / 100);
        gain.gain.value = isFinite(vol) ? vol : 1;

        srcNode.connect(gain);
        gain.connect(compressor);

        const startTime = Math.max(0, Number(track.startTime ?? 0));
        const trimStart = Math.max(0, Number(track.trimStart ?? 0));
        const duration = Math.max(0, Number(track.duration ?? (buf.duration - trimStart)));

        const ctxNow = audioCtx.currentTime;
        const when = ctxNow + 0.05 + startTime;
        const offset = Math.min(trimStart, Math.max(0, buf.duration - 0.001));
        const playDur = Math.max(0.001, Math.min(duration, buf.duration - offset));

        // Apply fades (export)
        const baseGain = isFinite(vol) ? vol : 1;
        const fadeIn = Number(track.fadeIn || 0);
        const fadeOut = Number(track.fadeOut || 0);

        try {
          if (fadeIn > 0) {
            gain.gain.setValueAtTime(0, when);
            gain.gain.linearRampToValueAtTime(baseGain, when + fadeIn);
          } else {
            gain.gain.setValueAtTime(baseGain, when);
          }

          if (fadeOut > 0) {
            const endTime = when + playDur;
            const fadeStart = Math.max(when, endTime - fadeOut);
            gain.gain.setValueAtTime(baseGain, fadeStart);
            gain.gain.linearRampToValueAtTime(0, endTime);
          }
        } catch (_) {}

        srcNode.start(when, offset, playDur);
activeNodes.push(srcNode, gain);
      } catch (err) {
        // Fallback: media element source (may stutter if network/cpu)
        try {
          const el = audioRefs.current?.[track.id];
          if (!el) return;
          const node = audioCtx.createMediaElementSource(el);
          const gain = audioCtx.createGain();
          const vol = (Number(track.volume ?? 100) / 100);
          gain.gain.value = isFinite(vol) ? vol : 1;
          node.connect(gain);
          gain.connect(compressor);
          activeNodes.push(node, gain);
        } catch (_) {}
      }
    };

    const cleanup = () => {
      try { window.clearInterval(exportRafRef.current); } catch (_) {}
      exportRafRef.current = null;
      try { activeNodes.forEach(n => { try { n.disconnect(); } catch (_) {} }); } catch (_) {}
      try { audioCtx.close(); } catch (_) {}
      try { exportCanvasRef.current = null; } catch (_) {}
    };

    const scheduleAll = async () => {
      const tracks = Array.isArray(audioTracksRef.current) ? audioTracksRef.current : (audioTracks || []);
      for (const t of tracks) {
        await scheduleTrack(t);
      }
      // Include video audio if present
      try {
        const vEl = videoRef.current;
        const segments = Array.isArray(videoSegmentsRef.current)
          ? videoSegmentsRef.current
          : (videoSegments || []);
        if (vEl && segments.length) {
          const hasAudible = segments.some((s) => (Number(s?.volume ?? 100) / 100) > 0.001);
          if (hasAudible) {
            const vNode = audioCtx.createMediaElementSource(vEl);
            const vGain = audioCtx.createGain();
            vGain.gain.value = 0;
            vNode.connect(vGain);
            vGain.connect(compressor);
            activeNodes.push(vNode, vGain);

            const base = audioCtx.currentTime + 0.05;
            try { vGain.gain.setValueAtTime(0, base); } catch (_) {}

            segments.forEach((seg) => {
              const start = Math.max(0, Number(seg.startTime ?? seg.timelineStart ?? 0));
              const dur = Math.max(0, Number(seg.duration || 0));
              const vol = Math.max(0, Math.min(1, Number(seg.volume ?? 100) / 100));
              if (dur <= 0) return;

              const tStart = base + start;
              const tEnd = tStart + dur;

              try {
                vGain.gain.setValueAtTime(vol, tStart + 0.001);
                vGain.gain.setValueAtTime(vol, Math.max(tStart + 0.001, tEnd - 0.001));
                vGain.gain.setValueAtTime(0, tEnd);
              } catch (_) {}
            });
          }
        }
      } catch (_) {}
    };

    const mixed = new MediaStream([...vStream.getVideoTracks(), ...dest.stream.getAudioTracks()]);
    return { stream: mixed, cleanup, scheduleAll };

  }, [projectFormat]);

  const stopExportRender = useCallback(() => {
    exportAbortRef.current = true;
    try {
      const r = exportRecorderRef.current;
      if (r && r.state !== 'inactive') r.stop();
    } catch (_) {}
    if (renderPollRef.current) {
      try { window.clearInterval(renderPollRef.current); } catch (_) {}
      renderPollRef.current = null;
    }
    renderJobRef.current = null;
    setIsExporting(false);
    resetExportStages();
    setExportStatus(ct('creator.export.cancelled', 'Export annulé'));
  }, [resetExportStages]);

  const isRemoteUrl = useCallback((url) => {
    return typeof url === 'string' && /^https?:\/\//i.test(url);
  }, []);

  const waitForUploads = useCallback(async (onProgress) => {
    const start = Date.now();
    const timeoutMs = 15 * 60 * 1000; // 15 min
    while (true) {
      const assets = [
        ...(videoAssetsRef.current || []),
        ...(imageAssetsRef.current || []),
        ...(audioAssetsRef.current || [])
      ];
      const hasError = assets.some((a) => a.uploadStatus === 'error');
      if (hasError) throw new Error(ct('creator.export.mediaUploadFail', 'Échec upload média.'));
      const pending = assets.filter((a) => a.uploadStatus === 'uploading');
      if (!pending.length) return;
      if (typeof onProgress === 'function') {
        const avg = pending.reduce((sum, a) => sum + (Number(a.uploadProgress) || 0), 0) / pending.length;
        onProgress(Math.max(0, Math.min(100, avg)));
      }
      if (Date.now() - start > timeoutMs) throw new Error(ct('creator.export.uploadTimeout', 'Upload trop long.'));
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 500));
    }
  }, []);

  const buildExportPayload = useCallback(() => {
    const resolveVideoUrl = (seg) => {
      if (seg?.assetId) {
        const asset = videoAssets.find((a) => a.id === seg.assetId);
        if (asset?.cdnUrl) return asset.cdnUrl;
      }
      if (seg?.sourceUrl && isRemoteUrl(seg.sourceUrl)) return seg.sourceUrl;
      if (seg?.url && isRemoteUrl(seg.url)) return seg.url;
      return null;
    };

    const resolveImageUrl = (img) => {
      if (img?.assetId) {
        const asset = imageAssets.find((a) => a.id === img.assetId);
        if (asset?.cdnUrl) return asset.cdnUrl;
      }
      if (img?.sourceUrl && isRemoteUrl(img.sourceUrl)) return img.sourceUrl;
      if (img?.url && isRemoteUrl(img.url)) return img.url;
      return null;
    };

    const resolveAudioUrl = (track) => {
      if (track?.sourceUrl && isRemoteUrl(track.sourceUrl)) return track.sourceUrl;
      if (track?.url && isRemoteUrl(track.url)) return track.url;
      if (track?.file_path && isRemoteUrl(track.file_path)) return track.file_path;
      if (track?.originalTrack?.file_path && isRemoteUrl(track.originalTrack.file_path)) return track.originalTrack.file_path;
      if (track?.assetId) {
        const asset = audioAssets.find((a) => a.id === track.assetId);
        if (asset?.cdnUrl) return asset.cdnUrl;
      }
      return null;
    };

    const payload = {
      project: {
        format: projectFormat,
        duration: montageDuration,
        fps: 30,
        videoSegments: (videoSegments || []).map((s) => ({
          ...s,
          timelineStart: s.timelineStart ?? s.startTime ?? 0,
          sourceUrl: resolveVideoUrl(s)
        })),
        imageTracks: (imageTracks || []).map((t) => ({
          ...t,
          sourceUrl: resolveImageUrl(t)
        })),
        textTracks: textTracks || [],
        audioTracks: (audioTracks || []).map((t) => ({
          ...t,
          sourceUrl: resolveAudioUrl(t)
        }))
      },
      metadata: {}
    };

    // Validate URLs
    const missingVideo = payload.project.videoSegments.some((s) => !s.sourceUrl);
    if (missingVideo) throw new Error(ct('creator.export.uploadVideoMissing', 'Upload vidéo en cours ou indisponible.'));
    const missingImages = payload.project.imageTracks.some((t) => !t.sourceUrl);
    if (missingImages) throw new Error(ct('creator.export.uploadImageMissing', 'Upload image en cours ou indisponible.'));
    const missingAudio = payload.project.audioTracks.some((t) => !t.sourceUrl);
    if (missingAudio) throw new Error(ct('creator.export.uploadAudioMissing', 'Upload audio en cours ou indisponible.'));

    const musicTrack = (audioTracks || []).find((track) => track?.originalTrack && track.originalTrack.title);
    if (musicTrack) {
      payload.metadata.music_title = musicTrack.originalTrack.title || musicTrack.name;
      payload.metadata.music_artist = musicTrack.originalTrack.artist || musicTrack.artist;
      payload.metadata.music_cover_image = musicTrack.originalTrack.cover_image || '';
    }

    return payload;
  }, [audioAssets, audioTracks, imageAssets, imageTracks, isRemoteUrl, montageDuration, projectFormat, textTracks, videoAssets, videoSegments]);

  const resolvePreviewAudioUrl = useCallback((track) => {
    if (!track) return null;
    if (track?.sourceUrl && isRemoteUrl(track.sourceUrl)) return track.sourceUrl;
    if (track?.url && isRemoteUrl(track.url)) return track.url;
    if (track?.file_path && isRemoteUrl(track.file_path)) return track.file_path;
    if (track?.originalTrack?.file_path && isRemoteUrl(track.originalTrack.file_path)) return track.originalTrack.file_path;
    if (track?.file_path && !isRemoteUrl(track.file_path)) {
      const filename = String(track.file_path).split('/').pop();
      return `${BACKEND_URL}/api/audio/${filename}`;
    }
    return null;
  }, [isRemoteUrl]);

  const stopPreview = useCallback(() => {
    if (previewStopTimerRef.current) {
      clearTimeout(previewStopTimerRef.current);
      previewStopTimerRef.current = null;
    }
    if (previewAudioRef.current) {
      try {
        previewAudioRef.current.pause();
        previewAudioRef.current.currentTime = 0;
      } catch (_) {}
      previewAudioRef.current = null;
    }
    setPreviewIsPlaying(false);
    setPreviewTrackId(null);
  }, []);

  const startPreview = useCallback((track) => {
    const url = resolvePreviewAudioUrl(track);
    if (!url) {
      showNotification(ct('creator.music.previewError', "Impossible de lire cet extrait."));
      return;
    }

    // Toggle stop if same track is playing
    if (previewTrackId === track.id && previewIsPlaying) {
      stopPreview();
      return;
    }

    stopPreview();
    const audio = new Audio(url);
    audio.preload = 'metadata';
    audio.volume = 1;
    previewAudioRef.current = audio;
    setPreviewTrackId(track.id);
    setPreviewIsPlaying(true);

    audio.onended = () => {
      stopPreview();
    };
    audio.play().catch(() => {
      setPreviewIsPlaying(false);
      setPreviewTrackId(null);
      showNotification(ct('creator.music.previewPlayError', "Lecture impossible."));
    });

    previewStopTimerRef.current = setTimeout(() => {
      stopPreview();
    }, 30000);
  }, [previewIsPlaying, previewTrackId, resolvePreviewAudioUrl, showNotification, stopPreview]);

  useEffect(() => {
    if (!showMusicModal) stopPreview();
  }, [showMusicModal, stopPreview]);

  useEffect(() => () => stopPreview(), [stopPreview]);

  const startServerRender = useCallback(async ({ mode }) => {
    if (isExporting || isPublishing) return;
    if (!hasTimelineElements) {
      showNotification(ct('creator.export.needVisual', "Ajoute au moins un élément visuel avant d'exporter."));
      return;
    }

    exportAbortRef.current = false;
    setExportUrl(null);
    setExportProgress(0);
    setExportStatus(ct('creator.export.preparing', 'Préparation…'));
    resetExportStages();
    setExportStageCurrent(1);

    if (mode === 'publish') {
      setIsPublishing(true);
      setPublishStatus(ct('creator.export.preparing', 'Préparation…'));
    } else {
      setIsExporting(true);
    }

    try {
      setExportStatus(ct('creator.stages.step', 'Étape {{current}}/3 · {{label}}').replace('{{current}}','1').replace('{{label}}', ct('creator.stages.upload', 'Upload des fichiers')));
      if (mode === 'publish') {
        setPublishStatus(
          ct('creator.stages.step', 'Étape {{current}}/3 · {{label}}')
            .replace('{{current}}','1')
            .replace('{{label}}', ct('creator.stages.upload', 'Upload des fichiers'))
        );
      }
      await waitForUploads((avg) => {
        const clamped = Math.max(0, Math.min(100, avg));
        const pct = Math.round(clamped);
        setStageProgress('upload', clamped / 100);
        setExportStatus(ct('creator.export.uploadProgress', 'Étape 1/3 · Upload des fichiers ({{pct}}%)').replace('{{pct}}', String(pct)));
        if (mode === 'publish') {
          setPublishStatus(
            ct('creator.export.uploadProgress', 'Étape 1/3 · Upload des fichiers ({{pct}}%)')
              .replace('{{pct}}', String(pct))
          );
        }
      });
      setStageProgress('upload', 1);
      setExportStageCurrent(2);

      setExportStatus(ct('creator.export.sendProject', 'Étape 2/3 · Envoi du projet…'));
      if (mode === 'publish') setPublishStatus(ct('creator.export.sendProject', 'Étape 2/3 · Envoi du projet…'));
      const payload = buildExportPayload();
      payload.mode = mode;

      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/render`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || ct('creator.export.launchError', 'Erreur lancement export'));
      }

      const { jobId } = await response.json();
      renderJobRef.current = jobId;
      setExportStageCurrent(2);
      setExportStatus(ct('creator.export.serverRender', 'Étape 2/3 · Rendu serveur…'));
      if (mode === 'publish') setPublishStatus(ct('creator.export.serverRender', 'Étape 2/3 · Rendu serveur…'));

      renderPollRef.current = window.setInterval(async () => {
        if (!renderJobRef.current) return;
        try {
          const res = await fetch(`${BACKEND_URL}/api/render/${renderJobRef.current}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (res.status === 404) {
            if (renderPollRef.current) {
              window.clearInterval(renderPollRef.current);
              renderPollRef.current = null;
            }
            renderJobRef.current = null;
            setIsExporting(false);
            setIsPublishing(false);
            resetExportStages();
            setExportStatus(ct('creator.export.interrupted', 'Export interrompu'));
            setPublishStatus('');
            showNotification(ct('creator.export.interruptedRestart', 'Export interrompu (serveur redémarré).'));
            return;
          }
          if (!res.ok) return;
          const data = await res.json();
          if (data.status === 'queued' || data.status === 'rendering') {
            setExportStageCurrent(2);
            if (typeof data.progress === 'number') {
              setStageProgress('render', Math.min(1, Math.max(0, data.progress)));
            }
            setExportStatus(ct('creator.export.serverRender', 'Étape 2/3 · Rendu serveur…'));
            if (mode === 'publish') setPublishStatus(ct('creator.export.serverRender', 'Étape 2/3 · Rendu serveur…'));
          } else if (data.status === 'uploading') {
            setExportStageCurrent(3);
            setStageProgress('render', 1);
            if (!uploadOutStartRef.current) uploadOutStartRef.current = Date.now();
            const elapsed = (Date.now() - uploadOutStartRef.current) / 6000;
            setStageProgress('uploadOut', Math.min(0.95, Math.max(0.05, elapsed)));
            setExportStatus(ct('creator.export.finalUpload', 'Étape 3/3 · Upload final…'));
            if (mode === 'publish') setPublishStatus(ct('creator.export.finalUpload', 'Étape 3/3 · Upload final…'));
          }
          if (data.status === 'completed') {
            if (renderPollRef.current) {
              window.clearInterval(renderPollRef.current);
              renderPollRef.current = null;
            }
            renderJobRef.current = null;
            setExportStageCurrent(3);
            setStageProgress('upload', 1);
            setStageProgress('render', 1);
            setStageProgress('uploadOut', 1);
            setExportStatus(ct('creator.export.done', 'Terminé ✅'));
            setIsExporting(false);
            if (mode === 'publish') {
              setPublishStatus('');
              setIsPublishing(false);
              setIsShareModalOpen(false);
              if (data.story && typeof onStoryPublished === 'function') {
                onStoryPublished(data.story);
              }
              showNotification(ct('creator.publish.done', 'Story publiée ✅'));
            } else {
              if (data.outputUrl) {
                setExportUrl(data.outputUrl);
                downloadExportFile(data.outputUrl);
              }
            }
          } else if (data.status === 'error') {
            if (renderPollRef.current) {
              window.clearInterval(renderPollRef.current);
              renderPollRef.current = null;
            }
            renderJobRef.current = null;
            setIsExporting(false);
            setIsPublishing(false);
            resetExportStages();
            setExportStatus(ct('creator.export.error', 'Erreur export'));
            setPublishStatus('');
            showNotification(data.error || ct('creator.export.renderError', 'Erreur lors du rendu.'));
          }
        } catch (_) {}
      }, 1000);
    } catch (err) {
      setIsExporting(false);
      setIsPublishing(false);
      resetExportStages();
      setExportStatus(ct('creator.export.error', 'Erreur export'));
      setPublishStatus('');
      showNotification(err?.message || ct('creator.export.error', 'Erreur export'));
    }
  }, [BACKEND_URL, buildExportPayload, downloadExportFile, hasTimelineElements, isExporting, isPublishing, onStoryPublished, resetExportStages, setStageProgress, showNotification, waitForUploads]);

  const startExportRender = useCallback(async (options = {}) => {
    if (isExporting) return;

    if (exportUrl) {
      try { URL.revokeObjectURL(exportUrl); } catch (_) {}
      setExportUrl(null);
      exportBlobRef.current = null;
    }

    const duration = Math.max(0.1, montageDuration);
    if (duration <= 0.11) {
    setNotification({ type: 'error', message: ct('creator.export.needVisualDetailed', "Ajoute au moins un élément visuel (vidéo/image/texte) avant d'exporter.") });
      if (typeof options.onError === 'function') {
        options.onError(ct('creator.export.needVisualDetailed', "Ajoute au moins un élément visuel (vidéo/image/texte) avant d'exporter."));
      }
      return;
    }

    const built = buildExportStream();
    if (!built) {
    setNotification({ type: 'error', message: ct('creator.export.streamUnsupported', "Export indisponible : captureStream non supporté par ce navigateur.") });
      if (typeof options.onError === 'function') {
        options.onError(ct('creator.export.streamUnsupported', "Export indisponible : captureStream non supporté par ce navigateur."));
      }
      return;
    }

    const { stream, cleanup, scheduleAll } = built;

    exportAbortRef.current = false;
    exportChunksRef.current = [];
    setIsExporting(true);
    setExportProgress(0);
    setExportStatus(ct('creator.export.preparing', 'Préparation…'));

    // Start from beginning (hard clamp to 0 to avoid any negative preroll)
    setCurrentTimeSynced(0);
    currentTimeRef.current = 0;

    // Seek media to 0 and wait for the first frame to be ready BEFORE we start recording,
    // otherwise we can capture a black preroll frame.
    try {
      ensureVideoAtProjectTime(0);
      syncAudioAtProjectTime(0);
    try { Object.values(audioRefs.current || {}).forEach(a => { if (a && a.paused) a.play().catch(() => {}); }); } catch (_) {} // initial sync is fine

      const v = videoRef.current;
      if (v && typeof v.addEventListener === 'function') {
        await new Promise((resolve) => {
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            try { v.removeEventListener('seeked', finish); } catch (_) {}
            resolve();
          };
          try { v.addEventListener('seeked', finish, { once: true }); } catch (_) {}
          window.setTimeout(finish, 250);
        });
      }
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    } catch (_) {}

    // Start playback so the render progresses (time-driven)
    isPlayingRef.current = true;
    setIsPlaying(true);
    ensureVideoAtProjectTime(0);
    syncAudioAtProjectTime(0);
    try { Object.values(audioRefs.current || {}).forEach(a => { if (a && a.paused) a.play().catch(() => {}); }); } catch (_) {} // initial sync is fine


    // MediaRecorder: WebM recommended; MP4 depends on browser support.
    const preferredMime = exportFormat === 'mp4' ? 'video/mp4' : 'video/webm;codecs=vp9,opus';
    let recorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: preferredMime, audioBitsPerSecond: 256000, videoBitsPerSecond: 8000000 });
    } catch (err) {
      try {
        recorder = new MediaRecorder(stream); // fallback
      } catch (err2) {
        cleanup();
        setIsExporting(false);
        setNotification({ type: 'error', message: ct('creator.export.startError', "Impossible de démarrer l'export sur ce navigateur.") });
        if (typeof options.onError === 'function') {
          options.onError(ct('creator.export.startError', "Impossible de démarrer l'export sur ce navigateur."));
        }
        return;
      }
    }

    exportRecorderRef.current = recorder;
    try { if (typeof scheduleAll === 'function') await scheduleAll(); } catch (_) {}

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) exportChunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      cleanup();

      // Stop playback
      isPlayingRef.current = false;
      setIsPlaying(false);
      try { pauseAllMedia(); } catch (_) {}

      const aborted = exportAbortRef.current;
      exportRecorderRef.current = null;

      if (aborted) {
        setIsExporting(false);
        setExportProgress(0);
      setExportStatus(ct('creator.export.cancelled', 'Export annulé'));
        return;
      }

      const blob = new Blob(exportChunksRef.current, { type: recorder.mimeType || 'video/webm' });
      const url = URL.createObjectURL(blob);

      exportBlobRef.current = blob;
      setExportUrl(url);
      setExportStatus(ct('creator.export.done', 'Terminé ✅'));
      setExportProgress(1);
      setIsExporting(false);

      if (typeof options.onComplete === 'function') {
        options.onComplete(blob, url);
      }
    };

    // Ensure fonts are loaded for canvas export (avoid fallback fonts)
    try {
      const families = Array.from(new Set((textTracksRef.current || [])
        .map(t => ((t.fontFamily || '').split(',')[0] || '').trim().replace(/['"]/g, ''))
        .filter(Boolean)
      ));
      if (document.fonts && families.length) {
        await Promise.all(families.map(f => document.fonts.load(`16px "${f}"`)));
        await document.fonts.ready;
      }
    } catch (_) {}

    exportStartPerfRef.current = performance.now();
    exportDurationRef.current = duration;

    recorder.start(250);
    setExportStatus(ct('creator.export.rendering', 'Rendu en cours…'));

    const tick = () => {
      const elapsed = (performance.now() - exportStartPerfRef.current) / 1000;
      const t = Math.max(0, Math.min(duration, elapsed));
      setExportProgress(Math.min(1, t / duration));

      if (exportAbortRef.current) return;

      if (t >= duration - 0.02) {
        try { recorder.stop(); } catch (_) {}
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    window.setTimeout(() => {
      if (!exportAbortRef.current) {
        try { recorder.stop(); } catch (_) {}
      }
    }, Math.ceil((duration + 2) * 1000));
  }, [isExporting, exportUrl, exportFormat, montageDuration, buildExportStream]);

  const captureThumbnailFromBlob = useCallback(async (videoBlob) => {
    if (!videoBlob) return null;

    return new Promise((resolve) => {
      let objectUrl = null;
      const cleanup = () => {
        if (objectUrl) {
          try { URL.revokeObjectURL(objectUrl); } catch (_) {}
        }
      };

      try {
        objectUrl = URL.createObjectURL(videoBlob);
        const video = document.createElement('video');
        video.src = objectUrl;
        video.muted = true;
        video.playsInline = true;
        video.preload = 'metadata';

        const handleError = () => {
          cleanup();
          resolve(null);
        };

        video.addEventListener('error', handleError, { once: true });
        video.addEventListener('loadedmetadata', () => {
          try {
            const seekTime = Math.min(0.1, video.duration || 0.1);
            video.currentTime = seekTime;
          } catch (_) {
            handleError();
          }
        }, { once: true });

        video.addEventListener('seeked', () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 720;
            canvas.height = video.videoHeight || 1280;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              cleanup();
              resolve(null);
              return;
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
              cleanup();
              resolve(blob || null);
            }, 'image/jpeg', 0.85);
          } catch (_) {
            cleanup();
            resolve(null);
          }
        }, { once: true });
      } catch (_) {
        cleanup();
        resolve(null);
      }
    });
  }, []);

  const publishStoryFromBlob = useCallback(async (videoBlob) => {
    if (!videoBlob) throw new Error(ct('creator.export.videoBlobMissing', 'Export vidéo manquant'));

    const formData = new FormData();
    const mime = videoBlob.type || '';
    let extension = 'webm';
    if (mime.includes('mp4')) extension = 'mp4';
    if (mime.includes('quicktime')) extension = 'mov';
    formData.append('video', videoBlob, `story.${extension}`);

    const thumbnailBlob = await captureThumbnailFromBlob(videoBlob);
    if (thumbnailBlob) {
      formData.append('thumbnail', thumbnailBlob, 'thumbnail.jpg');
    }

    // Ajouter les infos de musique de fond si disponibles
    const musicTrack = (audioTracks || []).find((track) => track?.originalTrack && track.originalTrack.title);
    if (musicTrack) {
      const title = musicTrack.originalTrack.title || musicTrack.name;
      const artist = musicTrack.originalTrack.artist || musicTrack.artist;
      const coverImage = musicTrack.originalTrack.cover_image || '';

      if (title) formData.append('music_title', title);
      if (artist) formData.append('music_artist', artist);
      if (coverImage) formData.append('music_cover_image', coverImage);
    }

    // Ajouter le format du projet pour conserver l'affichage exact dans la story
    if (projectFormat?.id) formData.append('format_id', projectFormat.id);
    if (projectFormat?.width) formData.append('format_width', String(projectFormat.width));
    if (projectFormat?.height) formData.append('format_height', String(projectFormat.height));

    const token = localStorage.getItem('token');
    const response = await fetch(`${BACKEND_URL}/api/stories`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.error || ct('creator.publish.error', 'Erreur lors de la publication'));
    }

    return response.json();
  }, [captureThumbnailFromBlob, audioTracks, projectFormat]);

  const handlePublishStory = useCallback(async () => {
    if (isPublishing || isExporting) return;
    await startServerRender({ mode: 'publish' });
  }, [isPublishing, isExporting, startServerRender]);

const generateThumbnails = useCallback(async (videoElement, duration) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const thumbnails = [];

    const interval = 2; // 1 thumbnail every 2 seconds
    const numThumbnails = Math.ceil(duration / interval);

    // Generate thumbnails across the full duration (not only the first minute)
    const maxThumbs = 240; // safety cap (e.g. ~8min at 1 thumb/2s)
    const count = Math.min(numThumbnails, maxThumbs);

    canvas.width = 160;
    canvas.height = 90;

    for (let i = 0; i < count; i++) {
      // If the video is long, spread thumbnails evenly across the whole duration
      const ratio = count <= 1 ? 0 : i / (count - 1);
      const time = ratio * duration;

      try {
        videoElement.currentTime = time;
        await new Promise(resolve => {
          videoElement.onseeked = resolve;
        });

        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        thumbnails.push({
          time,
          dataUrl: canvas.toDataURL('image/jpeg', 0.5)
        });
      } catch (e) {
        console.error('Error generating thumbnail:', e);
      }
    }

    videoElement.currentTime = 0;
    return thumbnails;
  }, []);

  // --------- Shared import helpers (input + drag&drop) ---------
  const importVideoFile = useCallback(async (file, opts = {}) => {
    if (!file || !file.type?.startsWith('video/')) return false;

    const url = URL.createObjectURL(file);
    const assetId = `vasset-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const tempVideo = document.createElement('video');
    tempVideo.src = url;
    tempVideo.preload = 'metadata';
    tempVideo.muted = true;

    tempVideo.onloadedmetadata = async () => {
      const duration = Number.isFinite(tempVideo.duration) ? tempVideo.duration : 0;

      let thumbnails = [];
      try {
        thumbnails = await generateThumbnails(tempVideo, duration || 1);
      } catch (err) {
        console.error('Error generating thumbnails', err);
      }

    const asset = {
      id: assetId,
      type: 'video',
      name: file.name,
      url,
      localUrl: url,
      cdnUrl: null,
      uploadStatus: 'uploading',
      uploadProgress: 0,
      file,
      duration,
      width: tempVideo.videoWidth || null,
      height: tempVideo.videoHeight || null,
      thumbnails,
    };

      setVideoAssets((prev) => [asset, ...prev]);
      if (typeof opts.onAssetReady === 'function') {
        try {
          opts.onAssetReady(asset);
        } catch (_) {}
      }
      showNotification(ct('creator.import.videoSuccess', 'Vidéo importée !'));

      uploadAssetToBunny(file, 'creator-studio/videos', (progress) => {
        updateVideoAsset(assetId, { uploadProgress: progress });
      })
        .then((cdnUrl) => {
          updateVideoAsset(assetId, { cdnUrl, uploadStatus: 'done', uploadProgress: 100, file: null });
        })
        .catch(() => {
          updateVideoAsset(assetId, { uploadStatus: 'error' });
          showNotification(ct('creator.import.videoUploadError', "Échec de l'upload vidéo."));
        });
    };

    tempVideo.onerror = () => {
      showNotification(ct('creator.import.videoReadError', "Impossible de lire la vidéo."));
      try { URL.revokeObjectURL(url); } catch (_) {}
    };

    return true;
  }, [generateThumbnails, uploadAssetToBunny, updateVideoAsset, showNotification]);

  const importImageFile = useCallback(async (file, opts = {}) => {
    if (!file || !file.type?.startsWith('image/')) return false;

    const url = URL.createObjectURL(file);
    const assetId = `iasset-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const asset = {
      id: assetId,
      type: 'image',
      name: file.name,
      url,
      localUrl: url,
      cdnUrl: null,
      uploadStatus: 'uploading',
      uploadProgress: 0,
      file,
    };
    setImageAssets((prev) => [asset, ...prev]);
    if (typeof opts.onAssetReady === 'function') {
      try {
        opts.onAssetReady(asset);
      } catch (_) {}
    }
    showNotification(ct('creator.import.imageSuccess', 'Image importée !'));

    uploadAssetToBunny(file, 'creator-studio/images', (progress) => {
      updateImageAsset(assetId, { uploadProgress: progress });
    })
      .then((cdnUrl) => {
        updateImageAsset(assetId, { cdnUrl, uploadStatus: 'done', uploadProgress: 100, file: null });
      })
      .catch(() => {
        updateImageAsset(assetId, { uploadStatus: 'error' });
        showNotification(ct('creator.import.imageUploadError', "Échec de l'upload image."));
      });
    return true;
  }, [uploadAssetToBunny, updateImageAsset, showNotification]);

  const importAudioFile = useCallback(async (file) => {
    if (!file || !file.type?.startsWith('audio/')) return false;

    const url = URL.createObjectURL(file);
    const assetId = `aasset-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const a = new Audio();
    a.src = url;
    a.preload = 'metadata';

    const pushAsset = (duration) => {
      const asset = {
        id: assetId,
        type: 'audio',
        title: file.name,
        artist: 'Import',
        file_path: url,
        localUrl: url,
        cdnUrl: null,
        uploadStatus: 'uploading',
        uploadProgress: 0,
        file,
        duration: Number.isFinite(duration) ? duration : 0,
      };
      setAudioAssets((prev) => [asset, ...prev]);
      showNotification(ct('creator.import.audioSuccess', 'Audio importé !'));

      uploadAssetToBunny(file, 'creator-studio/audio', (progress) => {
        updateAudioAsset(assetId, { uploadProgress: progress });
      })
        .then((cdnUrl) => {
          updateAudioAsset(assetId, { cdnUrl, uploadStatus: 'done', uploadProgress: 100, file: null });
        })
        .catch(() => {
          updateAudioAsset(assetId, { uploadStatus: 'error' });
          showNotification(ct('creator.import.audioUploadError', "Échec de l'upload audio."));
        });
    };

    a.onloadedmetadata = () => pushAsset(a.duration);
    a.onerror = () => pushAsset(0);
    return true;
  }, [uploadAssetToBunny, updateAudioAsset, showNotification]);

  const handleVideoImport = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) {
      e.target.value = '';
      return;
    }
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await importVideoFile(f);
    }
    e.target.value = '';
  };

  const handleImageImport = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) {
      e.target.value = '';
      return;
    }
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await importImageFile(f);
    }
    e.target.value = '';
  };

  const handleAudioImport = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) {
      e.target.value = '';
      return;
    }
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await importAudioFile(f);
    }
    e.target.value = '';
  };

  const addVideoAssetToTimeline = (asset) => {
    const startTime = currentTime;

    let finalStart = startTime;
    if (videoSegments.length > 0) {
      const lastSegment = [...videoSegments].sort((a, b) => (a.timelineStart ?? 0) - (b.timelineStart ?? 0)).slice(-1)[0];
      if (lastSegment) {
        const end = (lastSegment.timelineStart ?? 0) + (lastSegment.duration ?? 0);
        finalStart = Math.max(startTime, end);
      }
    }

    const projectRatio = projectAspect?.ratio || 16 / 9;
    const assetRatio = asset.width && asset.height ? (asset.width / asset.height) : projectRatio;
    const fitSize = assetRatio >= projectRatio
      ? { w: 100, h: (projectRatio / assetRatio) * 100 }
      : { w: (assetRatio / projectRatio) * 100, h: 100 };

    const newSegment = {
      id: `seg-${Date.now()}`,
      type: 'video-segment',
      name: asset.name,
      url: asset.localUrl || asset.url,
      assetId: asset.id,
      duration: asset.duration || 1,
      // Original in/out (used for thumbnail tiling + future trimming)
      originalStart: 0,
      originalEnd: asset.duration || 1,
      timelineStart: finalStart,
      lane: findAvailableLane({ start: finalStart, duration: asset.duration || 1, preferredLane: 0 }),
      volume: 100,
      thumbnails: asset.thumbnails || [],
      x: 50,
      y: 50,
      w: fitSize.w,
      h: fitSize.h,
      crop: { x: 0, y: 0, width: 100, height: 100 },
      flipH: false,
      flipV: false,
    };

    setVideoSegments(prev => [...prev, newSegment]);
    extendProjectIfNeeded(finalStart + (newSegment.duration ?? 1));
    setSelectedTrack({ id: newSegment.id, type: 'video-segment' });
    showNotification(ct('creator.timeline.videoAdded', 'Vidéo ajoutée à la timeline !'));
  };

  const addImageAssetToTimeline = (asset) => {
    const startTime = currentTime;
    // Default duration: 10 seconds (you can still resize by dragging the clip edges).
    const defaultDuration = 10;

    const newImage = {
      id: `img-${Date.now()}`,
      type: 'image',
      name: asset.name,
      url: asset.localUrl || asset.url,
      assetId: asset.id,
      startTime,
      duration: defaultDuration,
      lane: findAvailableLane({ start: startTime, duration: defaultDuration, preferredLane: 0 }),
      opacity: 100,
      flipH: false,
      flipV: false,
      x: 50,
      y: 50,
      w: 35,
      h: 35,
      cropTop: 0,
      cropRight: 0,
      cropBottom: 0,
      cropLeft: 0,
    };

    setImageTracks(prev => [...prev, newImage]);
    extendProjectIfNeeded(startTime + defaultDuration);
    setSelectedTrack({ id: newImage.id, type: 'image' });
    showNotification(ct('creator.timeline.imageAdded', 'Image ajoutée à la timeline !'));
  };

  const handleMediaImport = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) {
      e.target.value = '';
      return;
    }

    let importedAny = false;
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      const ok = (await importVideoFile(f, { onAssetReady: addVideoAssetToTimeline }))
        || (await importImageFile(f, { onAssetReady: addImageAssetToTimeline }));
      importedAny = importedAny || ok;
    }
    if (!importedAny) showNotification(ct('creator.import.dropHintShort', 'Déposez une vidéo ou une image.'));

    e.target.value = '';
  };

  const importDroppedFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    let importedAny = false;
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      const ok = (await importVideoFile(f, { onAssetReady: addVideoAssetToTimeline }))
        || (await importImageFile(f, { onAssetReady: addImageAssetToTimeline }));
      importedAny = importedAny || ok;
    }
    if (!importedAny) {
      showNotification(ct('creator.import.dropHint', 'Déposez une vidéo ou une image.'));
    }
  }, [importVideoFile, importImageFile, addVideoAssetToTimeline, addImageAssetToTimeline]);

  const addTextToTimeline = (preset) => {
    const startTime = currentTime;

    // Default duration: if there is already some content, extend to the end; otherwise 5 seconds.
    const timelineEnd = Math.max(
      0,
      ...videoSegments.map((s) => (s.timelineStart ?? 0) + (s.duration ?? 0)),
      ...imageTracks.map((t) => (t.startTime ?? 0) + (t.duration ?? 0)),
      ...textTracks.map((t) => (t.startTime ?? 0) + (t.duration ?? 0))
    );
    const defaultDuration = Math.max(1, (timelineEnd > 0 ? (timelineEnd - startTime) : 5));

    const presets = {
      title: { text: ct('creator.text.addTitle', 'Ajouter un titre'), fontSize: 64, fontWeight: 800, w: 80, h: 22 },
      subtitle: { text: ct('creator.text.addSubtitle', 'Ajouter un sous-titre'), fontSize: 40, fontWeight: 700, w: 70, h: 18 },
      body: { text: ct('creator.text.addText', 'Ajouter du texte'), fontSize: 28, fontWeight: 500, w: 70, h: 20 },
    };

    const p = presets[preset] || presets.body;

    const newText = {
      id: `txt-${Date.now()}`,
      type: 'text',
      name: ct('creator.text.label', 'Texte'),
      text: p.text,
      startTime,
      duration: defaultDuration,
      lane: findAvailableLane({ start: startTime, duration: defaultDuration, preferredLane: 0 }),
      x: 50,
      y: 50,
      w: p.w,
      h: p.h,
      opacity: 100,
      color: '#FFFFFF',
      align: 'center',
      fontFamily: 'Arial',
      fontSize: p.fontSize,
      fontWeight: p.fontWeight,
      italic: false,
      underline: false,
      backgroundColor: 'transparent',
      locked: false,
    };

    setTextTracks((prev) => [...prev, newText]);
    
    // Auto-fit newly added text inside its box
    scheduleFitText(newText.id);
extendProjectIfNeeded(startTime + defaultDuration);
    setSelectedTrack({ id: newText.id, type: 'text' });
    setEditingTextId(newText.id);
    showNotification(ct('creator.timeline.textAdded', 'Texte ajouté à la timeline !'));
  };

  const addAudioAssetToTimeline = (asset) => {
    handleAddMusic(asset);
  };

  const handleVideoLoaded = () => {
    if (videoRef.current) {
      // Apply deferred seek when switching sources
      if (pendingVideoSeekRef.current != null) {
        try { videoRef.current.currentTime = pendingVideoSeekRef.current; } catch (_) {}
        pendingVideoSeekRef.current = null;
      }
      if (isPlayingRef.current) {
        videoRef.current.play().catch(() => {});
      }
      // Apply settings from selected segment
      if (selectedTrack?.type === 'video-segment') {
        const segment = videoSegments.find(s => s.id === selectedTrack.id);
        if (segment) {
          videoRef.current.volume = (segment.volume ?? 100) / 100;
        }
      }
    } else if (selectedTrack?.type === 'text') {
      setTextTracks(prev => prev.map(t =>
        t.id === selectedTrack.id ? { ...t, opacity: val } : t
      ));
    showNotification(ct('creator.opacity.notification', 'Transparence: {{value}}%').replace('{{value}}', String(val)));
    }
  };

  // Keep refs in sync for RAF playback
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);

  // Helper: update state + ref synchronously to avoid RAF/video re-seek loops
  const setCurrentTimeSynced = useCallback((t) => {
    currentTimeRef.current = t;
    setCurrentTime(t);
  }, []);

  const syncAudioAtProjectTime = useCallback((projectTime) => {
    audioTracks.forEach(track => {
      const ref = audioRefs.current[track.id];
      if (!ref) return;

      const trackStart = track.startTime;
      const trackEnd = track.startTime + track.duration;
      const trimStart = track.trimStart || 0;
      const trimEnd = track.trimEnd || track.originalDuration;

      if (projectTime >= trackStart && projectTime < trackEnd) {
        const offsetInTrack = projectTime - trackStart;
        const audioTime = trimStart + offsetInTrack;

        if (audioTime >= trimEnd) {
          if (!ref.paused) ref.pause();
          return;
        }

        // Calculate fade volume
        let fadeVolume = 1;
        const fadeIn = track.fadeIn || 0;
        const fadeOut = track.fadeOut || 0;
        const trackDuration = track.duration;

        if (fadeIn > 0 && offsetInTrack < fadeIn) fadeVolume = offsetInTrack / fadeIn;
        if (fadeOut > 0 && offsetInTrack > (trackDuration - fadeOut)) {
          fadeVolume = Math.min(fadeVolume, (trackDuration - offsetInTrack) / fadeOut);
        }

        const baseVolume = (track.volume ?? 100) / 100;
        ref.volume = Math.max(0, Math.min(1, baseVolume * fadeVolume));

        // Keep in sync (seek if drift is large)
        const drift = Math.abs((ref.currentTime || 0) - audioTime);
        if (drift > 0.15) ref.currentTime = audioTime;

        if (isPlayingRef.current && ref.paused) {
          ref.play().catch(e => console.log('Audio play prevented:', e));
        }
      } else {
        if (!ref.paused) ref.pause();
      }
    });
  }, [audioTracks]);

  const ensureVideoAtProjectTime = useCallback((projectTime) => {
    const t = Number(projectTime || 0);

    const seg = (videoSegments || []).find((s) => {
      const st = Number(s.startTime ?? s.timelineStart ?? 0);
      const dur = Number(s.duration || 0);
      return t >= st && t < st + dur;
    });

    if (!seg || !videoRef.current) {
      if (videoRef.current && !videoRef.current.paused) videoRef.current.pause();
      return;
    }

    // Switch source if needed (multiple video files on the timeline)
    const wantedSrc = seg.url || null;
    if (wantedSrc) {
      const curSrc = activeVideoSrcRef.current || '';
      if (!curSrc || curSrc !== wantedSrc) {
        activeVideoSrcRef.current = wantedSrc;
        setActiveVideoSrc(wantedSrc);
        try {
          // Setting src directly is more reliable than relying only on React rerender timing
          videoRef.current.src = wantedSrc;
          videoRef.current.load();
        } catch (_) {}
      }
    }

    const st = Number(seg.startTime ?? seg.timelineStart ?? 0);
    const offsetInSegment = t - st;
    const desiredVideoTime = Number(seg.originalStart || 0) + offsetInSegment;

    // If metadata isn't ready yet, defer the seek
    if (Number.isNaN(videoRef.current.duration) || videoRef.current.readyState < 1) {
      pendingVideoSeekRef.current = desiredVideoTime;
    } else {
      const drift = Math.abs((videoRef.current.currentTime || 0) - desiredVideoTime);
      if (drift > 0.2) {
        try { videoRef.current.currentTime = desiredVideoTime; } catch (_) {}
      }
    }

    // Apply per-segment volume
    videoRef.current.volume = (seg.volume ?? 100) / 100;

    if (isPlayingRef.current && videoRef.current.paused) {
      videoRef.current.play().catch(() => {});
    }
  }, [videoSegments]);

  // Find the next visual item start (video segment or image) at/after a given time.
  // Uses a tiny epsilon so an item starting exactly on a cut boundary isn't skipped.
  const findNextPlayableStart = useCallback((afterTime) => {
    const EPS = 1e-4;
    const a = Number(afterTime || 0);
    const candidates = [];

    (videoSegments || []).forEach((s) => {
      const st = Number(s.startTime ?? s.timelineStart ?? 0);
      // Use a small epsilon in the other direction too, to avoid missing items that
      // start exactly on a cut boundary (floating point rounding when trimming / dragging).
      if (st >= a - EPS) candidates.push({ t: st, type: 'video' });
    });

    (imageTracks || []).forEach((img) => {
      const st = Number(img.startTime ?? img.timelineStart ?? 0);
      if (st >= a - EPS) candidates.push({ t: st, type: 'image' });
    });

    (textTracks || []).forEach((t) => {
      const st = Number(t.startTime ?? 0);
      if (st >= a - EPS) candidates.push({ t: st, type: 'text' });
    });

    if (!candidates.length) return null;
    candidates.sort((x, y) => x.t - y.t);
    return candidates[0].t;
  }, [videoSegments, imageTracks, textTracks]);

  // End of the actual content on the timeline (last visual/audio end).
  // Playback should stop here (not at the minimum project duration).
  const timelineContentEnd = useMemo(() => {
    let end = 0;
    (videoSegments || []).forEach((s) => {
      const st = Number(s.startTime ?? s.timelineStart ?? 0);
      const dur = Number(s.duration || 0);
      end = Math.max(end, st + dur);
    });
    (imageTracks || []).forEach((t) => {
      const st = Number(t.startTime ?? t.timelineStart ?? 0);
      const dur = Number(t.duration || 0);
      end = Math.max(end, st + dur);
    });
    (audioTracks || []).forEach((t) => {
      const st = Number(t.startTime || 0);
      const dur = Number(t.duration || 0);
      end = Math.max(end, st + dur);
    });
    (textTracks || []).forEach((t) => {
      const st = Number(t.startTime || 0);
      const dur = Number(t.duration || 0);
      end = Math.max(end, st + dur);
    });
    return end;
  }, [videoSegments, imageTracks, audioTracks, textTracks]);

  const playbackEndTime = useMemo(() => {
    // If nothing is placed, fall back to the current project duration.
    const end = Number(timelineContentEnd || 0);
    if (end > 0.001) return end;
    return Number(projectDuration || 0);
  }, [timelineContentEnd, projectDuration]);

  // RAF playback: advances time during gaps / image-only parts,
  // and kicks video/audio syncing so playback feels continuous.
  useEffect(() => {
    if (!isPlaying) {
      if (playbackRafRef.current) cancelAnimationFrame(playbackRafRef.current);
      playbackRafRef.current = null;
      playbackLastTsRef.current = null;
      return;
    }

    const step = (ts) => {
      if (!isPlayingRef.current) return;

      if (playbackLastTsRef.current == null) playbackLastTsRef.current = ts;
      const dt = Math.max(0, (ts - playbackLastTsRef.current) / 1000);
      playbackLastTsRef.current = ts;

      const t0 = currentTimeRef.current;
      let t1 = t0;

      const segNow = (videoSegments || []).find(s => {
        const st = Number(s.startTime ?? s.timelineStart ?? 0);
        const dur = Number(s.duration || 0);
        return t0 >= st && t0 < st + dur;
      });

      // If we are NOT inside a video segment, we drive time forward ourselves
      // (images/gaps). When we hit a next segment, we'll let ensureVideoAtProjectTime start it.
      if (!segNow) {
        t1 = Math.min(playbackEndTime, t0 + dt);

        // If we're in a big gap, optionally jump to the next element start once we pass it
        // (prevents "empty" long waits).
        const nextStart = findNextPlayableStart(t0);
        if (nextStart != null && nextStart > t0 && nextStart < t1) {
          t1 = nextStart;
        }

        if (t1 !== t0) setCurrentTimeSynced(t1);
      }

      // Keep media synced to the (possibly updated) time.
      // If we're inside a video segment, let the <video> drive time (avoid re-seeking each RAF tick).
      // If we're outside a segment, the RAF clock drives time and we keep media aligned to that.
      let tSync = (!segNow ? t1 : currentTimeRef.current);

      if (segNow && videoRef.current) {
        const vT = Number(videoRef.current.currentTime || 0);
        const segStart = Number(segNow.originalStart || 0);
        const segEnd = Number(segNow.originalEnd ?? (segStart + Number(segNow.duration || 0)));
        const segTlStart = Number(segNow.startTime ?? segNow.timelineStart ?? 0);
        const segTimelineEnd = segTlStart + Number(segNow.duration || 0);

        // Map current <video> time -> project time to keep playhead + audio moving even before onTimeUpdate fires.
        const projFromVideo = segTlStart + Math.max(0, Math.min(segEnd - segStart, vT - segStart));
        const clampedProj = Math.min(segTimelineEnd, Math.max(segTlStart, projFromVideo));

	        if (Math.abs(clampedProj - currentTimeRef.current) > 0.02) {
          setCurrentTimeSynced(clampedProj);
        }

	        // Always apply the segment's volume while it's playing (so mute works immediately)
	        try {
	          videoRef.current.volume = (segNow.volume ?? 100) / 100;
	        } catch (_) {}

	        tSync = currentTimeRef.current;
        syncAudioAtProjectTime(tSync);

        // If autoplay was blocked and video is paused while we think we're playing, try to resume.
        if (isPlayingRef.current && videoRef.current.paused) {
          videoRef.current.play().catch(() => {});
        }
      } else {
        // Outside a video segment: align underlying media to the timeline time.
        syncAudioAtProjectTime(tSync);
        ensureVideoAtProjectTime(tSync);
      }

      // Stop at end
      if (tSync >= playbackEndTime - 0.001) {
        if (videoRef.current && !videoRef.current.paused) videoRef.current.pause();
        Object.values(audioRefs.current).forEach(ref => ref?.pause());
        isPlayingRef.current = false;
      setIsPlaying(false);
        return;
      }

      playbackRafRef.current = requestAnimationFrame(step);
    };

    playbackRafRef.current = requestAnimationFrame(step);

    return () => {
      if (playbackRafRef.current) cancelAnimationFrame(playbackRafRef.current);
      playbackRafRef.current = null;
      playbackLastTsRef.current = null;
    };
  }, [isPlaying, videoSegments, playbackEndTime, ensureVideoAtProjectTime, syncAudioAtProjectTime, findNextPlayableStart]);



  const handleTimeUpdate = () => {
    // ONLY update playhead position during ACTIVE playback (not paused)
    // AND not while user is dragging the playhead
    if (videoRef.current && isPlaying && !isDraggingPlayhead && !videoRef.current.paused) {
const videoTime = videoRef.current.currentTime;

// Prefer the segment under the CURRENT playhead time (timeline time), so we never
// accidentally "jump" to another segment when a trimmed segment ends in the source.
const segNow = (videoSegments || []).find(seg => {
  const st = Number(seg.startTime ?? seg.timelineStart ?? 0);
  const dur = Number(seg.duration || 0);
  const t = Number(currentTimeRef.current || 0);
  return t >= st && t < st + dur;
});

if (segNow) {
  const segOriginalStart = segNow.originalStart || 0;
  const segOriginalEnd = segNow.originalEnd ?? (segOriginalStart + segNow.duration);
  const offsetInSegment = videoTime - segOriginalStart;
  const segTlStart = Number(segNow.startTime ?? segNow.timelineStart ?? 0);
  const projectTime = segTlStart + offsetInSegment;

  setCurrentTimeSynced(projectTime);

  // Apply per-segment volume during playback (mute must be immediate)
  try {
    if (videoRef.current) videoRef.current.volume = (segNow.volume ?? 100) / 100;
  } catch (_) {}

  const segEndProjectTime = segTlStart + Number(segNow.duration || 0);
  const reachedEnd =
    videoTime >= (segOriginalEnd - 0.03) ||
    projectTime >= (segEndProjectTime - 0.03);

  if (reachedEnd) {
    // Clamp to exact cut point on the timeline
    setCurrentTimeSynced(segEndProjectTime);

    // Pause current underlying video immediately (it would otherwise keep playing)
    try { videoRef.current.pause(); } catch (_) {}

    const nextStart = findNextPlayableStart(segEndProjectTime);
    if (nextStart == null) {
      Object.values(audioRefs.current).forEach(ref => ref?.pause());
      isPlayingRef.current = false;
      setIsPlaying(false);
      return;
    }

    // Jump to next element start; RAF will handle image-only parts,
    // and ensureVideoAtProjectTime will start the next video segment if needed.
    setCurrentTimeSynced(nextStart);
    syncAudioAtProjectTime(nextStart);
    ensureVideoAtProjectTime(nextStart);
    return;
  }

  // We already updated the playhead using the current segment.
  // Audio/video syncing is handled by the RAF loop, so we can exit here.
  return;
}

// Find the segment that contains this video time
      for (const seg of videoSegments) {
        if (videoTime >= seg.originalStart && videoTime < seg.originalEnd) {
          const offsetInSegment = videoTime - seg.originalStart;
          const projectTime = seg.timelineStart + offsetInSegment;
          
          setCurrentTimeSynced(projectTime);

          // Stop at the end of the trimmed segment and continue on the next element (video/image) if any
          const segEndProjectTime = seg.timelineStart + seg.duration;
          if (videoTime >= (seg.originalEnd - 0.03)) {
            // Clamp to exact cut point on the timeline
            setCurrentTimeSynced(segEndProjectTime);

            // Pause current underlying video immediately (it would otherwise keep playing)
            try { videoRef.current.pause(); } catch (_) {}

            const nextStart = findNextPlayableStart(segEndProjectTime);
            if (nextStart == null) {
              Object.values(audioRefs.current).forEach(ref => ref?.pause());
              setIsPlaying(false);
              return;
            }

            // Jump to next element start; RAF will handle image-only parts,
            // and ensureVideoAtProjectTime will start the next video segment if needed.
            setCurrentTimeSynced(nextStart);
            syncAudioAtProjectTime(nextStart);
            // Let React update videoUrl; we still try to align ASAP
            ensureVideoAtProjectTime(nextStart);
            return;
          }

          
          // Sync audio tracks with current project time
          audioTracks.forEach(track => {
            const ref = audioRefs.current[track.id];
            if (ref) {
              const trackStart = track.startTime;
              const trackEnd = track.startTime + track.duration;
              const trimStart = track.trimStart || 0;
              const trimEnd = track.trimEnd || track.originalDuration;
              
              if (projectTime >= trackStart && projectTime < trackEnd) {
                // Audio should be playing
                const offsetInTrack = projectTime - trackStart;
                const audioTime = trimStart + offsetInTrack;
                
                // Check if we've passed the trim end
                if (audioTime >= trimEnd) {
                  if (!ref.paused) ref.pause();
                } else {
                  // Calculate fade volume
                  let fadeVolume = 1;
                  const fadeIn = track.fadeIn || 0;
                  const fadeOut = track.fadeOut || 0;
                  const trackDuration = track.duration;
                  
                  // Fade in
                  if (fadeIn > 0 && offsetInTrack < fadeIn) {
                    fadeVolume = offsetInTrack / fadeIn;
                  }
                  // Fade out
                  if (fadeOut > 0 && offsetInTrack > (trackDuration - fadeOut)) {
                    fadeVolume = Math.min(fadeVolume, (trackDuration - offsetInTrack) / fadeOut);
                  }
                  
                  const baseVolume = (track.volume ?? 100) / 100;
                  ref.volume = Math.max(0, Math.min(1, baseVolume * fadeVolume));
                  
                  if (ref.paused) {
                    ref.currentTime = audioTime;
                    ref.play().catch(e => console.log('Audio play prevented:', e));
                  }
                }
              } else {
                // Audio should not be playing at this time
                if (!ref.paused) {
                  ref.pause();
                }
              }
            }
          });
          
          return;
        }
      }
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      if (videoRef.current) videoRef.current.pause();
      Object.values(audioRefs.current).forEach(ref => ref?.pause());
      setIsPlaying(false);
    } else {
      // IMPORTANT: playback is timeline-driven (not video-driven).
      // Do NOT jump to the next video segment when the playhead is currently over
      // text/images. Otherwise text-only sections get skipped.
      let playFromTime = currentTimeRef.current;

      // If user is already at/after the end of timeline content, restart from 0
      if (playFromTime >= playbackEndTime - 0.001) {
        playFromTime = 0;
        setCurrentTimeSynced(0);
      }

      // Start playback clock (RAF). We sync video/audio to the playhead time.
      isPlayingRef.current = true;
      setIsPlaying(true);

      // If playhead sits in a gap, playback will advance and start the next element automatically.
      // If it sits inside a segment, we start that segment immediately.
      ensureVideoAtProjectTime(playFromTime);
      syncAudioAtProjectTime(playFromTime);
    }
  };

  const startVoiceoverCountdown = useCallback(() => {
    if (!hasTimelineElements) return;
    // Start from current playhead position
    const startAt = currentTimeRef.current || 0;
    voiceoverStartTimeRef.current = startAt;

    setVoiceoverCountdown(3);
    let v = 3;
    const t = window.setInterval(() => {
      v -= 1;
      if (v >= 0) {
        setVoiceoverCountdown(v);
      }
      if (v <= 0) {
        window.clearInterval(t);
        // Begin recording on next tick (so UI updates)
        window.setTimeout(() => beginVoiceoverRecording(), 50);
      }
    }, 1000);
  }, [hasTimelineElements]);

  const beginVoiceoverRecording = useCallback(async () => {
    // Reset countdown overlay
    setVoiceoverCountdown(null);

    const startAt = voiceoverStartTimeRef.current || currentTimeRef.current || 0;

    // Create a temp voiceover clip in timeline (grows while recording)
    const id = `voiceover-${Date.now()}`;
    voiceoverTrackIdRef.current = id;
    voiceoverChunksRef.current = [];

    setAudioTracks(prev => ([
      ...prev,
      {
        id,
        name: ct('creator.voiceover.name', 'Voix off'),
        url: null,
        startTime: startAt,
        duration: 0,
        originalDuration: 0,
        trimStart: 0,
        trimEnd: 0,
        volume: 1,
        isVoiceover: true,
        isRecording: true,
      }
    ]));

    // Ensure playhead is exactly at startAt, then start playback
    setCurrentTimeSynced(startAt);

    // Start playback (timeline-driven)
    isPlayingRef.current = true;
    setIsPlaying(true);
    ensureVideoAtProjectTime(startAt);
    syncAudioAtProjectTime(startAt); // initial sync is fine


    setIsVoiceoverRecording(true);

    // Start microphone capture
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error('Microphone access denied:', err);
      setIsVoiceoverRecording(false);
      // Remove temp clip
      setAudioTracks(prev => prev.filter(t => t.id !== id));
      voiceoverTrackIdRef.current = null;
      return;
    }

    voiceoverStreamRef.current = stream;

    const rec = new MediaRecorder(stream);
    voiceoverRecorderRef.current = rec;

    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) voiceoverChunksRef.current.push(e.data);
    };

    rec.onstop = () => {
      try {
        stream.getTracks().forEach(tr => tr.stop());
      } catch (_) {}

      const chunks = voiceoverChunksRef.current || [];
      const blob = new Blob(chunks, { type: 'audio/webm' });
      const url = URL.createObjectURL(blob);

      const start = voiceoverStartTimeRef.current || 0;
      const end = currentTimeRef.current || start;
      const dur = Math.max(0.05, end - start);

      setAudioTracks(prev => prev.map(t => {
        if (t.id !== id) return t;
        return {
          ...t,
          url,
          duration: dur,
          originalDuration: dur,
          isRecording: false,
        };
      }));

      setIsVoiceoverRecording(false);
      voiceoverTrackIdRef.current = null;
      voiceoverRecorderRef.current = null;
      voiceoverStreamRef.current = null;
      voiceoverChunksRef.current = [];
    };

    rec.start(250);
  }, []);



  const handleAddMusic = (track) => {
    if (!userIsPremium && track?.is_premium) {
      setPremiumTrack(track);
      setShowPremiumModal(true);
      return;
    }
    const startTime = currentTime;
    const originalDuration = track.duration || 30;

    // By default, the visible part of a new music track should match the current montage length
    // (video/images/text). This avoids having a huge "playback zone" that extends to the end
    // of the music file when your montage is shorter.
    const visualEnd = getVisualTimelineEnd();
    const targetVisibleDuration = Math.max(1, visualEnd - startTime);
    const visibleDuration = Math.min(originalDuration, targetVisibleDuration || originalDuration);

    const newTrack = {
      id: `audio-${Date.now()}`,
      type: 'audio',
      name: track.title,
      artist: track.artist || 'Mood',
      url: track.file_path,
      sourceUrl: track.cdnUrl || track.file_path,
      assetId: track.assetId || track.id,
      originalDuration: originalDuration,   // Full duration of the audio file
      trimStart: 0,                         // Where to start playing in the original audio
      trimEnd: visibleDuration,             // Default selection ends with the montage length
      duration: visibleDuration,            // Visible duration on timeline (trimEnd - trimStart)
      startTime: startTime,                 // Position on timeline
      originalTrack: track,
      volume: 100,
      fadeIn: 0,                            // Fade in duration in seconds
      fadeOut: 0                            // Fade out duration in seconds
    };

    setAudioTracks(prev => [...prev, newTrack]);
    // Also keep the track in the Canva-like audio library (so you can reuse it)
    setAudioAssets(prev => {
      const exists = prev.some(a => a.file_path === track.file_path);
      if (exists) return prev;
      return [
        {
          id: `music-${track.id || Date.now()}`,
          type: 'audio',
          title: track.title,
          artist: track.artist || 'Mood',
          file_path: track.file_path,
          cdnUrl: track.cdnUrl,
          duration: track.duration || originalDuration,
        },
        ...prev,
      ];
    });
    setShowMusicModal(false);

    // Extend the project only to the end of the visible selection (not the full audio file)
    extendProjectIfNeeded(startTime + visibleDuration);

    setSelectedTrack({ id: newTrack.id, type: 'audio' });
    showNotification(ct('creator.timeline.trackAdded', '"{{title}}" ajouté à la timeline !').replace('{{title}}', track.title));
  };

  // ============ TOOLBAR FUNCTIONS ============

  // Get selected segment
  const getSelectedSegment = () => {
    if (selectedTrack?.type === 'video-segment') {
      return videoSegments.find(s => s.id === selectedTrack.id);
    }
    return null;
  };

  // ============ TIMELINE CONTEXT MENU (VIDEO) ============

  
  // Handle text clip resize on the timeline (same behavior as images)
  const handleTextResizeStart = (e, txt) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    } catch (_) {}

    const initialClientX = e.clientX;
    const initialStartTime = txt.startTime ?? 0;
    const initialDuration = txt.duration ?? 1;
    const initialEndTime = initialStartTime + initialDuration;

    const minDuration = MIN_TEXT_DURATION_SECONDS;

    const onMove = (ev) => {
      if (ev.buttons === 0) { stop(); return; }
      const deltaX = ev.clientX - initialClientX;
      const deltaTime = getTimeFromPosition(deltaX);

      let proposedStart = initialStartTime + deltaTime;
      proposedStart = Math.max(0, proposedStart);

      let proposedDuration = initialEndTime - proposedStart;
      proposedDuration = Math.max(minDuration, proposedDuration);

      if (proposedStart + proposedDuration > projectDuration) {
        proposedDuration = Math.max(minDuration, projectDuration - proposedStart);
      }

      setTextTracks((prev) =>
        prev.map((t) =>
          t.id === txt.id
            ? {
                ...t,
                startTime: proposedStart,
                duration: proposedDuration,
                lane: findAvailableLane({ start: proposedStart, duration: proposedDuration, excludeType: 'text', excludeId: txt.id, preferredLane: t.lane ?? 0 }),
              }
            : t
        )
      );
    };

    const stop = () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', stop, true);
      document.removeEventListener('pointercancel', stop, true);
      window.removeEventListener('blur', stop, true);
    };

    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', stop, true);
    document.addEventListener('pointercancel', stop, true);
    window.addEventListener('blur', stop, true);
  };

  const handleTextResizeEnd = (e, txt) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    } catch (_) {}

    const initialClientX = e.clientX;
    const initialDuration = txt.duration ?? 1;
    const startTime = txt.startTime ?? 0;

    const minDuration = MIN_TEXT_DURATION_SECONDS;

    const onMove = (ev) => {
      if (ev.buttons === 0) { stop(); return; }
      const deltaX = ev.clientX - initialClientX;
      const deltaTime = getTimeFromPosition(deltaX);

      let proposedDuration = initialDuration + deltaTime;
      proposedDuration = Math.max(minDuration, proposedDuration);

      if (startTime + proposedDuration > projectDuration) {
        proposedDuration = Math.max(minDuration, projectDuration - startTime);
      }

      setTextTracks((prev) =>
        prev.map((t) =>
          t.id === txt.id
            ? {
                ...t,
                duration: proposedDuration,
                lane: findAvailableLane({ start: startTime, duration: proposedDuration, excludeType: 'text', excludeId: txt.id, preferredLane: t.lane ?? 0 }),
              }
            : t
        )
      );
    };

    const stop = () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', stop, true);
      document.removeEventListener('pointercancel', stop, true);
      window.removeEventListener('blur', stop, true);
    };

    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', stop, true);
    document.addEventListener('pointercancel', stop, true);
    window.removeEventListener('blur', stop, true);
  };

const openVideoSegmentContextMenu = (e, segment) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedTrack({ id: segment.id, type: 'video-segment' });

    const r = e.currentTarget?.getBoundingClientRect?.();
    const anchorRect = r
      ? { left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height }
      : null;

    // Seed a position (will be refined after the menu mounts/measures)
    setContextMenuPos({ left: e.clientX, top: e.clientY });
    setContextMenu({ segmentId: segment.id, anchorRect });
    setContextSubmenu(null);
  };

  const getVideoSegmentById = (id) => videoSegments.find(s => s.id === id);

  const copySelectedVideoSegment = (segmentId) => {
    const seg = getVideoSegmentById(segmentId);
    if (!seg) return;
    setTimelineClipboard({ type: 'video-segment', data: JSON.parse(JSON.stringify(seg)) });
    showNotification(ct('creator.timeline.copied', 'Élément copié'));
  };

  const copyVideoSegmentStyle = (segmentId) => {
    const seg = getVideoSegmentById(segmentId);
    if (!seg) return;
    const style = {
      opacity: seg.opacity ?? 100,
      volume: seg.volume ?? 100,
      flipH: !!seg.flipH,
      flipV: !!seg.flipV,
      cropTop: seg.cropTop ?? 0,
      cropRight: seg.cropRight ?? 0,
      cropBottom: seg.cropBottom ?? 0,
      cropLeft: seg.cropLeft ?? 0,
    };
    setStyleClipboard({ type: 'video-segment', style });
    showNotification(ct('creator.timeline.styleCopied', 'Style copié'));
  };


  const applyCopiedStyleToVideoSegment = (segmentId) => {
    if (!styleClipboard || styleClipboard.type !== 'video-segment') return;
    const st = styleClipboard.style || {};
    setVideoSegments(prev => prev.map(s => s.id === segmentId ? {
      ...s,
      opacity: st.opacity ?? s.opacity,
      volume: st.volume ?? s.volume,
      flipH: st.flipH ?? s.flipH,
      flipV: st.flipV ?? s.flipV,
      cropTop: st.cropTop ?? s.cropTop,
      cropRight: st.cropRight ?? s.cropRight,
      cropBottom: st.cropBottom ?? s.cropBottom,
      cropLeft: st.cropLeft ?? s.cropLeft,
    } : s));
    showNotification(ct('creator.timeline.stylePasted', 'Style collé'));
  };

  const pasteToTimelineAtPlayhead = () => {
    if (!timelineClipboard) return;
    if (timelineClipboard.type !== 'video-segment') return;

    const data = JSON.parse(JSON.stringify(timelineClipboard.data || {}));
    const duration = Math.max(0.25, Number(data.duration ?? 1));
    const start = Math.max(0, Number(currentTime ?? 0));

    const newSeg = {
      ...data,
      id: `segment-${Date.now()}`,
      timelineStart: start,
      duration,
      lane: findAvailableLane({ start, duration, preferredLane: Number(data.lane ?? 0) }),
      // keep original in/out consistent
      originalStart: Number(data.originalStart ?? 0),
      originalEnd: Number(data.originalEnd ?? (Number(data.originalStart ?? 0) + duration)),
      locked: false,
    };

    setVideoSegments(prev => [...prev, newSeg].sort((a, b) => a.timelineStart - b.timelineStart));
    extendProjectIfNeeded(start + duration);
    setSelectedTrack({ id: newSeg.id, type: 'video-segment' });
    showNotification(ct('creator.timeline.pasted', 'Collé'));
  };

  const duplicateVideoSegment = (segmentId) => {
    const seg = getVideoSegmentById(segmentId);
    if (!seg) return;

    const start = Math.max(0, Number(seg.timelineStart ?? 0) + 0.2);
    const duration = Math.max(0.25, Number(seg.duration ?? 1));

    const copy = {
      ...JSON.parse(JSON.stringify(seg)),
      id: `segment-${Date.now()}-dup`,
      timelineStart: start,
      locked: false,
      lane: findAvailableLane({ start, duration, preferredLane: Number(seg.lane ?? 0) }),
    };

    setVideoSegments(prev => [...prev, copy].sort((a, b) => a.timelineStart - b.timelineStart));
    extendProjectIfNeeded(start + duration);
    setSelectedTrack({ id: copy.id, type: 'video-segment' });
    showNotification(ct('creator.timeline.duplicated', 'Dupliqué'));
  };

  const deleteVideoSegment = (segmentId) => {
    setVideoSegments(prev => prev.filter(s => s.id !== segmentId));
    if (selectedTrack?.type === 'video-segment' && selectedTrack?.id === segmentId) {
      setSelectedTrack(null);
    }
    showNotification(ct('creator.timeline.deleted', 'Supprimé'));
  };

  const toggleLockVideoSegment = (segmentId) => {
    setVideoSegments(prev => prev.map(s => s.id === segmentId ? { ...s, locked: !s.locked } : s));
  };

  const setVideoSegmentZ = (segmentId, mode) => {
    setVideoSegments(prev => {
      const segs = [...prev];
      const idx = segs.findIndex(s => s.id === segmentId);
      if (idx < 0) return prev;
      const [item] = segs.splice(idx, 1);
      if (mode === 'front') segs.push(item);
      else if (mode === 'back') segs.unshift(item);
      else if (mode === 'forward') segs.splice(Math.min(segs.length, idx + 1), 0, item);
      else if (mode === 'behind') segs.splice(Math.max(0, idx - 1), 0, item);
      return segs;
    });
  };

  const alignVideoSegment = (segmentId, mode) => {
    setVideoSegments(prev => prev.map(s => {
      if (s.id !== segmentId) return s;
      const w = Math.max(1, Math.min(100, Number(s.w ?? 100)));
      const h = Math.max(1, Math.min(100, Number(s.h ?? 100)));
      let x = Number(s.x ?? 50);
      let y = Number(s.y ?? 50);

      if (mode === 'left') x = w / 2;
      if (mode === 'center') x = 50;
      if (mode === 'right') x = 100 - w / 2;

      if (mode === 'top') y = h / 2;
      if (mode === 'middle') y = 50;
      if (mode === 'bottom') y = 100 - h / 2;

      return { ...s, x, y };
    }));
  };

  const addCommentToVideoSegment = (segmentId) => {
    const seg = getVideoSegmentById(segmentId);
    if (!seg) return;
    const value = window.prompt(ct('creator.context.addComment', 'Ajouter un commentaire'), seg.comment ?? '');
    if (value == null) return;
    setVideoSegments(prev => prev.map(s => s.id === segmentId ? { ...s, comment: value } : s));
  };

  const setAltTextForVideoSegment = (segmentId) => {
    const seg = getVideoSegmentById(segmentId);
    if (!seg) return;
    const value = window.prompt(ct('creator.context.altText', 'Texte alternatif (accessibilité)'), seg.altText ?? '');
    if (value == null) return;
    setVideoSegments(prev => prev.map(s => s.id === segmentId ? { ...s, altText: value } : s));
  };

  const splitVideoSegmentAtPlayhead = (segmentId) => {
    setSelectedTrack({ id: segmentId, type: 'video-segment' });
    // Defer to ensure selectedTrack is applied before cutting
    setTimeout(() => {
      try { handleCutVideo(); } catch (_) {}
    }, 0);
  };

  const setVideoSegmentAsBackground = (segmentId) => {
    setVideoSegments(prev => prev.map(s => {
      if (s.id !== segmentId) return { ...s, asBackground: false };
      return {
        ...s,
        asBackground: true,
        x: 50,
        y: 50,
        w: 100,
        h: 100,
        cropTop: 0,
        cropRight: 0,
        cropBottom: 0,
        cropLeft: 0,
      };
    }));
    // push to back in layer order
    setVideoSegmentZ(segmentId, 'back');
  };

  const extractAudioFromVideoSegment = (segmentId) => {
    const seg = getVideoSegmentById(segmentId);
    if (!seg) return;

    const startTime = Number(seg.timelineStart ?? 0);
    const duration = Math.max(0.25, Number(seg.duration ?? 1));
    const trimStart = Number(seg.originalStart ?? 0);
    const trimEnd = Number(seg.originalEnd ?? (trimStart + duration));

    const newTrack = {
      id: `audio-from-video-${Date.now()}`,
      type: 'audio',
      name: seg.name
        ? ct('creator.audio.fromVideoNamed', 'Audio - {{name}}').replace('{{name}}', seg.name)
        : ct('creator.audio.fromVideo', 'Audio (vidéo)'),
      artist: ct('creator.audio.fromVideoArtist', 'Vidéo'),
      url: seg.url,
      originalDuration: Math.max(duration, Number(seg.originalEnd ?? duration)),
      trimStart,
      trimEnd,
      duration: Math.max(0.25, trimEnd - trimStart),
      startTime,
      originalTrack: { fromVideoSegmentId: seg.id },
      volume: 100,
      fadeIn: 0,
      fadeOut: 0,
    };

    setAudioTracks(prev => [...prev, newTrack]);
    extendProjectIfNeeded(startTime + newTrack.duration);
    setSelectedTrack({ id: newTrack.id, type: 'audio' });
    showNotification(ct('creator.timeline.audioExtracted', 'Audio extrait'));
  };


// Segment vidéo actif selon le temps courant (pour appliquer crop/transform même sans sélection)
const getActiveVideoSegment = () => {
  // S'il existe un segment vidéo à l'écran, on prend celui qui couvre currentTime
  const t = currentTime ?? 0;
  const seg = videoSegments.find((s) => t >= s.startTime && t <= s.startTime + s.duration);
  return seg || (videoSegments.length > 0 ? videoSegments[0] : null);
};

  // Handle Volume Change
  const handleVolumeChange = (value) => {
    const val = parseInt(value);
    if (selectedTrack?.type === 'video-segment') {
      setVideoSegments(prev => prev.map(seg => 
        seg.id === selectedTrack.id ? { ...seg, volume: val } : seg
      ));
      if (videoRef.current) {
      // Apply deferred seek when switching sources
      if (pendingVideoSeekRef.current != null) {
        try { videoRef.current.currentTime = pendingVideoSeekRef.current; } catch (_) {}
        pendingVideoSeekRef.current = null;
      }
      if (isPlayingRef.current) {
        videoRef.current.play().catch(() => {});
      }
        videoRef.current.volume = val / 100;
      }
    showNotification(ct('creator.volume.notification', 'Volume: {{value}}%').replace('{{value}}', String(val)));
    } else if (selectedTrack?.type === 'audio') {
      setAudioTracks(prev => prev.map(t => {
        if (t.id === selectedTrack.id) {
          const ref = audioRefs.current[t.id];
          if (ref) ref.volume = val / 100;
          return { ...t, volume: val };
        }
        return t;
      }));
      showNotification(ct('creator.volume.notification', 'Volume: {{value}}%').replace('{{value}}', String(val)));
    }
  };

  // Handle Cut Video - SPLITS the segment into two parts
  const handleCutVideo = () => {
    if (selectedTrack?.type !== 'video-segment') {
      showNotification(ct('creator.timeline.selectVideoSegment', "Sélectionnez d'abord un segment vidéo"));
      return;
    }
    
    const segment = getSelectedSegment();
    if (!segment) return;
    
    // Calculate cut point relative to the segment
    const cutTimeInTimeline = currentTime;
    
    // Check if cut point is within this segment
    if (cutTimeInTimeline <= segment.timelineStart + 0.5 || cutTimeInTimeline >= segment.timelineStart + segment.duration - 0.5) {
      showNotification(ct('creator.timeline.cutVideoHint', 'Positionnez le curseur dans le segment pour couper'));
      return;
    }
    
    const offsetInSegment = cutTimeInTimeline - segment.timelineStart;
    const cutPointInOriginal = segment.originalStart + offsetInSegment;
    
    // Create two new segments from the cut
    const leftSegment = {
      ...segment,
      id: `segment-${Date.now()}-left`,
      originalEnd: cutPointInOriginal,
      duration: offsetInSegment
    };
    
    const rightSegment = {
      ...segment,
      id: `segment-${Date.now()}-right`,
      timelineStart: cutTimeInTimeline,
      originalStart: cutPointInOriginal,
      duration: segment.duration - offsetInSegment
    };
    
    // Replace the original segment with the two new segments
    setVideoSegments(prev => {
      const newSegments = prev.filter(s => s.id !== segment.id);
      return [...newSegments, leftSegment, rightSegment].sort((a, b) => a.timelineStart - b.timelineStart);
    });
    
    // Select the right segment (the one after the cut)
    setSelectedTrack({ id: rightSegment.id, type: 'video-segment' });
    
    showNotification(
      ct('creator.timeline.videoCut', 'Vidéo coupée à {{time}} - 2 segments créés')
        .replace('{{time}}', formatTime(offsetInSegment))
    );
  };

  // Cut (split) an audio clip at the playhead
  const handleCutAudio = () => {
    if (selectedTrack?.type !== 'audio') {
      showNotification(ct('creator.timeline.selectAudioToCut', 'Sélectionnez une piste audio pour couper'));
      return;
    }

    const track = audioTracks.find(t => t.id === selectedTrack.id);
    if (!track) return;

    const cutTime = currentTime;

    // Must be inside the clip
    if (cutTime <= track.startTime + 0.25 || cutTime >= track.startTime + track.duration - 0.25) {
      showNotification(ct('creator.timeline.cutAudioHint', 'Positionnez le curseur dans la piste audio pour couper'));
      return;
    }

    const offset = cutTime - track.startTime;
    const cutPointInOriginal = (track.trimStart ?? 0) + offset;

    const leftTrack = {
      ...track,
      id: `audio-${Date.now()}-left`,
      trimEnd: cutPointInOriginal,
      duration: offset,
    };

    const rightTrack = {
      ...track,
      id: `audio-${Date.now()}-right`,
      startTime: cutTime,
      trimStart: cutPointInOriginal,
      duration: track.duration - offset,
    };

    setAudioTracks(prev => {
      const next = prev.filter(t => t.id !== track.id);
      return [...next, leftTrack, rightTrack].sort((a, b) => a.startTime - b.startTime);
    });

    setSelectedTrack({ id: rightTrack.id, type: 'audio' });
    showNotification(ct('creator.timeline.audioCut', 'Audio coupé !'));
  };

  // Cut (split) an image clip at the playhead
  const handleCutImage = () => {
    if (selectedTrack?.type !== 'image') {
      showNotification(ct('creator.timeline.selectImageToCut', 'Sélectionnez une image pour couper'));
      return;
    }

    const track = imageTracks.find(t => t.id === selectedTrack.id);
    if (!track) return;

    const cutTime = currentTime;

    if (cutTime <= track.startTime + 0.25 || cutTime >= track.startTime + track.duration - 0.25) {
      showNotification(ct('creator.timeline.cutImageHint', "Positionnez le curseur dans l'image pour couper"));
      return;
    }

    const offset = cutTime - track.startTime;

    const leftTrack = {
      ...track,
      id: `img-${Date.now()}-left`,
      duration: offset,
    };

    const rightTrack = {
      ...track,
      id: `img-${Date.now()}-right`,
      startTime: cutTime,
      duration: track.duration - offset,
    };

    setImageTracks(prev => {
      const next = prev.filter(t => t.id !== track.id);
      return [...next, leftTrack, rightTrack].sort((a, b) => a.startTime - b.startTime);
    });

    setSelectedTrack({ id: rightTrack.id, type: 'image' });
    showNotification(ct('creator.timeline.imageCut', 'Image coupée !'));
  };

  // Keyboard shortcuts
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };

    const onKeyDown = (e) => {
      if (e.defaultPrevented) return;
      if (isTypingTarget(e.target)) return;

      // S => cut/split selected clip at playhead
      if ((e.key === 's' || e.key === 'S') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        if (selectedTrack?.type === 'video-segment') handleCutVideo();
        else if (selectedTrack?.type === 'audio') handleCutAudio();
        else if (selectedTrack?.type === 'image') handleCutImage();
      }
    };

    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedTrack, currentTime, audioTracks, imageTracks, handleCutVideo, handleCutAudio, handleCutImage]);


  // Handle Crop
  const handleOpenCrop = () => {
    setShowCropModal(true);
    setShowVolumeSlider(false);
    setShowOpacitySlider(false);
  };

  const handleApplyCrop = (cropSettings) => {
    if (selectedTrack?.type === 'video-segment') {
      setVideoSegments(prev => prev.map(seg => 
        seg.id === selectedTrack.id ? { 
          ...seg, 
          cropTop: cropSettings.top,
          cropRight: cropSettings.right,
          cropBottom: cropSettings.bottom,
          cropLeft: cropSettings.left
        } : seg
      ));
    }
    setShowCropModal(false);
    showNotification(ct('creator.crop.applied', 'Rognage appliqué !'));
  };

  // Handle Opacity Change
  const handleOpacityChange = (value) => {
    const val = parseInt(value);
    if (selectedTrack?.type === 'video-segment') {
      setVideoSegments(prev => prev.map(seg => 
        seg.id === selectedTrack.id ? { ...seg, opacity: val } : seg
      ));
      showNotification(ct('creator.opacity.notification', 'Transparence: {{value}}%').replace('{{value}}', String(val)));
    } else if (selectedTrack?.type === 'image') {
      setImageTracks(prev => prev.map(t => 
        t.id === selectedTrack.id ? { ...t, opacity: val } : t
      ));
      showNotification(ct('creator.opacity.notification', 'Transparence: {{value}}%').replace('{{value}}', String(val)));
    } else if (selectedTrack?.type === 'text') {
      setTextTracks(prev => prev.map(t =>
        t.id === selectedTrack.id ? { ...t, opacity: val } : t
      ));
      showNotification(ct('creator.opacity.notification', 'Transparence: {{value}}%').replace('{{value}}', String(val)));
    }
  };

  // Handle Flip Horizontal
  const handleFlipHorizontal = () => {
    if (selectedTrack?.type === 'video-segment') {
      setVideoSegments(prev => prev.map(seg => 
        seg.id === selectedTrack.id ? { ...seg, flipH: !seg.flipH } : seg
      ));
      const seg = getSelectedSegment();
      showNotification(seg?.flipH ? ct('creator.flip.horizontalCleared', 'Flip horizontal annulé') : ct('creator.flip.horizontalApplied', 'Flip horizontal appliqué'));
    } else if (selectedTrack?.type === 'image') {
      setImageTracks(prev => prev.map(t => 
        t.id === selectedTrack.id ? { ...t, flipH: !t.flipH } : t
      ));
      const img = imageTracks.find(t => t.id === selectedTrack.id);
      showNotification(img?.flipH ? ct('creator.flip.horizontalCleared', 'Flip horizontal annulé') : ct('creator.flip.horizontalApplied', 'Flip horizontal appliqué'));
    }
  };

  // Handle Flip Vertical
  const handleFlipVertical = () => {
    if (selectedTrack?.type === 'video-segment') {
      setVideoSegments(prev => prev.map(seg => 
        seg.id === selectedTrack.id ? { ...seg, flipV: !seg.flipV } : seg
      ));
      const seg = getSelectedSegment();
      showNotification(seg?.flipV ? ct('creator.flip.verticalCleared', 'Flip vertical annulé') : ct('creator.flip.verticalApplied', 'Flip vertical appliqué'));
    } else if (selectedTrack?.type === 'image') {
      setImageTracks(prev => prev.map(t => 
        t.id === selectedTrack.id ? { ...t, flipV: !t.flipV } : t
      ));
      const img = imageTracks.find(t => t.id === selectedTrack.id);
      showNotification(img?.flipV ? ct('creator.flip.verticalCleared', 'Flip vertical annulé') : ct('creator.flip.verticalApplied', 'Flip vertical appliqué'));
    }
  };

  // Handle Delete
  const handleDeleteTrack = () => {
    if (!selectedTrack) return;
    
    if (selectedTrack.type === 'video-segment') {
      const segment = getSelectedSegment();
      setVideoSegments(prev => prev.filter(s => s.id !== selectedTrack.id));

      // If we just deleted the last segment, stop playback and reset the playhead
      if (videoSegments.length <= 1) {
        if (videoRef.current) {
      // Apply deferred seek when switching sources
      if (pendingVideoSeekRef.current != null) {
        try { videoRef.current.currentTime = pendingVideoSeekRef.current; } catch (_) {}
        pendingVideoSeekRef.current = null;
      }
      if (isPlayingRef.current) {
        videoRef.current.play().catch(() => {});
      }
          videoRef.current.pause();
          try { videoRef.current.currentTime = 0; } catch (_) {}
        }
        setIsPlaying(false);
        setCurrentTime(0);
      }
      
      showNotification(ct('creator.timeline.videoDeleted', 'Segment vidéo supprimé'));
    } else if (selectedTrack.type === 'audio') {
      const track = audioTracks.find(t => t.id === selectedTrack.id);
      setAudioTracks(prev => prev.filter(t => t.id !== selectedTrack.id));
      showNotification(ct('creator.timeline.trackDeleted', '"{{title}}" supprimé').replace('{{title}}', track?.name || ''));
    } else if (selectedTrack.type === 'image') {
      setImageTracks(prev => prev.filter(t => t.id !== selectedTrack.id));
      showNotification(ct('creator.timeline.imageDeleted', 'Image supprimée'));
    } else if (selectedTrack.type === 'text') {
      setTextTracks(prev => prev.filter(t => t.id !== selectedTrack.id));
      showNotification(ct('creator.timeline.textDeleted', 'Texte supprimé'));
    }
    
    setSelectedTrack(null);
    setShowVolumeSlider(false);
    setShowOpacitySlider(false);
  };

  // ============ END TOOLBAR FUNCTIONS ============

  
  const hashToSeed = (str) => {
    let h = 1779033703 ^ str.length;
    for (let i = 0; i < str.length; i++) {
      h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
      h = (h << 13) | (h >>> 19);
    }
    return (h >>> 0);
  };

  const mulberry32 = (a) => {
    return () => {
      let t = a += 0x6D2B79F5;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  const getWaveformHeights = (trackId, barsCount) => {
    const key = `${trackId}:${barsCount}`;
    const cache = waveformCacheRef.current;
    if (cache.has(key)) return cache.get(key);

    const rand = mulberry32(hashToSeed(String(trackId)));
    const heights = Array.from({ length: barsCount }).map((_, i) => {
      // Stable "music-like" envelope: sine + seeded noise
      const base = 22 + Math.sin(i * 0.45) * 18;
      const noise = rand() * 28;
      const h = Math.max(8, Math.min(92, base + noise));
      return h;
    });

    cache.set(key, heights);
    return heights;
  };

const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const seekPreviewToTime = useCallback((newTime) => {
    const segment = videoSegments.find(seg =>
      newTime >= seg.timelineStart && newTime < seg.timelineStart + seg.duration
    );
    if (segment && videoRef.current) {
      const offsetInSegment = newTime - segment.timelineStart;
      try { videoRef.current.currentTime = segment.originalStart + offsetInSegment; } catch (_) {}
    }
  }, [videoSegments]);

  const seekTimelineTo = useCallback((newTime) => {
    const clamped = Math.max(0, Math.min(projectDuration, newTime));
    setCurrentTime(clamped);
    currentTimeRef.current = clamped;
    seekPreviewToTime(clamped);

    // If audio segments exist, try to sync their elements as well (best-effort)
    Object.values(audioRefs.current).forEach((ref) => {
      try { if (ref) ref.currentTime = clamped; } catch (_) {}
    });
  }, [projectDuration, seekPreviewToTime]);

  const setPlayheadFromClientX = useCallback((clientX) => {
    // IMPORTANT: use the *scroll container* rect for pointer math.
    // When the timeline is scrolled, the inner content's bounding rect shifts left,
    // so using timelineRef.current.getBoundingClientRect() would double-count scrollLeft.
    const containerEl = timelineContainerRef.current;
    const rect = (containerEl || timelineRef.current)?.getBoundingClientRect?.();
    if (!rect) return;

    const scrollLeft = containerEl?.scrollLeft || 0;
    const x = clientX - rect.left + scrollLeft - TRACK_LABEL_WIDTH;
    if (x < 0) return;

    const newTime = Math.max(0, Math.min(projectDuration, getTimeFromPosition(x)));
    latestScrubTimeRef.current = newTime;
    setCurrentTime(newTime);

    // Pause playback when user positions the playhead
    if (isPlaying) {
      if (videoRef.current) videoRef.current.pause();
      Object.values(audioRefs.current).forEach(ref => ref?.pause());
      setIsPlaying(false);
    }

    seekPreviewToTime(newTime);
  }, [projectDuration, getTimeFromPosition, isPlaying, seekPreviewToTime]);

  
  const timelinePressRef = useRef({ active: false, t0: 0, x0: 0, y0: 0, moved: false });

const handleTimelinePointerDownCapture = (e) => {
    // Do NOT move the playhead on pointerdown (prevents long-press/resize interference).
    // We'll move it only on a simple click.
    if (e.button != null && e.button !== 0) return;

    const t = e.target;
    if (t?.closest?.('[data-no-scrub="1"]')) return;
    if (t?.closest?.('button, a, input, textarea, select, [role="button"]')) return;

    timelinePressRef.current = {
      active: true,
      t0: performance.now(),
      x0: e.clientX,
      y0: e.clientY,
      moved: false,
    };
  };



  const handleTimelineClick = (e) => {
    if (!timelineRef.current || isDraggingPlayhead || draggingTrack) return;

    const press = timelinePressRef.current;
    // Only treat as click if it was a short press with no meaningful move.
    const elapsed = press?.active ? (performance.now() - (press.t0 || 0)) : 0;
    const dx = press?.active ? Math.abs(e.clientX - (press.x0 || 0)) : 0;
    const dy = press?.active ? Math.abs(e.clientY - (press.y0 || 0)) : 0;

    // Long-press => do nothing (prevents playhead jumping under the cursor while resizing/holding).
    if (press?.active && (elapsed > 200 || dx > 3 || dy > 3)) {
      timelinePressRef.current.active = false;
      return;
    }

    timelinePressRef.current.active = false;
    setPlayheadFromClientX(e.clientX);
  };


  // Improve mouse-wheel behavior in the timeline:
  // - If the timeline can scroll vertically (many tracks), the wheel scrolls vertically as expected.
  // - Otherwise, the wheel scrolls horizontally (common behavior in video editors).
  // - Holding Shift always forces horizontal scrolling.
  const handleTimelineWheel = useCallback((e) => {
    const el = timelineContainerRef.current;
    if (!el) return;

    // CTRL/CMD + wheel => zoom timeline (like most editors)
    // (On Mac trackpads, pinch-to-zoom often appears as ctrlKey + wheel.)
    if (e.ctrlKey || e.metaKey) {
      // Prevent browser zoom / trackpad page zoom
      e.preventDefault();

      const containerRect = (timelineContainerRef.current || timelineRef.current)?.getBoundingClientRect?.();
      if (!containerRect) return;

      // Compute the timeline time under the mouse before zoom
      const pointerXInTimelinePx =
        (e.clientX - containerRect.left) +
        (el.scrollLeft || 0) -
        TRACK_LABEL_WIDTH;

      const timeUnderPointer = Math.max(0, getTimeFromPosition(pointerXInTimelinePx));

      const dy = e.deltaY || 0;
      const step = 5;
      const nextZoom = Math.max(25, Math.min(400, zoom + (dy < 0 ? step : -step)));

      // If nothing changes, do nothing
      if (nextZoom === zoom) return;

      setZoom(nextZoom);

      // Keep the same time under the cursor after zoom by adjusting scrollLeft
      const nextPixelsPerSecond = BASE_PIXELS_PER_SECOND * (nextZoom / 100);
      const pointerXInsideContainer = (e.clientX - containerRect.left);

      const nextScrollLeft =
        (timeUnderPointer * nextPixelsPerSecond) +
        TRACK_LABEL_WIDTH -
        pointerXInsideContainer;

      // Defer to next frame so widths update smoothly
      requestAnimationFrame(() => {
        if (timelineContainerRef.current) {
          timelineContainerRef.current.scrollLeft = Math.max(0, nextScrollLeft);
        }
      });

      return;
    }

    const canScrollVertically = el.scrollHeight > el.clientHeight + 1;
    const canScrollHorizontally = el.scrollWidth > el.clientWidth + 1;

    const dx = e.deltaX || 0;
    const dy = e.deltaY || 0;

    // Shift + wheel (or no vertical overflow) => horizontal scroll
    if ((e.shiftKey || !canScrollVertically) && canScrollHorizontally) {
      e.preventDefault();
      el.scrollLeft += (Math.abs(dx) > 0 ? dx : dy);
      return;
    }

    // If we can scroll vertically, let the browser do it naturally (no preventDefault).
    if (canScrollVertically) return;

    // Fallback: no vertical overflow but horizontal overflow exists.
    if (canScrollHorizontally) {
      e.preventDefault();
      el.scrollLeft += dy;
    }
  }, [zoom, getTimeFromPosition, setZoom]);


const handleTimelineScrubPointerDown = (e) => {
  // Left click only
  if (e.button != null && e.button !== 0) return;

  // Don't start scrubbing if user is interacting with a clip/handle/menu
  const t = e.target;
  if (t?.closest?.('[data-clip="1"]')) return;
  if (t?.closest?.('[data-no-scrub="1"]')) return;
  if (t?.closest?.('button, a, input, textarea, select, [role="button"]')) return;

  handlePlayheadMouseDown(e);
};

const handlePlayheadMouseDown = (e) => {
  e.preventDefault();
  e.stopPropagation();

  // Pause playback while scrubbing
  if (videoRef.current && !videoRef.current.paused) {
    videoRef.current.pause();
  }
  Object.values(audioRefs.current).forEach(ref => ref?.pause());
  setIsPlaying(false);

  setIsDraggingPlayhead(true);
  isScrubbingRef.current = true;
  scrubPointerIdRef.current = e.pointerId ?? null;

  // Capture pointer so we reliably receive the release, even outside the element
  try {
    if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  } catch (_) {}

  const updateFromEvent = (ev) => {
    const containerEl = timelineContainerRef.current;
    const rect = (containerEl || timelineRef.current)?.getBoundingClientRect?.();
    if (!rect) return;

    const scrollLeft = containerEl?.scrollLeft || 0;

    const x = ev.clientX - rect.left + scrollLeft - TRACK_LABEL_WIDTH;
    if (x < 0) return;

    const newTime = Math.max(0, Math.min(projectDuration, getTimeFromPosition(x)));
    latestScrubTimeRef.current = newTime;
    setCurrentTime(newTime);
  };

  // Set playhead immediately on pointer down
  updateFromEvent(e);

  const onMove = (ev) => {
    // If the button is no longer pressed but we missed pointerup, stop scrubbing
    if (typeof ev.buttons === 'number' && ev.buttons === 0) {
      stop();
      return;
    }
    if (!isScrubbingRef.current) return;
    updateFromEvent(ev);
  };

  const stop = () => {
    if (!isScrubbingRef.current) return;

    isScrubbingRef.current = false;
    scrubPointerIdRef.current = null;
    setIsDraggingPlayhead(false);

    // Seek the preview video to the correct segment position (ready for play)
    const finalTime = latestScrubTimeRef.current;
    const segment = videoSegments.find(seg =>
      finalTime >= seg.timelineStart && finalTime < seg.timelineStart + seg.duration
    );

    if (segment && videoRef.current) {
      const offsetInSegment = finalTime - segment.timelineStart;
      try {
        videoRef.current.currentTime = segment.originalStart + offsetInSegment;
      } catch (_) {}
    }

    document.removeEventListener('pointermove', onMove, true);
    document.removeEventListener('pointerup', stop, true);
    document.removeEventListener('pointercancel', stop, true);
    document.removeEventListener('mouseup', stop, true);
    window.removeEventListener('blur', stop, true);
  };

  document.addEventListener('pointermove', onMove, { passive: false, capture: true });
  document.addEventListener('pointerup', stop, { passive: true, capture: true });
  document.addEventListener('pointercancel', stop, { passive: true, capture: true });
  document.addEventListener('mouseup', stop, { passive: true, capture: true });
  window.addEventListener('blur', stop, { passive: true, capture: true });
};

  
  // ============ MAGNETIC SNAP + REORDER HELPERS ============

  const SNAP_THRESHOLD_SECONDS = 0.15; // ~150ms feels good; adjust if you want stronger snap

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));


const updateImageTrack = (id, patch) => {
  setImageTracks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
};

const fitTextRafRef = useRef({});

const getPreviewRect = () => previewStageRef.current?.getBoundingClientRect?.() || null;

const fitTextNow = (id) => {
  const el = textEditableRefs.current?.[id];
  if (!el) return;

  const rect = getPreviewRect();
  if (!rect) return;

  const track = (textTracksRef.current || []).find((t) => t.id === id);
  if (!track) return;

  // Measure within the editable element itself (it has padding applied).
  const minSize = 8;
  const maxSize = Math.max(minSize, Math.round(track.fontSize ?? 28));

  // If it already fits at maxSize, keep it.
  const fitsAt = (px) => {
    el.style.fontSize = `${px}px`;
    // Force reflow read
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    if (!cw || !ch) return true;
    // scrollWidth/Height include overflow content
    return el.scrollWidth <= cw + 1 && el.scrollHeight <= ch + 1;
  };

  // If it doesn't fit, shrink via binary search.
  if (fitsAt(maxSize)) {
    // Restore exact size from track (avoid small drift)
    el.style.fontSize = `${maxSize}px`;
    return;
  }

  let lo = minSize;
  let hi = maxSize;
  let best = minSize;

  // Binary search for the largest size that fits.
  for (let i = 0; i < 14; i++) {
    const mid = Math.floor((lo + hi) / 2);
    if (fitsAt(mid)) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  // Apply & persist if changed
  if ((track.fontSize ?? 28) !== best) {
    updateTextTrack(id, { fontSize: best }, { skipFit: true });
  } else {
    el.style.fontSize = `${best}px`;
  }
};

const scheduleFitText = (id) => {
  if (!id) return;
  const prev = fitTextRafRef.current[id];
  if (prev) {
    try { cancelAnimationFrame(prev); } catch (_) {}
  }
  // Wait 2 frames so DOM has the new box dimensions/text
  fitTextRafRef.current[id] = requestAnimationFrame(() => {
    fitTextRafRef.current[id] = requestAnimationFrame(() => {
      fitTextNow(id);
      delete fitTextRafRef.current[id];
    });
  });
};

const updateTextTrack = (id, patch, opts = {}) => {
  setTextTracks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  if (opts?.skipFit) return;
  // Auto-fit when content or box changes
  if (
    patch?.text !== undefined ||
    patch?.w !== undefined ||
    patch?.h !== undefined ||
    patch?.fontFamily !== undefined ||
    patch?.fontWeight !== undefined ||
    patch?.fontStyle !== undefined ||
    patch?.letterSpacing !== undefined ||
    patch?.lineHeight !== undefined ||
    patch?.padding !== undefined ||
    patch?.align !== undefined
  ) {
    scheduleFitText(id);
  }
};


const beginPointerAction = (action) => {
  // Respect element lock
  if (action?.targetType === 'video') {
    const seg = videoSegments.find(s => s.id === action.id);
    if (seg?.locked) return;
  }
  if (action?.targetType === 'image') {
    const img = imageTracks.find(t => t.id === action.id);
    if (img?.locked) return;
  }
  if (action?.targetType === 'text') {
    const txt = textTracks.find(t => t.id === action.id);
    if (txt?.locked) return;
  }
  activePointerActionRef.current = action;

  // Use capture listeners so element-level handlers calling stopPropagation()
  // can't block the global pointerup from reaching us.
  const stop = () => {
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('pointerup', stop, true);
    window.removeEventListener('pointercancel', stop, true);
    window.removeEventListener('mouseup', stop, true);
    window.removeEventListener('blur', stop, true);
    // Clicking elsewhere should also end the action (covers edge cases)
    window.removeEventListener('pointerdown', stop, true);
    suppressNextClickRef.current = true;
    activePointerActionRef.current = null;
  };

  function onMove(e) {
    const a = activePointerActionRef.current;
    if (!a) return;

    // If button is released but we missed pointerup, stop.
    if (typeof e.buttons === 'number' && e.buttons === 0) {
      stop();
      return;
    }

    const rect = getPreviewRect();
    if (!rect) return;

    const dx = e.clientX - a.startX;
    const dy = e.clientY - a.startY;

    if (a.targetType === 'image') {
      const boxWpx = (a.startState.w / 100) * rect.width;
      const boxHpx = (a.startState.h / 100) * rect.height;

      if (a.type === 'move') {
        const nx = clamp(a.startState.x + (dx / rect.width) * 100, -50, 150);
        const ny = clamp(a.startState.y + (dy / rect.height) * 100, -50, 150);
        updateImageTrack(a.id, { x: nx, y: ny });
        return;
      }

      if (a.type === 'resize') {
        let { x, y, w, h } = a.startState;

        if (a.handle.includes('e')) w = clamp(a.startState.w + (dx / rect.width) * 100, 2, 300);
        if (a.handle.includes('s')) h = clamp(a.startState.h + (dy / rect.height) * 100, 2, 300);

        if (a.handle.includes('w')) {
          w = clamp(a.startState.w - (dx / rect.width) * 100, 2, 300);
          x = clamp(a.startState.x + (dx / rect.width) * 100 / 2, -50, 150);
        }

        if (a.handle.includes('n')) {
          h = clamp(a.startState.h - (dy / rect.height) * 100, 2, 300);
          y = clamp(a.startState.y + (dy / rect.height) * 100 / 2, -50, 150);
        }

        updateImageTrack(a.id, { x, y, w, h });
        return;
      }

      if (a.type === 'crop') {
        let { cropTop, cropRight, cropBottom, cropLeft } = a.startState;

        if (a.handle === 'left') cropLeft = clamp(a.startState.cropLeft + (dx / Math.max(1, boxWpx)) * 100, 0, 80);
        if (a.handle === 'right') cropRight = clamp(a.startState.cropRight - (dx / Math.max(1, boxWpx)) * 100, 0, 80);
        if (a.handle === 'top') cropTop = clamp(a.startState.cropTop + (dy / Math.max(1, boxHpx)) * 100, 0, 80);
        if (a.handle === 'bottom') cropBottom = clamp(a.startState.cropBottom - (dy / Math.max(1, boxHpx)) * 100, 0, 80);

        updateImageTrack(a.id, { cropTop, cropRight, cropBottom, cropLeft });
        return;
      }
    }

if (a.targetType === 'text') {
  if (a.type === 'move') {
    const nx = clamp(a.startState.x + (dx / rect.width) * 100, 0, 100);
    const ny = clamp(a.startState.y + (dy / rect.height) * 100, 0, 100);
    updateTextTrack(a.id, { x: nx, y: ny });
    return;
  }

  if (a.type === 'resize') {
    let { x, y, w, h } = a.startState;

    if (a.handle.includes('e')) w = clamp(a.startState.w + (dx / rect.width) * 100, 5, 100);
    if (a.handle.includes('s')) h = clamp(a.startState.h + (dy / rect.height) * 100, 5, 100);

    if (a.handle.includes('w')) {
      w = clamp(a.startState.w - (dx / rect.width) * 100, 5, 100);
      x = clamp(a.startState.x + (dx / rect.width) * 100 / 2, 0, 100);
    }

    if (a.handle.includes('n')) {
      h = clamp(a.startState.h - (dy / rect.height) * 100, 5, 100);
      y = clamp(a.startState.y + (dy / rect.height) * 100 / 2, 0, 100);
    }

    updateTextTrack(a.id, { x, y, w, h });
    return;
  }
}

    if (a.targetType === 'video') {
  const seg = videoSegments.find((s) => s.id === a.id);
  if (!seg) return;

  const rect = getPreviewRect();
  if (!rect) return;

  const dx = e.clientX - a.startX;
  const dy = e.clientY - a.startY;

  const start = a.startState || {};
  const startXc = start.x ?? (seg.x ?? 50);
  const startYc = start.y ?? (seg.y ?? 50);
  const startW = start.w ?? (seg.w ?? 100);
  const startH = start.h ?? (seg.h ?? 100);

  if (a.type === 'move') {
    const nx = clamp(startXc + (dx / rect.width) * 100, -50, 150);
    const ny = clamp(startYc + (dy / rect.height) * 100, -50, 150);
    setVideoSegments((prev) => prev.map((s) => (s.id === a.id ? { ...s, x: nx, y: ny } : s)));
    return;
  }

  if (a.type === 'resize') {
    let x = startXc;
    let y = startYc;
    let w = startW;
    let h = startH;

    const handle = a.handle || 'se';

    if (handle.includes('e')) w = clamp(startW + (dx / rect.width) * 100, 2, 300);
    if (handle.includes('s')) h = clamp(startH + (dy / rect.height) * 100, 2, 300);

    if (handle.includes('w')) {
      w = clamp(startW - (dx / rect.width) * 100, 2, 300);
      x = clamp(startXc + (dx / rect.width) * 50, -50, 150);
    }

    if (handle.includes('n')) {
      h = clamp(startH - (dy / rect.height) * 100, 2, 300);
      y = clamp(startYc + (dy / rect.height) * 50, -50, 150);
    }

    setVideoSegments((prev) => prev.map((s) => (s.id === a.id ? { ...s, x, y, w, h } : s)));
    return;
  }

  if (a.type === 'crop') {
    const boxWpx = (startW / 100) * rect.width;
    const boxHpx = (startH / 100) * rect.height;

    let cropTop = start.cropTop ?? (seg.cropTop ?? 0);
    let cropRight = start.cropRight ?? (seg.cropRight ?? 0);
    let cropBottom = start.cropBottom ?? (seg.cropBottom ?? 0);
    let cropLeft = start.cropLeft ?? (seg.cropLeft ?? 0);

    if (a.handle === 'left') cropLeft = clamp((start.cropLeft ?? cropLeft) + (dx / Math.max(1, boxWpx)) * 100, 0, 80);
    if (a.handle === 'right') cropRight = clamp((start.cropRight ?? cropRight) - (dx / Math.max(1, boxWpx)) * 100, 0, 80);
    if (a.handle === 'top') cropTop = clamp((start.cropTop ?? cropTop) + (dy / Math.max(1, boxHpx)) * 100, 0, 80);
    if (a.handle === 'bottom') cropBottom = clamp((start.cropBottom ?? cropBottom) - (dy / Math.max(1, boxHpx)) * 100, 0, 80);

    setVideoSegments((prev) =>
      prev.map((s) => (s.id === a.id ? { ...s, cropTop, cropRight, cropBottom, cropLeft } : s))
    );
    return;
  }
}
  }

  window.addEventListener('pointermove', onMove, true);
  window.addEventListener('pointerup', stop, true);
  window.addEventListener('pointercancel', stop, true);
  window.addEventListener('mouseup', stop, true);
  window.addEventListener('blur', stop, true);
  // Any new pointerdown elsewhere ends the current drag/resize
  window.addEventListener('pointerdown', stop, true);
};

  const buildSnapCandidates = useCallback((exclude) => {
    const candidates = new Set([0, currentTime]);

    // Video segment edges
    videoSegments.forEach(s => {
      if (exclude?.type === 'video-segment' && exclude?.id === s.id) return;
      candidates.add(s.timelineStart);
      candidates.add(s.timelineStart + s.duration);
    });

    // Audio edges (start/end on timeline, not original file)
    audioTracks.forEach(t => {
      if (exclude?.type === 'audio' && exclude?.id === t.id) return;
      candidates.add(t.startTime);
      candidates.add(t.startTime + t.duration);
    });

    // Image edges
    imageTracks.forEach(t => {
      if (exclude?.type === 'image' && exclude?.id === t.id) return;
      candidates.add(t.startTime);
      candidates.add(t.startTime + t.duration);
    });

    // Text edges
    textTracks.forEach(t => {
      if (exclude?.type === 'text' && exclude?.id === t.id) return;
      candidates.add(t.startTime);
      candidates.add(t.startTime + t.duration);
    });

    return Array.from(candidates).filter(n => Number.isFinite(n)).sort((a, b) => a - b);
  }, [videoSegments, audioTracks, imageTracks, textTracks, currentTime]);

  const applyMagneticSnap = useCallback((proposedStart, duration, exclude) => {
    const candidates = buildSnapCandidates(exclude);
    if (!candidates.length) return proposedStart;

    // Snap start OR end, pick the closest adjustment
    let bestStart = proposedStart;
    let bestDist = SNAP_THRESHOLD_SECONDS + 1;

    for (const c of candidates) {
      const distStart = Math.abs(proposedStart - c);
      if (distStart < bestDist) {
        bestDist = distStart;
        bestStart = c;
      }

      const proposedEnd = proposedStart + duration;
      const distEnd = Math.abs(proposedEnd - c);
      if (distEnd < bestDist) {
        bestDist = distEnd;
        bestStart = c - duration;
      }
    }

    if (bestDist <= SNAP_THRESHOLD_SECONDS) {
      return clamp(bestStart, 0, Math.max(0, projectDuration - duration));
    }

    return proposedStart;
  }, [buildSnapCandidates, projectDuration]);

  const moveArrayItem = (arr, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return arr;
    const next = arr.slice();
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    return next;
  };

  const getTrackRowFromPoint = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return null;
    const row = el.closest?.('[data-track-row="1"]');
    if (!row) return null;
    const trackType = row.getAttribute('data-track-type');
    const trackId = row.getAttribute('data-track-id');
    const indexStr = row.getAttribute('data-track-index');
    const index = indexStr != null ? Number(indexStr) : null;
    if (!trackType || !trackId || index == null || Number.isNaN(index)) return null;
    return { trackType, trackId, index };
  };


  const getTimelineXFromClient = (clientX) => {
    if (!timelineRef.current) return 0;
    const rect = timelineRef.current.getBoundingClientRect();
    const scrollLeft = timelineContainerRef.current?.scrollLeft || 0;
    return clientX - rect.left + scrollLeft - TRACK_LABEL_WIDTH;
  };


  const VISUAL_LANE_HEIGHT_PX = 64; // h-16

  const getVisualLaneIndexFromClientY = (clientY) => {
    const el = visualLanesRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const y = clientY - rect.top;
    if (y < 0) return 0;
    const lane = Math.floor(y / VISUAL_LANE_HEIGHT_PX);
    if (!Number.isFinite(lane) || lane < 0) return 0;
    return lane;
  };

  const autoScrollTimelineWhileDragging = (clientX, clientY) => {
    const container = timelineContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const edge = 48;
    const maxSpeed = 22;

    // Horizontal autoscroll
    if (clientX < rect.left + edge) {
      const t = (rect.left + edge - clientX) / edge;
      container.scrollLeft -= Math.ceil(maxSpeed * Math.min(1, t));
    } else if (clientX > rect.right - edge) {
      const t = (clientX - (rect.right - edge)) / edge;
      container.scrollLeft += Math.ceil(maxSpeed * Math.min(1, t));
    }

    // Vertical autoscroll (if needed)
    if (container.scrollHeight > container.clientHeight + 1) {
      if (clientY < rect.top + edge) {
        const t = (rect.top + edge - clientY) / edge;
        container.scrollTop -= Math.ceil(maxSpeed * Math.min(1, t));
      } else if (clientY > rect.bottom - edge) {
        const t = (clientY - (rect.bottom - edge)) / edge;
        container.scrollTop += Math.ceil(maxSpeed * Math.min(1, t));
      }
    }
  };


  const handleSegmentDragStart = (e, segment) => {
    e.preventDefault();
    e.stopPropagation();


    if (segment?.locked) {
      showNotification(ct('creator.timeline.locked', 'Élément verrouillé'));
      return;
    }
    try {
      if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    } catch (_) {}
    
    const dragStartClientX = e.clientX;
    const dragStartClientY = e.clientY;
    const originalStartTime = segment.timelineStart;

    // Compute the cursor offset inside the clip using TIMELINE coordinates (accounts for scroll)
    const clickTimelineX = getTimelineXFromClient(e.clientX);
    const segmentLeftPx = getPositionFromTime(segment.timelineStart);
    const offsetPx = Math.max(0, clickTimelineX - segmentLeftPx);
    const offsetTime = getTimeFromPosition(offsetPx);

    const DRAG_THRESHOLD_PX = 1;

setDraggingTrack({ ...segment });
    setSelectedTrack({ id: segment.id, type: 'video-segment' });
    
    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault?.();
      autoScrollTimelineWhileDragging(moveEvent.clientX, moveEvent.clientY);
      if (typeof moveEvent.buttons === 'number' && moveEvent.buttons === 0) { handleMouseUp(); return; }
      const dx = moveEvent.clientX - dragStartClientX;
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      if (timelineRef.current) {
        const timelineRect = timelineRef.current.getBoundingClientRect();
        const scrollLeft = timelineContainerRef.current?.scrollLeft || 0;
        const x = moveEvent.clientX - timelineRect.left + scrollLeft - TRACK_LABEL_WIDTH;
        let newStartTime = getTimeFromPosition(x) - offsetTime;
        const maxStart = Math.max(0, projectDuration - segment.duration);
        newStartTime = Math.max(0, Math.min(maxStart, newStartTime));
        newStartTime = Math.round(newStartTime * 100) / 100; // fine snap during drag (0.01s)
        if (!moveEvent.altKey) {
          newStartTime = applyMagneticSnap(newStartTime, segment.duration, { type: 'video-segment', id: segment.id });
        }
        
        autoScrollTimelineWhileDragging(moveEvent.clientX, moveEvent.clientY);

        let preferredLane = segment.lane ?? 0;
        const dy = moveEvent.clientY - dragStartClientY;
        if (Math.abs(dy) > 10) {
          const laneFromY = getVisualLaneIndexFromClientY(moveEvent.clientY);
          if (typeof laneFromY === 'number') preferredLane = laneFromY;
        }

        const newLane = findAvailableLane({ start: newStartTime, duration: segment.duration, excludeType: 'video-segment', excludeId: segment.id, preferredLane });

        setVideoSegments(prev => prev.map(seg => 
          seg.id === segment.id ? { ...seg, timelineStart: newStartTime, lane: newLane } : seg
        ));
        extendProjectIfNeeded(newStartTime + segment.duration);
      }
    };
    
    const handleMouseUp = () => {
      setDraggingTrack(null);
      document.removeEventListener('pointermove', handleMouseMove, true);
      document.removeEventListener('pointerup', handleMouseUp, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      document.removeEventListener('pointercancel', handleMouseUp, true);
      window.removeEventListener('blur', handleMouseUp);
    };
    
    document.addEventListener('pointermove', handleMouseMove, { passive: false, capture: true });
    document.addEventListener('pointerup', handleMouseUp, { passive: true, capture: true });
    document.addEventListener('mouseup', handleMouseUp, { passive: true, capture: true });
    window.addEventListener('blur', handleMouseUp);
    document.addEventListener('pointercancel', handleMouseUp, { passive: true, capture: true });
  };

  const handleTrackDragStart = (e, track, trackType) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    } catch (_) {}

    const dragStartClientX = e.clientX;

    // Compute the cursor offset inside the clip using TIMELINE coordinates (accounts for scroll)
    const clickTimelineX = getTimelineXFromClient(e.clientX);
    const clipLeftPx = getPositionFromTime(trackType === 'video-segment' ? (track.timelineStart ?? 0) : (track.startTime ?? 0));
    const offsetPx = Math.max(0, clickTimelineX - clipLeftPx);
    const offsetTime = getTimeFromPosition(offsetPx);

    const DRAG_THRESHOLD_PX = 1;

    setDraggingTrack({ ...track, trackType });
    setSelectedTrack({ id: track.id, type: trackType });

    // Track reordering support (vertical drag between lanes)
    let currentIndex = -1;
    if (trackType === 'audio') currentIndex = audioTracks.findIndex(t => t.id === track.id);
    if (trackType === 'image') currentIndex = imageTracks.findIndex(t => t.id === track.id);
    if (trackType === 'text') currentIndex = textTracks.findIndex(t => t.id === track.id);

    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault?.();
      autoScrollTimelineWhileDragging(moveEvent.clientX, moveEvent.clientY);
      if (typeof moveEvent.buttons === 'number' && moveEvent.buttons === 0) { handleMouseUp(); return; }
      const dx = moveEvent.clientX - dragStartClientX;
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      if (!timelineRef.current) return;

      const timelineRect = timelineRef.current.getBoundingClientRect();
      const scrollLeft = timelineContainerRef.current?.scrollLeft || 0;
      const x = moveEvent.clientX - timelineRect.left + scrollLeft - TRACK_LABEL_WIDTH;

      let newStartTime = getTimeFromPosition(x) - offsetTime;
      const maxStart = Math.max(0, projectDuration - (track.duration ?? 0));
      newStartTime = Math.max(0, Math.min(maxStart, newStartTime));
      newStartTime = Math.round(newStartTime * 100) / 100; // fine snap during drag (0.01s)
        if (!moveEvent.altKey) {
          newStartTime = applyMagneticSnap(newStartTime, track.duration, { type: trackType, id: track.id });
        }

      // Vertical lane selection (visual lanes) + audio list reorder
      let preferredLane = track.lane ?? 0;

      if (trackType === 'audio') {
        const rowHit = getTrackRowFromPoint(moveEvent.clientX, moveEvent.clientY);
        if (rowHit && rowHit.trackType === trackType && typeof rowHit.index === 'number') {
          const nextIndex = rowHit.index;
          if (nextIndex !== currentIndex && nextIndex >= 0) {
            setAudioTracks((prev) => {
              const from = prev.findIndex((t) => t.id === track.id);
              if (from === -1) return prev;
              const to = Math.max(0, Math.min(prev.length - 1, nextIndex));
              currentIndex = to;
              return moveArrayItem(prev, from, to);
            });
          }
        }
      } else if (trackType === 'image') {
        autoScrollTimelineWhileDragging(moveEvent.clientX, moveEvent.clientY);

        const laneFromY = getVisualLaneIndexFromClientY(moveEvent.clientY);
        if (typeof laneFromY === 'number') {
          preferredLane = laneFromY;
        }
        const laneCandidate = findAvailableLane({
          start: newStartTime,
          duration: track.duration ?? 0,
          excludeType: 'image',
          excludeId: track.id,
          preferredLane,
        });
        preferredLane = laneCandidate;
      } else if (trackType === 'text') {
        autoScrollTimelineWhileDragging(moveEvent.clientX, moveEvent.clientY);

        const laneFromY = getVisualLaneIndexFromClientY(moveEvent.clientY);
        if (typeof laneFromY === 'number') {
          preferredLane = laneFromY;
        }
        const laneCandidate = findAvailableLane({
          start: newStartTime,
          duration: track.duration ?? 0,
          excludeType: 'text',
          excludeId: track.id,
          preferredLane,
        });
        preferredLane = laneCandidate;
      }


      if (trackType === 'audio') {
        setAudioTracks(prev => prev.map(t =>
          t.id === track.id ? { ...t, startTime: newStartTime } : t
        ));
        extendProjectIfNeeded(newStartTime + track.duration);
      } else if (trackType === 'image') {
        setImageTracks(prev => prev.map(t =>
          t.id === track.id ? { ...t, startTime: newStartTime, lane: preferredLane } : t
        ));
        extendProjectIfNeeded(newStartTime + track.duration);
      } else if (trackType === 'text') {
        setTextTracks(prev => prev.map(t =>
          t.id === track.id ? { ...t, startTime: newStartTime, lane: preferredLane } : t
        ));
        extendProjectIfNeeded(newStartTime + track.duration);
      }
    };

    const handleMouseUp = () => {
      setDraggingTrack(null);
      document.removeEventListener('pointermove', handleMouseMove, true);
      document.removeEventListener('pointerup', handleMouseUp, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
      document.removeEventListener('pointercancel', handleMouseUp, true);
      window.removeEventListener('blur', handleMouseUp);
    };

    document.addEventListener('pointermove', handleMouseMove, { passive: false, capture: true });
    document.addEventListener('pointerup', handleMouseUp, { passive: true, capture: true });
    document.addEventListener('mouseup', handleMouseUp, { passive: true, capture: true });
    window.addEventListener('blur', handleMouseUp);
    document.addEventListener('pointercancel', handleMouseUp, { passive: true, capture: true });
  };

  // Handle audio track trim from left (adjust where the audio starts playing)
const handleAudioTrimStart = (e, track) => {
  e.preventDefault();
  e.stopPropagation();

  // Pointer capture helps ensure we still receive the pointerup even if the cursor leaves the handle.
  try {
    if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  } catch (_) {}

  const initialClientX = e.clientX;
  const initialTrimStart = track.trimStart ?? 0;
  const initialStartTime = track.startTime ?? 0;
  const initialTrimEnd = track.trimEnd ?? track.originalDuration;

  const handlePointerMove = (moveEvent) => {
    moveEvent.preventDefault?.();

    // Safety kill-switch: if the mouse button is no longer pressed, stop the drag.
    if (typeof moveEvent.buttons === 'number' && moveEvent.buttons === 0) {
      handlePointerUp();
      return;
    }

    const deltaX = moveEvent.clientX - initialClientX;
    const deltaTime = getTimeFromPosition(deltaX);

    // New trimStart must be within [0, trimEnd - 1s]
    const maxTrimStart = Math.max(0, (initialTrimEnd ?? track.originalDuration) - 1);
    const newTrimStart = Math.max(0, Math.min(maxTrimStart, initialTrimStart + deltaTime));

    // Timeline start moves with the trim so the audible content stays aligned
    const newStartTime = Math.max(0, initialStartTime + (newTrimStart - initialTrimStart));
    const newDuration = (initialTrimEnd ?? track.originalDuration) - newTrimStart;

    setAudioTracks(prev =>
      prev.map(t =>
        t.id === track.id
          ? { ...t, trimStart: newTrimStart, startTime: newStartTime, duration: newDuration }
          : t
      )
    );
  };

  const handlePointerUp = () => {
    document.removeEventListener('pointermove', handlePointerMove, true);
    document.removeEventListener('pointerup', handlePointerUp, true);
    document.removeEventListener('pointercancel', handlePointerUp, true);
    document.removeEventListener('mouseup', handlePointerUp, true);
    window.removeEventListener('blur', handlePointerUp, true);
  };

  document.addEventListener('pointermove', handlePointerMove, { passive: false, capture: true });
  document.addEventListener('pointerup', handlePointerUp, { passive: true, capture: true });
  document.addEventListener('pointercancel', handlePointerUp, { passive: true, capture: true });
  document.addEventListener('mouseup', handlePointerUp, { passive: true, capture: true });
  window.addEventListener('blur', handlePointerUp, true);
};

// Handle audio track trim from right (adjust where the audio stops playing)
const handleAudioTrimEnd = (e, track) => {
  e.preventDefault();
  e.stopPropagation();

  try {
    if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  } catch (_) {}

  const initialClientX = e.clientX;
  const initialTrimStart = track.trimStart ?? 0;
  const initialTrimEnd = track.trimEnd ?? track.originalDuration;

  const handlePointerMove = (moveEvent) => {
    moveEvent.preventDefault?.();

    if (typeof moveEvent.buttons === 'number' && moveEvent.buttons === 0) {
      handlePointerUp();
      return;
    }

    const deltaX = moveEvent.clientX - initialClientX;
    const deltaTime = getTimeFromPosition(deltaX);

    // New trimEnd must be within [trimStart + 1s, originalDuration]
    const minTrimEnd = initialTrimStart + 1;
    const newTrimEnd = Math.max(minTrimEnd, Math.min(track.originalDuration, initialTrimEnd + deltaTime));
    const newDuration = newTrimEnd - initialTrimStart;

    setAudioTracks(prev =>
      prev.map(t =>
        t.id === track.id ? { ...t, trimEnd: newTrimEnd, duration: newDuration } : t
      )
    );

    extendProjectIfNeeded((track.startTime ?? 0) + newDuration);
  };

  const handlePointerUp = () => {
    document.removeEventListener('pointermove', handlePointerMove, true);
    document.removeEventListener('pointerup', handlePointerUp, true);
    document.removeEventListener('pointercancel', handlePointerUp, true);
    document.removeEventListener('mouseup', handlePointerUp, true);
    window.removeEventListener('blur', handlePointerUp, true);
  };

  document.addEventListener('pointermove', handlePointerMove, { passive: false, capture: true });
  document.addEventListener('pointerup', handlePointerUp, { passive: true, capture: true });
  document.addEventListener('pointercancel', handlePointerUp, { passive: true, capture: true });
  document.addEventListener('mouseup', handlePointerUp, { passive: true, capture: true });
  window.addEventListener('blur', handlePointerUp, true);
};

  // Handle image clip resize (similar to audio trim, but it stretches the image duration on the timeline)
  const handleImageResizeStart = (e, img) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    } catch (_) {}

    const initialClientX = e.clientX;
    const initialStartTime = img.startTime ?? 0;
    const initialDuration = img.duration ?? 1;
    const initialEndTime = initialStartTime + initialDuration;

    const minDuration = 0.1; // seconds

    const onMove = (ev) => {
      // Kill-switch if button released but pointerup was missed
      if (ev.buttons === 0) {
        stop();
        return;
      }
      const deltaX = ev.clientX - initialClientX;
      const deltaTime = getTimeFromPosition(deltaX);

      let proposedStart = initialStartTime + deltaTime;
      proposedStart = Math.max(0, proposedStart);

      // Keep end fixed while moving start
      let proposedDuration = initialEndTime - proposedStart;
      proposedDuration = Math.max(minDuration, proposedDuration);

      // Clamp to project duration
      if (proposedStart + proposedDuration > projectDuration) {
        proposedDuration = Math.max(minDuration, projectDuration - proposedStart);
      }

      setImageTracks((prev) =>
        prev.map((t) =>
          t.id === img.id
            ? { ...t, startTime: proposedStart, duration: proposedDuration, lane: findAvailableLane({ start: proposedStart, duration: proposedDuration, excludeType: 'image', excludeId: img.id, preferredLane: t.lane ?? 0 }) }
            : t
        )
      );
    };

    const stop = () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', stop, true);
      document.removeEventListener('pointercancel', stop, true);
      window.removeEventListener('blur', stop, true);
    };

    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', stop, true);
    document.addEventListener('pointercancel', stop, true);
    window.addEventListener('blur', stop, true);
  };

  const handleImageResizeEnd = (e, img) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    } catch (_) {}

    const initialClientX = e.clientX;
    const initialDuration = img.duration ?? 1;
    const startTime = img.startTime ?? 0;

    const minDuration = 0.1; // seconds

    const onMove = (ev) => {
      if (ev.buttons === 0) {
        stop();
        return;
      }
      const deltaX = ev.clientX - initialClientX;
      const deltaTime = getTimeFromPosition(deltaX);

      let proposedDuration = initialDuration + deltaTime;
      proposedDuration = Math.max(minDuration, proposedDuration);

      // Clamp to project duration
      if (startTime + proposedDuration > projectDuration) {
        proposedDuration = Math.max(minDuration, projectDuration - startTime);
      }

      setImageTracks((prev) =>
        prev.map((t) => (t.id === img.id ? { ...t, duration: proposedDuration, lane: findAvailableLane({ start: startTime, duration: proposedDuration, excludeType: 'image', excludeId: img.id, preferredLane: t.lane ?? 0 }) } : t))
      );
    };

    const stop = () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', stop, true);
      document.removeEventListener('pointercancel', stop, true);
      window.removeEventListener('blur', stop, true);
    };

    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', stop, true);
    document.addEventListener('pointercancel', stop, true);
    window.addEventListener('blur', stop, true);
  };

  // Handle VIDEO segment resize on the timeline (trim start/end)
  // - Left handle: keeps timeline end fixed, moves timeline start, and trims the original start accordingly.
  // - Right handle: keeps timeline start fixed, changes duration, and trims the original end accordingly.
  const handleVideoResizeStart = (e, segment) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    } catch (_) {}

    const initialClientX = e.clientX;
    const initialTimelineStart = segment.timelineStart ?? segment.startTime ?? 0;
    const initialDuration = segment.duration ?? 0;
    const initialTimelineEnd = initialTimelineStart + initialDuration;

    const initialOriginalStart = segment.originalStart ?? 0;
    const initialOriginalEnd = segment.originalEnd ?? (initialOriginalStart + initialDuration);

    const onMove = (ev) => {
      if (typeof ev.buttons === 'number' && ev.buttons === 0) {
        stop();
        return;
      }

      const deltaX = ev.clientX - initialClientX;
      const deltaTime = getTimeFromPosition(deltaX);

      // Move start; keep end fixed
      let proposedStart = Math.max(0, initialTimelineStart + deltaTime);
      let proposedDuration = initialTimelineEnd - proposedStart;
      proposedDuration = Math.max(MIN_TIMELINE_CLIP_DURATION, proposedDuration);

      // If duration was clamped, re-derive start so end stays fixed
      proposedStart = initialTimelineEnd - proposedDuration;

      // Clamp to project duration
      if (proposedStart + proposedDuration > projectDuration) {
        proposedDuration = Math.max(MIN_TIMELINE_CLIP_DURATION, projectDuration - proposedStart);
        proposedStart = Math.max(0, projectDuration - proposedDuration);
      }

      // Trim original start by the same amount we moved the timeline start
      const trimAmount = proposedStart - initialTimelineStart;
      let proposedOriginalStart = initialOriginalStart + trimAmount;
      // Keep original end fixed
      let proposedOriginalEnd = initialOriginalEnd;

      // Ensure original range is valid
      if (proposedOriginalEnd - proposedOriginalStart < MIN_TIMELINE_CLIP_DURATION) {
        proposedOriginalStart = proposedOriginalEnd - MIN_TIMELINE_CLIP_DURATION;
      }

      setVideoSegments((prev) =>
        prev.map((s) =>
          s.id === segment.id
            ? {
                ...s,
                timelineStart: proposedStart,
                startTime: proposedStart,
                duration: proposedDuration,
                originalStart: proposedOriginalStart,
                originalEnd: proposedOriginalEnd,
              }
            : s
        )
      );
    };

    const stop = () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', stop, true);
      document.removeEventListener('pointercancel', stop, true);
      window.removeEventListener('blur', stop, true);
    };

    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', stop, true);
    document.addEventListener('pointercancel', stop, true);
    window.addEventListener('blur', stop, true);
  };

  const handleVideoResizeEnd = (e, segment) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (e.currentTarget?.setPointerCapture && e.pointerId != null) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    } catch (_) {}

    const initialClientX = e.clientX;
    const startTime = segment.timelineStart ?? segment.startTime ?? 0;
    const initialDuration = segment.duration ?? 0;
    const initialOriginalStart = segment.originalStart ?? 0;

    const onMove = (ev) => {
      if (typeof ev.buttons === 'number' && ev.buttons === 0) {
        stop();
        return;
      }

      const deltaX = ev.clientX - initialClientX;
      const deltaTime = getTimeFromPosition(deltaX);

      let proposedDuration = initialDuration + deltaTime;
      proposedDuration = Math.max(MIN_TIMELINE_CLIP_DURATION, proposedDuration);

      // Clamp to project duration
      if (startTime + proposedDuration > projectDuration) {
        proposedDuration = Math.max(MIN_TIMELINE_CLIP_DURATION, projectDuration - startTime);
      }

      const proposedOriginalEnd = initialOriginalStart + proposedDuration;

      setVideoSegments((prev) =>
        prev.map((s) =>
          s.id === segment.id
            ? {
                ...s,
                duration: proposedDuration,
                originalStart: initialOriginalStart,
                originalEnd: proposedOriginalEnd,
              }
            : s
        )
      );

      extendProjectIfNeeded(startTime + proposedDuration);
    };

    const stop = () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', stop, true);
      document.removeEventListener('pointercancel', stop, true);
      window.removeEventListener('blur', stop, true);
    };

    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('pointerup', stop, true);
    document.addEventListener('pointercancel', stop, true);
    window.addEventListener('blur', stop, true);
  };


const generateTimeMarkers = () => {
    const markers = [];
    let interval = zoom >= 150 ? 5 : zoom >= 100 ? 10 : zoom >= 50 ? 30 : 60;
    for (let i = 0; i <= projectDuration; i += interval) {
      markers.push(i);
    }
    return markers;
  };

  const filteredTracks = allTracks?.filter(track => {
    const matchesSearch = !searchQuery || 
      track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (track.artist && track.artist.toLowerCase().includes(searchQuery.toLowerCase()));
    if (musicFilter === 'music') return matchesSearch && !track.is_mood;
    if (musicFilter === 'moods') return matchesSearch && track.is_mood;
    return matchesSearch;
  }) || [];

  const categoryImages = {
    'Motivant': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400',
    'Lifestyle': 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=400',
    'Jeu Vidéo': 'https://images.unsplash.com/photo-1538481199705-c710c4e965fc?w=400',
    'Relax': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400',
  };

  const timelineWidth = getTimelineWidth();
  const playheadPosition = getPositionFromTime(currentTime);

  const isVideoSegmentSelected = selectedTrack?.type === 'video-segment';
  const isImageSelected = selectedTrack?.type === 'image';
  const isAudioSelected = selectedTrack?.type === 'audio';
  const isTextSelected = selectedTrack?.type === 'text';

  const selectedTextTrack = useMemo(() => {
    if (selectedTrack?.type !== 'text') return null;
    return textTracks.find(t => t.id === selectedTrack.id) || null;
  }, [selectedTrack, textTracks]);

  const selectedSegment = getSelectedSegment();

    // Segment vidéo sous le playhead (lecture / scrub)
  const segmentAtTime = videoSegments.find((seg) => {
    const st = seg.startTime ?? seg.timelineStart ?? 0;
    const dur = seg.duration ?? 0;
    const t = currentTime ?? 0;
    return t >= st && t < st + dur;
  }) || null;

  // Segment affiché dans le preview :
  // - pendant la lecture : uniquement le segment qui est sous le playhead (sinon pas de vidéo -> images seules)
  // - à l'arrêt : si un segment vidéo est sélectionné, on l'affiche, sinon celui sous le playhead
  const previewSegment = isPlaying
    ? segmentAtTime
    : ((isVideoSegmentSelected && selectedSegment) ? selectedSegment : segmentAtTime);

  const videoUrl = previewSegment?.url || null;

  const getCurrentVolume = () => {
    if (isVideoSegmentSelected && selectedSegment) return selectedSegment.volume ?? 100;
    if (isAudioSelected) {
      const track = audioTracks.find(t => t.id === selectedTrack.id);
      return track?.volume ?? 100;
    }
    return 100;
  };

  const getCurrentOpacity = () => {
    if (isVideoSegmentSelected && selectedSegment) return selectedSegment.opacity ?? 100;
    if (isImageSelected) {
      const track = imageTracks.find(t => t.id === selectedTrack.id);
      return track?.opacity ?? 100;
    }
    if (isTextSelected) {
      const track = textTracks.find(t => t.id === selectedTrack.id);
      return track?.opacity ?? 100;
    }
    return 100;
  };

  // Get thumbnails for a specific segment
  const getSegmentThumbnails = (segment) => {
    const thumbs = segment?.thumbnails || [];
    if (!thumbs.length) return [];
    // thumbnails times are relative to the original video timeline
    return thumbs.filter(t => t.time >= segment.originalStart && t.time < segment.originalEnd);
  };

  // Pick the closest thumbnail for a given original timeline time (in seconds)
  const getClosestThumbnailAt = (segment, originalTime) => {
    const thumbs = segment?.thumbnails || [];
    if (!thumbs.length) return null;

    // Clamp time to available thumb range
    const t = Math.max(0, originalTime);

    // Thumbs are generated in increasing time order.
    // We choose the last thumb <= t (fallback to first).
    let chosen = thumbs[0];
    for (let i = 0; i < thumbs.length; i++) {
      if (thumbs[i].time <= t) chosen = thumbs[i];
      else break;
    }
    return chosen;
  };

  // Render a Canva-like thumbnail strip that fills the whole segment width.
  // We tile small thumbnails across the segment so you always see images, like in your screenshot.
  const renderSegmentThumbnailStrip = (segment) => {
    const widthPx = Math.max(getPositionFromTime(segment.duration), MIN_CLIP_WIDTH_PX);
    const tileWidth = Math.max(32, Math.min(72, getPositionFromTime(1))); // responsive with zoom
    const tiles = Math.max(1, Math.ceil(widthPx / tileWidth));

    const segmentThumbs = getSegmentThumbnails(segment);
    if (!segmentThumbs.length) {
      return (
        <div className="flex-1 bg-gradient-to-r from-cyan-600/40 to-cyan-500/20 flex items-center justify-center">
          <span className="text-[10px] text-cyan-300 truncate px-1">{formatTime(segment.originalStart)}-{formatTime(segment.originalEnd)}</span>
        </div>
      );
    }

    return (
      <div className="flex-1 h-full flex overflow-hidden">
        {Array.from({ length: tiles }).map((_, i) => {
          const r = (i + 0.5) / tiles; // midpoint sample
          const start = segment.originalStart ?? 0;
          const end = segment.originalEnd ?? (start + (segment.duration ?? 0));
          const span = Math.max(0.0001, end - start);
          const originalTime = start + r * span;
          const thumb = getClosestThumbnailAt(segment, originalTime) || segmentThumbs[0];
          return (
            <div
              key={i}
              className="h-full flex-shrink-0 border-r-0"
              style={{ width: `${tileWidth}px` }}
            >
              <img
                src={thumb.dataUrl}
                alt=""
                className="h-full w-full object-cover"
                style={{ opacity: (segment.opacity ?? 100) / 100 }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // Get current segment's transform and clip
  const getSegmentStyle = (segment) => {
    let transform = '';
    if (segment.flipH) transform += 'scaleX(-1) ';
    if (segment.flipV) transform += 'scaleY(-1) ';
    
    const clipPath = (segment.cropTop || segment.cropRight || segment.cropBottom || segment.cropLeft)
      ? `inset(${segment.cropTop || 0}% ${segment.cropRight || 0}% ${segment.cropBottom || 0}% ${segment.cropLeft || 0}%)`
      : 'none';
    
    return {
      transform: transform.trim() || 'none',
      opacity: (segment.opacity ?? 100) / 100,
      clipPath
    };
  };

  const renderStageBars = useCallback(() => {
    if (!exportStageCurrent) return null;
    return (
      <div className="space-y-2">
        {renderStages.map((step, idx) => {
          const stepNum = idx + 1;
          const progress = exportStageProgress?.[step.key] ?? 0;
          const percent = Math.round(progress * 100);
          const isActive = exportStageCurrent === stepNum;
          const isDone = exportStageCurrent > stepNum || percent >= 100;
          return (
            <div key={step.key} className={`rounded-lg p-2 border ${isActive ? 'border-purple-500/40 bg-white/5' : 'border-white/10 bg-white/3'}`}>
              <div className="flex items-center justify-between text-[11px] text-white/70 mb-1">
                <span className={isActive ? 'text-white/90' : 'text-white/60'}>
                  {ct('creator.stages.step', 'Étape {{current}}/3 · {{label}}')
                    .replace('{{current}}', String(stepNum))
                    .replace('{{label}}', step.label)}
                </span>
                <span className={isDone ? 'text-emerald-300' : 'text-white/60'}>
                  {percent}%
                </span>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${isDone ? 'bg-emerald-400' : isActive ? 'bg-purple-500' : 'bg-white/30'}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [exportStageCurrent, exportStageProgress, renderStages]);

  const containerClass = embedded
    ? "relative w-full h-full min-h-0 bg-[#0d0d14] flex flex-col overflow-hidden pb-16 md:pb-0"
    : "fixed inset-0 relative bg-[#0d0d14] z-[100] flex flex-col overflow-hidden pb-16 md:pb-0";

  return (
    <div className={containerClass} data-testid="creator-studio">

      <style>{`
        @keyframes formatPop {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* Emoji picker (emoji-picker-react) dark theme overrides */
        .EmojiPickerReact, .EmojiPickerReact * { box-sizing: border-box; }
        .EmojiPickerReact {
          --epr-bg-color: #0f0f16;
          --epr-hover-bg-color: rgba(255,255,255,0.08);
          --epr-highlight-color: #a855f7;
          --epr-text-color: rgba(255,255,255,0.92);
          --epr-search-border-color: rgba(255,255,255,0.14);
          --epr-category-label-bg-color: rgba(0,0,0,0.0);
        }
        .EmojiPickerReact, .EmojiPickerReact .epr-main, .EmojiPickerReact .epr-body {
          background: #0f0f16 !important;
          color: rgba(255,255,255,0.92) !important;
        }
        .EmojiPickerReact .epr-search-container input,
        .EmojiPickerReact input.epr-search {
          background: #0f0f16 !important;
          color: rgba(255,255,255,0.95) !important;
          border: 1px solid rgba(255,255,255,0.14) !important;
          border-radius: 12px !important;
        }
        .EmojiPickerReact .epr-search-container input::placeholder,
        .EmojiPickerReact input.epr-search::placeholder {
          color: rgba(255,255,255,0.45) !important;
        }
        .EmojiPickerReact .epr-category-nav, .EmojiPickerReact .epr-emoji-category-label {
          display: none !important; /* no internal tabs/categories bar */
        }
        .EmojiPickerReact .epr-preview { display: none !important; }
        .EmojiPickerReact .epr-emoji:hover { background: rgba(255,255,255,0.08) !important; }
        /* Remove ALL visible borders/separators inside the emoji picker */
        .EmojiPickerReact,
        .EmojiPickerReact .epr-main,
        .EmojiPickerReact .epr-body,
        .EmojiPickerReact .epr-header,
        .EmojiPickerReact .epr-search-container,
        .EmojiPickerReact .epr-search-container input,
        .EmojiPickerReact input.epr-search,
        .EmojiPickerReact .epr-emoji-list,
        .EmojiPickerReact .epr-emoji-category-label,
        .EmojiPickerReact .epr-category-nav,
        .EmojiPickerReact .epr-preview,
        .EmojiPickerReact .epr-skin-tones,
        .EmojiPickerReact .epr-skin-tones * {
          border: 0 !important;
          outline: 0 !important;
          box-shadow: none !important;
        }

        .EmojiPickerReact * {
          border-color: transparent !important;
          box-shadow: none !important;
        }

        /* Hide any thin separators that may remain */
        .EmojiPickerReact hr,
        .EmojiPickerReact .epr-hr,
        .EmojiPickerReact [class*="separator"],
        .EmojiPickerReact [class*="Separator"] {
          display: none !important;
        }

      `}</style>

      {/* Notification */}
      
      {/* Share modal */}
      {isShareModalOpen && (
        <div className="fixed inset-0 z-[1300] bg-black/60 flex items-center justify-center p-4 modal-text" onClick={() => !isPublishing && !isExporting && setIsShareModalOpen(false)}>
          <div
            className="bg-[#1a1a24] border border-white/10 rounded-xl w-full max-w-md p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-white font-bold text-lg">{ct('creator.share.title', 'Partager')}</div>
              <button
                onClick={() => !isPublishing && !isExporting && setIsShareModalOpen(false)}
                className={`w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors ${isPublishing || isExporting ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={isPublishing || isExporting}
              >
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>

            <p className="text-sm text-white/60 mb-4">
              {ct('creator.share.subtitle', 'Publie ta vidéo en story ou exporte le fichier.')}
            </p>

            {isPublishing && (
              <div className="mb-3">
                <div className="text-xs text-white/70 mb-2">{publishStatus || ct('creator.publish.progress', 'Publication…')}</div>
                {renderStageBars()}
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handlePublishStory}
                disabled={isPublishing || isExporting}
                className={`flex-1 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition ${isPublishing || isExporting ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isPublishing ? ct('creator.publish.progress', 'Publication…') : ct('creator.publish.action', 'Publier')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsShareModalOpen(false);
                  setIsExportModalOpen(true);
                }}
                disabled={isPublishing || isExporting}
                className={`flex-1 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 transition ${isPublishing || isExporting ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {ct('creator.export.action', 'Exporter')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 z-[1300] bg-black/60 flex items-center justify-center p-4 modal-text" onClick={() => !isExporting && setIsExportModalOpen(false)}>
          <div className="w-full max-w-md bg-[#15151b] border border-white/10 rounded-2xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-white font-bold text-lg">{ct('creator.export.title', 'Exporter')}</div>
              <button
                onClick={() => !isExporting && setIsExportModalOpen(false)}
                className={`w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors ${isExporting ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={isExporting}
                title={ct('common.close', 'Fermer')}
              >
                <X className="w-5 h-5 text-white/70" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="text-white/70 text-sm">{ct('creator.export.chooseFormat', 'Choisis le format de sortie :')}</div>

              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={() => setExportFormat('mp4')}
                  className="px-3 py-3 rounded-xl border text-left transition border-purple-500 bg-purple-500/10"
                >
                  <div className="text-white font-semibold">MP4</div>
                  <div className="text-white/60 text-xs">{ct('creator.export.serverFast', 'Export serveur (rapide)')}</div>
                </button>
              </div>

              {isExporting && (
                <div className="mt-2 space-y-3">
                  <div className="flex items-center justify-between text-xs text-white/70">
                    <span>{exportStatus || ct('creator.export.progress', 'Export…')}</span>
                    <span>{Math.round(exportProgress * 100)}%</span>
                  </div>
                  {renderStageBars()}
                  <button
                    type="button"
                    onClick={stopExportRender}
                    className="mt-3 w-full py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 transition"
                  >
                    {ct('common.cancel', 'Annuler')}
                  </button>
                </div>
              )}

              {!isExporting && exportUrl && (
                <button
                  type="button"
                  onClick={() => downloadExportFile(exportUrl)}
                  disabled={isDownloadingExport}
                  className={`mt-2 w-full py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 transition flex items-center justify-center gap-2 ${isDownloadingExport ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <Download className="w-4 h-4" />
                  {isDownloadingExport ? ct('creator.export.downloading', 'Téléchargement…') : ct('creator.export.downloadFile', 'Télécharger le fichier')}
                </button>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsExportModalOpen(false)}
                  disabled={isExporting}
                  className={`flex-1 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 transition ${isExporting ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {ct('common.close', 'Fermer')}
                </button>
                <button
                  type="button"
                  onClick={() => startServerRender({ mode: 'export' })}
                  disabled={isExporting}
                  className={`flex-1 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white rounded-xl transition font-semibold ${isExporting ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isExporting ? ct('creator.export.progress', 'Export…') : ct('creator.export.start', 'Lancer l’export')}
                </button>
              </div>

              <div className="text-[11px] text-white/40 leading-snug">
                {ct('creator.export.serverNote', 'Export serveur (FFmpeg) : pas de lecture locale pendant l’export.')}
              </div>
            </div>
          </div>
        </div>
      )}

{notification && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[200] px-4 py-2 bg-purple-600 text-white rounded-lg shadow-xl animate-pulse">
          {notification}
        </div>
      )}

      {/* Header */}
      <header className="h-14 relative flex items-center px-4 gap-4 flex-shrink-0 border-b border-white/10 overflow-x-auto">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600"></div>
        <div className="flex-1 min-w-0" />
        <div className="ml-auto flex items-center gap-2 flex-nowrap whitespace-nowrap">

          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setPreviewZoom((z) => Math.max(50, z - 10))}
              className="w-9 h-9 rounded-md hover:bg-white/10 flex items-center justify-center transition-colors"
              title={ct('creator.zoomOut', 'Zoom -')}
            >
              <Minus className="w-4 h-4 text-white/80" />
            </button>
            <div className="px-2 text-xs text-white/70 min-w-[44px] text-center select-none">
              {previewZoom}%
            </div>
            <button
              type="button"
              onClick={() => setPreviewZoom((z) => Math.min(200, z + 10))}
              className="w-9 h-9 rounded-md hover:bg-white/10 flex items-center justify-center transition-colors"
              title={ct('creator.zoomIn', 'Zoom +')}
            >
              <Plus className="w-4 h-4 text-white/80" />
            </button>
          </div>

          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setIsFormatPickerOpen((v) => !v)}
            data-tool-trigger="format"
            className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg font-semibold text-sm text-white/80 transition-colors"
            title={ct('creator.format.change', 'Changer le format')}
          >
            {projectFormat?.description}
          </button>

          {selectedTrack && (
            <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
              <div className="z-20 w-full max-w-full flex flex-nowrap items-center gap-1 bg-[#1a1a24] border border-white/20 rounded-xl px-2 py-1.5 shadow-2xl overflow-x-auto scrollbar-none whitespace-nowrap">
              {/* Volume control */}
              {(isVideoSegmentSelected || isAudioSelected) && (
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowVolumeSlider(!showVolumeSlider); setShowOpacitySlider(false); setShowFadeControls(false); }}
                    data-tool-trigger="volume"
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors ${showVolumeSlider ? 'bg-purple-600' : 'hover:bg-white/10'}`}
                  >
                    {getCurrentVolume() === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    <span className="text-xs">{getCurrentVolume()}%</span>
                  </button>
                  
                  {showVolumeSlider && !useGlobalToolPopovers && (
                    <div
                      className={`${isMobile ? 'fixed left-4 right-4' : 'absolute left-0 min-w-[200px]'} top-full mt-2 bg-[#1a1a24] border border-white/20 rounded-lg p-3 shadow-xl z-30`}
                      style={isMobile ? { top: 'calc(env(safe-area-inset-top, 0px) + 64px)' } : undefined}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <p className="text-xs text-white/60 mb-2">{ct('creator.volume.label', 'Volume')}</p>
                      <input type="range" min="0" max="100" value={getCurrentVolume()} onChange={(e) => handleVolumeChange(e.target.value)} className="w-full accent-purple-500 cursor-pointer" />
                      <p className="text-center text-sm text-white mt-2">{getCurrentVolume()}%</p>
                    </div>
                  )}
                </div>
              )}

              {/* Fade controls - Audio only */}
              {isAudioSelected && (
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowFadeControls(!showFadeControls); setShowVolumeSlider(false); setShowOpacitySlider(false); }}
                    data-tool-trigger="fade"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${showFadeControls ? 'bg-purple-600' : 'hover:bg-white/10'}`}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 20 L12 4 L22 20" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-xs">{ct('creator.fade.label', 'Fondu')}</span>
                  </button>
                  
                  {showFadeControls && !useGlobalToolPopovers && (
                    <div
                      className={`${isMobile ? 'fixed left-4 right-4' : 'absolute left-1/2 -translate-x-1/2 min-w-[280px]'} top-full mt-2 bg-[#1a1a24] border border-white/20 rounded-lg p-4 shadow-xl z-30`}
                      style={isMobile ? { top: 'calc(env(safe-area-inset-top, 0px) + 64px)' } : undefined}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <p className="text-sm text-white font-medium mb-4">{ct('creator.fade.title', 'Effets de fondu')}</p>
                      
                      {/* Fade In */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-white/60">{ct('creator.fade.in', "Fondu d'entrée")}</span>
                          <span className="text-xs text-purple-400">{(audioTracks.find(t => t.id === selectedTrack?.id)?.fadeIn || 0).toFixed(1)}s</span>
                        </div>
                        <input 
                          type="range" 
                          min="0" 
                          max="10" 
                          step="0.5"
                          value={audioTracks.find(t => t.id === selectedTrack?.id)?.fadeIn || 0} 
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            setAudioTracks(prev => prev.map(t => 
                              t.id === selectedTrack?.id ? { ...t, fadeIn: val } : t
                            ));
                          }} 
                          className="w-full accent-purple-500 cursor-pointer" 
                        />
                        <div className="flex justify-between text-[10px] text-white/40 mt-1">
                          <span>0s</span>
                          <span>10s</span>
                        </div>
                      </div>
                      
                      {/* Fade Out */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-white/60">{ct('creator.fade.out', 'Fondu de sortie')}</span>
                          <span className="text-xs text-purple-400">{(audioTracks.find(t => t.id === selectedTrack?.id)?.fadeOut || 0).toFixed(1)}s</span>
                        </div>
                        <input 
                          type="range" 
                          min="0" 
                          max="10" 
                          step="0.5"
                          value={audioTracks.find(t => t.id === selectedTrack?.id)?.fadeOut || 0} 
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            setAudioTracks(prev => prev.map(t => 
                              t.id === selectedTrack?.id ? { ...t, fadeOut: val } : t
                            ));
                          }} 
                          className="w-full accent-purple-500 cursor-pointer" 
                        />
                        <div className="flex justify-between text-[10px] text-white/40 mt-1">
                          <span>0s</span>
                          <span>10s</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="w-px h-6 bg-white/20 mx-1"></div>

              {/* Cut */}
              {isVideoSegmentSelected && (
                <button onClick={(e) => { e.stopPropagation(); handleCutVideo(); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors" title={ct('creator.timeline.cutAtPlayhead', 'Couper à la position actuelle')}>
                  <Scissors className="w-4 h-4" />
                  <span className="text-xs">{ct('creator.timeline.cut', 'Couper')}</span>
                </button>
              )}


              {/* Text controls */}
              {isTextSelected && selectedTextTrack && (
                <>
                  {/* Font family */}
                  <div className="flex items-center gap-2 px-2">
                    <select
                      value={selectedTextTrack.fontFamily || 'Arial'}
                      onChange={(e) => updateTextTrack(selectedTextTrack.id, { fontFamily: e.target.value })}
                      className="bg-transparent text-white text-sm px-2 py-1.5 rounded-lg border border-white/15 hover:border-white/25 focus:outline-none focus:ring-2 focus:ring-purple-600/40"
                                          style={{ fontFamily: selectedTextTrack.fontFamily || 'Arial' }}
                    >
                      {[
                        'Arial',
                        'Helvetica',
                        'Times New Roman',
                        'Georgia',
                        'Verdana',
                        'Trebuchet MS',
                        'Courier New',
                        'Impact',
                        'Comic Sans MS',
                        'Montserrat',
                        'Poppins',
                        'Roboto',
                        'Inter',
                      ].map((f) => (
                        <option key={f} value={f} className="bg-[#0f0f18]" style={{ fontFamily: f }}>{f}</option>
                      ))}
                    </select>

                    {/* Font size */}
                    <div className="flex items-center gap-1 border border-white/15 rounded-lg overflow-hidden">
                      <button
                        className="px-2 py-1.5 hover:bg-white/10"
                        onClick={() => updateTextTrack(selectedTextTrack.id, { fontSize: Math.max(8, (Number(selectedTextTrack.fontSize) || 24) - 2) })}
                        title={ct('creator.text.decrease', 'Diminuer')}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        min="8"
                        max="240"
                        value={Number(selectedTextTrack.fontSize) || 24}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (!Number.isFinite(v)) return;
                          updateTextTrack(selectedTextTrack.id, { fontSize: Math.min(240, Math.max(8, v)) });
                        }}
                        className="w-[70px] bg-transparent text-white text-sm text-center outline-none py-1.5"
                      />
                      <button
                        className="px-2 py-1.5 hover:bg-white/10"
                        onClick={() => updateTextTrack(selectedTextTrack.id, { fontSize: Math.min(240, (Number(selectedTextTrack.fontSize) || 24) + 2) })}
                        title={ct('creator.text.increase', 'Augmenter')}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Color */}
                  <button
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/10"
                    onClick={(e) => { e.stopPropagation(); }}
                    title={ct('creator.text.color', 'Couleur')}
                  >
                    <Palette className="w-4 h-4" />
                    <span className="text-xs">{ct('creator.text.color', 'Couleur')}</span>
                    <input
                      type="color"
                      value={selectedTextTrack.color || '#ffffff'}
                      onChange={(e) => updateTextTrack(selectedTextTrack.id, { color: e.target.value })}
                      className="ml-2 w-6 h-6 p-0 bg-transparent border-0 cursor-pointer"
                      style={{ background: 'transparent' }}
                      title={ct('creator.text.colorText', 'Couleur du texte')}
                    />
                  </button>

                  {/* Bold / Italic / Underline */}
                  <div className="flex items-center">
                    <button
                      className={`px-3 py-1.5 rounded-lg transition-colors ${Number(selectedTextTrack.fontWeight) >= 700 ? 'bg-purple-600' : 'hover:bg-white/10'}`}
                      onClick={() => {
                        const isBold = Number(selectedTextTrack.fontWeight) >= 700;
                        updateTextTrack(selectedTextTrack.id, { fontWeight: isBold ? 400 : 700 });
                      }}
                      title={ct('creator.text.bold', 'Gras')}
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      className={`px-3 py-1.5 rounded-lg transition-colors ${selectedTextTrack.italic ? 'bg-purple-600' : 'hover:bg-white/10'}`}
                      onClick={() => updateTextTrack(selectedTextTrack.id, { italic: !selectedTextTrack.italic })}
                      title={ct('creator.text.italic', 'Italique')}
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                    <button
                      className={`px-3 py-1.5 rounded-lg transition-colors ${selectedTextTrack.underline ? 'bg-purple-600' : 'hover:bg-white/10'}`}
                      onClick={() => updateTextTrack(selectedTextTrack.id, { underline: !selectedTextTrack.underline })}
                      title={ct('creator.text.underline', 'Souligné')}
                    >
                      <Underline className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Align */}
                  <div className="flex items-center border border-white/15 rounded-lg overflow-hidden">
                    <button
                      className={`px-3 py-1.5 transition-colors ${selectedTextTrack.align === 'left' ? 'bg-purple-600' : 'hover:bg-white/10'}`}
                      onClick={() => updateTextTrack(selectedTextTrack.id, { align: 'left' })}
                      title={ct('creator.text.alignLeft', 'Aligner à gauche')}
                    >
                      <AlignLeft className="w-4 h-4" />
                    </button>
                    <button
                      className={`px-3 py-1.5 transition-colors ${selectedTextTrack.align === 'center' ? 'bg-purple-600' : 'hover:bg-white/10'}`}
                      onClick={() => updateTextTrack(selectedTextTrack.id, { align: 'center' })}
                      title={ct('creator.text.alignCenter', 'Centrer')}
                    >
                      <AlignCenter className="w-4 h-4" />
                    </button>
                    <button
                      className={`px-3 py-1.5 transition-colors ${selectedTextTrack.align === 'right' ? 'bg-purple-600' : 'hover:bg-white/10'}`}
                      onClick={() => updateTextTrack(selectedTextTrack.id, { align: 'right' })}
                      title={ct('creator.text.alignRight', 'Aligner à droite')}
                    >
                      <AlignRight className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}

              {/* Crop */}
              {(isVideoSegmentSelected || isImageSelected || isTextSelected) && (
                <button onClick={(e) => { e.stopPropagation(); handleOpenCrop(); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors">
                  <Crop className="w-4 h-4" />
                  <span className="text-xs">{ct('creator.crop.title', 'Rogner')}</span>
                </button>
              )}

              {/* Transparency */}
              {(isVideoSegmentSelected || isImageSelected) && (
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowOpacitySlider(!showOpacitySlider); setShowVolumeSlider(false); }}
                    data-tool-trigger="opacity"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${showOpacitySlider ? 'bg-purple-600' : 'hover:bg-white/10'}`}
                  >
                    <Eye className="w-4 h-4" />
                    <span className="text-xs">{getCurrentOpacity()}%</span>
                  </button>
                  
                  {showOpacitySlider && !useGlobalToolPopovers && (
                    <div
                      className={`${isMobile ? 'fixed left-4 right-4' : 'absolute left-0 min-w-[200px]'} top-full mt-2 bg-[#1a1a24] border border-white/20 rounded-lg p-3 shadow-xl z-30`}
                      style={isMobile ? { top: 'calc(env(safe-area-inset-top, 0px) + 64px)' } : undefined}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <p className="text-xs text-white/60 mb-2">{ct('creator.opacity.label', 'Transparence')}</p>
                      <input type="range" min="0" max="100" value={getCurrentOpacity()} onChange={(e) => handleOpacityChange(e.target.value)} className="w-full accent-purple-500 cursor-pointer" />
                      <p className="text-center text-sm text-white mt-2">{getCurrentOpacity()}%</p>
                    </div>
                  )}
                </div>
              )}

              {/* Flip */}
              {(isVideoSegmentSelected || isImageSelected) && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); handleFlipHorizontal(); }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${(selectedSegment?.flipH || imageTracks.find(t => t.id === selectedTrack?.id)?.flipH) ? 'bg-purple-600' : 'hover:bg-white/10'}`}>
                    <FlipHorizontal className="w-4 h-4" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleFlipVertical(); }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${(selectedSegment?.flipV || imageTracks.find(t => t.id === selectedTrack?.id)?.flipV) ? 'bg-purple-600' : 'hover:bg-white/10'}`}>
                    <FlipVertical className="w-4 h-4" />
                  </button>
                </>
              )}

              <div className="w-px h-6 bg-white/20 mx-1"></div>

              {/* Delete */}
              <button onClick={(e) => { e.stopPropagation(); handleDeleteTrack(); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" />
                <span className="text-xs">{ct('common.delete', 'Supprimer')}</span>
              </button>
              </div>
            </div>
          )}

          {isFormatPickerOpen && !useGlobalToolPopovers && (
            <div
              className="absolute right-12 top-[calc(100%+10px)] w-80 bg-[#15151f] border border-white/10 rounded-xl shadow-2xl p-3 z-[200]"
              style={{ animation: 'formatPop 180ms ease-out' }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div className="text-white/70 text-xs font-semibold mb-2">{ct('creator.format.socialTitle', 'Formats réseaux sociaux')}</div>
              <div className="grid grid-cols-1 gap-2">
                {videoFormats.map((fmt) => (
                  <button
                    key={fmt.id}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => applyProjectFormat(fmt)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                      projectFormat?.id === fmt.id ? 'border-purple-500 bg-purple-500/10' : 'border-white/10 hover:bg-white/5'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-md bg-black/30/40 border border-white/10 flex items-center justify-center overflow-hidden">
                      <div style={{ width: 36, height: 36, aspectRatio: `${fmt.width} / ${fmt.height}` }} className="bg-white/10 rounded-sm"></div>
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-white text-sm font-semibold">{fmt.label}</div>
                      <div className="text-white/60 text-xs">{fmt.description}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/40" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => setIsShareModalOpen(true)}
            disabled={isExporting || !hasTimelineElements}
            title={!hasTimelineElements ? ct('creator.share.needElement', "Ajoute un élément (vidéo/image/texte) pour partager.") : undefined}
            className={`px-5 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold text-sm transition-colors inline-flex items-center gap-2 ${
              isExporting || !hasTimelineElements ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            <Download className="w-4 h-4" />
            <span className="hidden md:inline">{ct('creator.share.title', 'Partager')}</span>
          </button>
        </div>
      </header>

      {/* Tool popovers (rendered outside header to avoid clipping) */}
      {useGlobalToolPopovers && showVolumeSlider && (
        <div
          className={`fixed ${isMobile ? 'left-4 right-4' : 'left-1/2 -translate-x-1/2 w-[320px]'} bg-[#1a1a24] border border-white/20 rounded-lg p-3 shadow-xl z-[1400]`}
          style={{ top: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 64px)' : '80px' }}
          data-tool-popover="volume"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-xs text-white/60 mb-2">{ct('creator.volume.label', 'Volume')}</p>
          <input
            type="range"
            min="0"
            max="100"
            value={getCurrentVolume()}
            onChange={(e) => handleVolumeChange(e.target.value)}
            className="w-full accent-purple-500 cursor-pointer"
          />
          <p className="text-center text-sm text-white mt-2">{getCurrentVolume()}%</p>
        </div>
      )}

      {useGlobalToolPopovers && showFadeControls && (
        <div
          className={`fixed ${isMobile ? 'left-4 right-4' : 'left-1/2 -translate-x-1/2 w-[360px]'} bg-[#1a1a24] border border-white/20 rounded-lg p-4 shadow-xl z-[1400]`}
          style={{ top: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 64px)' : '80px' }}
          data-tool-popover="fade"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-sm text-white font-medium mb-4">{ct('creator.fade.title', 'Effets de fondu')}</p>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-white/60">{ct('creator.fade.in', "Fondu d'entrée")}</span>
              <span className="text-xs text-purple-400">{(audioTracks.find(t => t.id === selectedTrack?.id)?.fadeIn || 0).toFixed(1)}s</span>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={audioTracks.find(t => t.id === selectedTrack?.id)?.fadeIn || 0}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setAudioTracks(prev => prev.map(t =>
                  t.id === selectedTrack?.id ? { ...t, fadeIn: val } : t
                ));
              }}
              className="w-full accent-purple-500 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-white/40 mt-1">
              <span>0s</span>
              <span>10s</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-white/60">{ct('creator.fade.out', 'Fondu de sortie')}</span>
              <span className="text-xs text-purple-400">{(audioTracks.find(t => t.id === selectedTrack?.id)?.fadeOut || 0).toFixed(1)}s</span>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={audioTracks.find(t => t.id === selectedTrack?.id)?.fadeOut || 0}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setAudioTracks(prev => prev.map(t =>
                  t.id === selectedTrack?.id ? { ...t, fadeOut: val } : t
                ));
              }}
              className="w-full accent-purple-500 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-white/40 mt-1">
              <span>0s</span>
              <span>10s</span>
            </div>
          </div>
        </div>
      )}

      {useGlobalToolPopovers && showOpacitySlider && (
        <div
          className={`fixed ${isMobile ? 'left-4 right-4' : 'left-1/2 -translate-x-1/2 w-[320px]'} bg-[#1a1a24] border border-white/20 rounded-lg p-3 shadow-xl z-[1400]`}
          style={{ top: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 64px)' : '80px' }}
          data-tool-popover="opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-xs text-white/60 mb-2">{ct('creator.opacity.label', 'Transparence')}</p>
          <input
            type="range"
            min="0"
            max="100"
            value={getCurrentOpacity()}
            onChange={(e) => handleOpacityChange(e.target.value)}
            className="w-full accent-purple-500 cursor-pointer"
          />
          <p className="text-center text-sm text-white mt-2">{getCurrentOpacity()}%</p>
        </div>
      )}

      {useGlobalToolPopovers && isFormatPickerOpen && (
        <div
          className={`fixed ${isMobile ? 'left-4 right-4' : 'right-6 w-80'} bg-[#15151f] border border-white/10 rounded-xl shadow-2xl p-3 z-[1400]`}
          style={{ top: isMobile ? 'calc(env(safe-area-inset-top, 0px) + 64px)' : '72px', animation: 'formatPop 180ms ease-out' }}
          data-tool-popover="format"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="text-white/70 text-xs font-semibold mb-2">{ct('creator.format.socialTitle', 'Formats réseaux sociaux')}</div>
          <div className="grid grid-cols-1 gap-2">
            {videoFormats.map((fmt) => (
              <button
                key={fmt.id}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => applyProjectFormat(fmt)}
                className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                  projectFormat?.id === fmt.id ? 'border-purple-500 bg-purple-500/10' : 'border-white/10 hover:bg-white/5'
                }`}
              >
                <div className="w-12 h-12 rounded-md bg-black/30/40 border border-white/10 flex items-center justify-center overflow-hidden">
                  <div style={{ width: 36, height: 36, aspectRatio: `${fmt.width} / ${fmt.height}` }} className="bg-white/10 rounded-sm"></div>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-white text-sm font-semibold">{fmt.label}</div>
                  <div className="text-white/60 text-xs">{fmt.description}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/40" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mobile bottom tabs */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-[1200] bg-[#12121a]/95 border-t border-white/10 backdrop-blur">
        <div className="h-16 grid grid-cols-3">
          {menuItems.map((item) => {
            const isActive = selectedTool === item.id && drawerOpen;
            return (
              <button
                key={item.id}
                onClick={(e) => { e.stopPropagation(); openDrawer(item.id); }}
                className={`flex flex-col items-center justify-center gap-1 text-xs font-medium transition ${
                  isActive ? 'text-white' : 'text-white/70'
                }`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-white/70'}`} />
                <span className="text-[11px]">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left Sidebar (Canva-style) */}
        <aside className="hidden md:flex w-24 bg-[#12121a] border-r-0 flex-col flex-shrink-0">
          <nav className="py-2 flex flex-col gap-1 items-stretch">
            {menuItems.map((item) => {
              const isActive = selectedTool === item.id && drawerOpen;
              return (
                <button
                  key={item.id}
                  onClick={(e) => { e.stopPropagation(); openDrawer(item.id); }}
                  className={`mx-2 rounded-xl px-2 py-3 flex flex-col items-center justify-center gap-2 transition-all ${
                    isActive ? 'bg-white/10 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.12)]' : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon className="w-6 h-6" />
                  <span className="text-[11px] font-medium leading-none">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Drawer Panel */}
        <div
          className={`bg-[#0f0f16] border-r-0 flex-shrink-0 md:min-w-0 overflow-hidden transition-[width,height,opacity,transform] duration-300 ease-out fixed left-0 right-0 bottom-16 z-[1100] md:static md:bottom-auto md:left-auto md:right-auto md:z-auto w-full md:h-full md:translate-y-0 ${
            drawerOpen ? 'h-[60vh] opacity-100 translate-y-0 md:w-[360px]' : 'h-0 opacity-0 translate-y-full md:w-0'
          }`}
        >
          <div className={`h-full w-full md:overflow-hidden transition-[width,transform] duration-300 ease-out ${
            drawerOpen ? 'md:w-[360px] translate-y-0 md:translate-x-0' : 'md:w-0 translate-y-4 md:-translate-x-4'
          }`}>
            <div className="h-full flex flex-col">
              <div className="p-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-purple-500" />
                  <span className="text-sm font-semibold text-white">
                    {menuItems.find(m => m.id === selectedTool)?.label || ''}
                  </span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); closeDrawer(); }}
                  className="p-2 rounded-lg hover:bg-white/5 text-white/70 hover:text-white transition"
                  aria-label={ct('common.close', 'Fermer')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                {selectedTool === 'importer' && (
                  <div className="space-y-3">
                    {/* Icône de recherche supprimée */}

                    <div className="flex gap-2">
                      {[
                        { id: 'video', label: ct('creator.import.tabVideo', 'Vidéos') },
                        { id: 'image', label: ct('creator.import.tabImage', 'Images') },
                        { id: 'audio', label: ct('creator.import.tabAudio', 'Audio') },
                      ].map(t => (
                        <button
                          key={t.id}
                          onClick={(e) => { e.stopPropagation(); setImportTab(t.id); }}
                          className={`flex-1 rounded-xl px-3 py-2 text-sm transition ${
                            importTab === t.id ? 'bg-purple-600 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>

                    {importTab === 'video' && (
                      <div className="space-y-3">
                        <button
                          onClick={() => videoImportInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl py-2 transition"
                        >
                          <Upload className="w-4 h-4" />
                          {ct('creator.import.video', 'Importer une vidéo')}
                        </button>

                        <div className="grid grid-cols-2 gap-3">
                          {videoAssets.map(v => (
                            <button
                              key={v.id}
                              onClick={(e) => { e.stopPropagation(); addVideoAssetToTimeline(v); }}
                              className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 transition text-left"
                              title={ct('creator.timeline.clickToAdd', 'Cliquer pour ajouter à la timeline')}
                            >
                              <div className="aspect-video bg-black/30/30 relative">
                                {v.thumbnails?.[0] ? (
                                  <img src={v.thumbnails[0].dataUrl} alt={v.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-white/40 text-xs">{ct('creator.preview.label', 'Aperçu')}</div>
                                )}
                                <div className="absolute inset-0 bg-black/30/0 group-hover:bg-black/30/20 transition" />
                              </div>
                              <div className="p-2">
                                <div className="text-xs font-medium text-white line-clamp-1">{v.name}</div>
                                <div className="text-[11px] text-white/50">{formatTime(v.duration || 0)}</div>
                              </div>
                            </button>
                          ))}
                          {videoAssets.length === 0 && (
                            <div className="col-span-2 text-sm text-white/50 text-center py-6">
                              {ct('creator.import.noVideo', 'Aucune vidéo importée.')}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {importTab === 'image' && (
                      <div className="space-y-3">
                        <button
                          onClick={() => imageImportInputRef.current?.click()}
                          className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl py-2 transition"
                        >
                          <Upload className="w-4 h-4" />
                          {ct('creator.import.image', 'Importer une image')}
                        </button>

                        <div className="grid grid-cols-3 gap-3">
                          {imageAssets.map(img => (
                            <button
                              key={img.id} data-clip="1"
                              onClick={(e) => { e.stopPropagation(); addImageAssetToTimeline(img); }}
                              className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 transition"
                              title={ct('creator.timeline.clickToAdd', 'Cliquer pour ajouter à la timeline')}
                            >
                              <div className="aspect-square bg-black/30/20">
                                <img src={img.url} alt="" className="h-full w-full object-cover" style={{ opacity: (img.opacity ?? 100) / 100, transform: `${img.flipH ? 'scaleX(-1)' : ''} ${img.flipV ? 'scaleY(-1)' : ''}` }} />
                            {/* Resize handles */}
                            <div
                              className="absolute left-0 top-0 h-full w-1 cursor-ew-resize bg-yellow-400/20 hover:bg-yellow-400/35"
                              onPointerDown={(e) => handleImageResizeStart(e, img)}
                            />
                            <div
                              className="absolute right-0 top-0 h-full w-1 cursor-ew-resize bg-yellow-400/20 hover:bg-yellow-400/35"
                              onPointerDown={(e) => handleImageResizeEnd(e, img)}
                            />
                              </div>
                              <div className="p-2">
                                <div className="text-xs font-medium text-white line-clamp-1">{img.name}</div>
                              </div>
                            </button>
                          ))}
                          {imageAssets.length === 0 && (
                            <div className="col-span-3 text-sm text-white/50 text-center py-6">
                              {ct('creator.import.noImage', 'Aucune image importée.')}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {importTab === 'audio' && (
                      <div className="space-y-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowMusicModal(true); }}
                          className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl py-2 transition"
                        >
                          <Music className="w-4 h-4" />
                          {ct('creator.audio.addMusic', 'Ajouter une musique')}
                        </button>

                        <button
                          disabled={!hasTimelineElements}
                          onClick={(e) => { e.stopPropagation(); setVoiceoverArmed(true); }}
                          className={`w-full flex items-center justify-center gap-2 rounded-xl py-2 transition border ${hasTimelineElements ? "bg-white/5 hover:bg-white/10 text-white border-white/10" : "bg-white/5 text-white/30 border-white/5 cursor-not-allowed"}`}
                          title={hasTimelineElements
                            ? ct('creator.audio.voiceoverTitle', 'Ajouter une voix off (enregistrer sur le montage)')
                            : ct('creator.audio.voiceoverNeedElement', "Ajoute d'abord un élément (vidéo, image ou texte) dans la timeline")
                          }
                        >
                          <Mic className="w-4 h-4" />
                          {ct('creator.audio.addVoiceover', 'Ajouter une voix off')}
                        </button>


                        <div className="space-y-2">
                          {audioAssets.map(a => (
                            <button
                              key={a.id}
                              onClick={(e) => { e.stopPropagation(); addAudioAssetToTimeline(a); }}
                              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10 transition text-left"
                              title={ct('creator.timeline.clickToAdd', 'Cliquer pour ajouter à la timeline')}
                            >
                              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                                <Music className="w-5 h-5 text-white/70" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-white truncate">{a.title}</div>
                                <div className="text-xs text-white/50 truncate">{formatTime(a.duration || 0)}</div>
                              </div>
                              <Plus className="w-4 h-4 text-white/60" />
                            </button>
                          ))}
                          {audioAssets.length === 0 && (
                            <div className="text-sm text-white/50 text-center py-6">
                              {ct('creator.audio.noMusic', 'Aucune musique ajoutée.')}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedTool === 'texte' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); addTextToTimeline('title'); }}
                        className="w-full px-4 py-3 rounded-2xl bg-purple-600 hover:bg-purple-500 text-white text-left transition"
                      >
                        <div className="text-xl font-extrabold leading-tight">{ct('creator.text.addTitle', 'Ajouter un titre')}</div>
                        <div className="text-white/80 text-xs mt-1">{ct('creator.text.titleHint', 'Grand texte, style Canva')}</div>
                      </button>

                      <button
                        onClick={(e) => { e.stopPropagation(); addTextToTimeline('subtitle'); }}
                        className="w-full px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-left transition"
                      >
                        <div className="text-lg font-bold leading-tight">{ct('creator.text.addSubtitle', 'Ajouter un sous-titre')}</div>
                        <div className="text-white/60 text-xs mt-1">{ct('creator.text.subtitleHint', 'Texte moyen, lisible')}</div>
                      </button>

                      <button
                        onClick={(e) => { e.stopPropagation(); addTextToTimeline('body'); }}
                        className="w-full px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-left transition"
                      >
                        <div className="text-base font-semibold leading-tight">{ct('creator.text.addText', 'Ajouter du texte')}</div>
                        <div className="text-white/60 text-xs mt-1">{ct('creator.text.bodyHint', 'Paragraphe / description')}</div>
                      </button>
                    </div>

                    <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
                      <div className="text-xs text-white/60 mb-2">{ct('creator.text.tipTitle', 'Astuce')}</div>
                      <div className="text-sm text-white/80">
                        {ct('creator.text.tip', 'Double-clique sur le texte dans l’aperçu pour l’éditer. Étire le clip dans la timeline pour changer sa durée.')}
                      </div>
                    </div>
                  </div>
                )}

                {selectedTool === 'emojis' && (
                  <div className="space-y-3">

                    {EmojiPickerComponent ? (
                      <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0f0f16]">
                        <EmojiPickerComponent
                          width="100%"
                          height={520}
                          searchDisabled={false}
                          previewConfig={{ showPreview: false }}
                          theme="\dark\" categoriesDisabled={true}
                          onEmojiClick={(emojiData) => {
                            if (emojiData?.emoji) addEmojiToTimeline(emojiData.emoji);
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="text-white/60 text-xs leading-snug">
                          {ct('creator.emojis.tip', 'Pour avoir')} <b>{ct('creator.emojis.tipAll', 'tous')}</b> {ct('creator.emojis.tipRest', 'les emojis + recherche avancée, installe :')} <span className="text-white/80">npm i emoji-picker-react</span>
                        </div>

                        <div className="grid grid-cols-8 gap-2">
                          {builtInEmojis
                            .filter((e) => !emojiQuery || e.includes(emojiQuery))
                            .map((e) => (
                              <button
                                key={e}
                                onClick={(ev) => { ev.stopPropagation(); addEmojiToTimeline(e); }}
                                className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-xl"
                                title={e}
                              >
                                {e}
                              </button>
                            ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* NOTE: Placeholder message removed (was: "Contenu de ... bientôt disponible.") */}
              </div>
            </div>
          </div>
        </div>

        {/* Hidden inputs */}
        <input
          ref={videoImportInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={handleVideoImport}
        />
        <input
          ref={imageImportInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageImport}
        />
        <input
          ref={audioImportInputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={handleAudioImport}
        />

        {/* Main Preview Area */}
        <main className="flex-1 flex flex-col bg-[#0a0a10] min-w-0 relative">

          {/* Preview */}
          <div className={`flex-1 flex flex-col items-center justify-center p-4 min-h-0 bg-[#575757] ${isPreviewFullscreen ? "fixed inset-0 z-[999] bg-black p-0" : ""}`} onClick={() => { setSelectedTrack(null); setShowVolumeSlider(false); setShowOpacitySlider(false); }}>
            {(videoUrl || videoSegments.length > 0 || imageTracks.length > 0 || textTracks.length > 0) ? (
              <div
                ref={previewStageRef}
                className={`relative bg-black overflow-visible shadow-2xl cursor-pointer group ${isPreviewFullscreen ? "rounded-none" : "rounded-lg"}`}
                style={{
                  aspectRatio: projectAspect.css,
                  height: isPreviewFullscreen ? '100vh' : '60vh',
                  maxHeight: 520,
                  width: 'auto',
                  maxWidth: '95vw',
                  transform: `scale(${previewZoom / 100})`,
                  transformOrigin: 'center',
                }}
                onClick={(e) => { e.stopPropagation(); if (videoSegments.length > 0) setSelectedTrack({ id: videoSegments[0].id, type: 'video-segment' }); }}
              >
                <div className={`absolute inset-0 ${isPreviewFullscreen ? "rounded-none" : "rounded-lg"} overflow-hidden`}>
                  <div className="absolute inset-0 bg-black" />
                {/* Voice-over countdown overlay */}
                {voiceoverCountdown !== null && (
                  <div className="fixed inset-0 z-[1200] bg-black/50 backdrop-blur-sm flex items-center justify-center">
                    <div className="text-white text-[180px] font-black leading-none drop-shadow-2xl select-none">
                      {voiceoverCountdown}
                    </div>
                  </div>
                )}

{isPreviewFullscreen && (
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePreviewFullscreen(); }}
                    className="absolute top-4 right-4 z-[1000] w-10 h-10 rounded-xl bg-black/60 border border-white/15 hover:bg-black/80 flex items-center justify-center"
                    title={ct('creator.preview.exitFullscreen', 'Quitter le plein écran')}
                  >
                    <Minimize2 className="w-5 h-5 text-white" />
                  </button>
                )}

                {isPreviewFullscreen && (
                  <div
                    className="absolute inset-0 z-[900] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => { e.stopPropagation(); togglePlayPause(); }}
                    title={isPlaying ? ct('creator.preview.pause', 'Pause') : ct('creator.preview.play', 'Play')}
                  >
                    <div className="w-16 h-16 rounded-full bg-black/60 border border-white/15 flex items-center justify-center">
                      {isPlaying ? <Pause className="w-7 h-7 text-white" /> : <Play className="w-7 h-7 text-white" />}
                    </div>
                  </div>
                )}


                {isPreviewFullscreen && (
                  <div
                    className="absolute left-0 right-0 bottom-0 z-[950] p-4"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <div className="rounded-2xl bg-black/60 border border-white/10 px-4 py-3 backdrop-blur-sm">
                      <div className="flex items-center gap-3">
                        <span className="text-xs tabular-nums text-white/80 w-12 text-right">
                          {formatTime(currentTime)}
                        </span>

                        <input
                          type="range"
                          min={0}
                          max={montageDuration || 0}
                          step={0.05}
                          value={Math.min(currentTime, montageDuration || 0)}
                          onChange={(e) => {
                            const next = Math.min(parseFloat(e.target.value), montageDuration || 0);
                            seekTimelineTo(next);
                          }}
                          className="flex-1"
                        />

                        <span className="text-xs tabular-nums text-white/80 w-12">
                          {formatTime(montageDuration)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                
{(() => {
  const seg = (getActiveVideoSegment && getActiveVideoSegment()) || selectedSegment || null;
  if (!seg || !(videoUrl || activeVideoSrc)) return null;

  const isSelected = selectedTrack?.type === 'video-segment' && selectedTrack?.id === seg.id;

  const x = seg.x ?? 50;
  const y = seg.y ?? 50;
  const w = seg.w ?? 100;
  const h = seg.h ?? 100;

  const cropTop = seg.cropTop ?? 0;
  const cropRight = seg.cropRight ?? 0;
  const cropBottom = seg.cropBottom ?? 0;
  const cropLeft = seg.cropLeft ?? 0;

  const clip = (cropTop || cropRight || cropBottom || cropLeft)
    ? `inset(${cropTop}% ${cropRight}% ${cropBottom}% ${cropLeft}%)`
    : 'none';

  const flip = `${seg.flipH ? 'scaleX(-1)' : ''} ${seg.flipV ? 'scaleY(-1)' : ''}`.trim();

  return (
    <div
      className="absolute overflow-visible"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${w}%`,
        height: `${h}%`,
        transform: 'translate(-50%, -50%)',
        zIndex: 5,
        pointerEvents: 'auto',
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        // Capture pointer so we always receive the release even if the cursor leaves the element.
        try {
          e.currentTarget?.setPointerCapture?.(e.pointerId);
        } catch (_) {}
        if (!isSelected) {
          setSelectedTrack({ id: seg.id, type: 'video-segment' });
          revealClipInPreview(seg.timelineStart, seg.duration);
          return;
        }
        if (seg.locked) return;
        beginPointerAction({
          targetType: 'video',
          type: 'move',
          id: seg.id,
          startX: e.clientX,
          startY: e.clientY,
          startState: { x, y, w, h, cropTop, cropRight, cropBottom, cropLeft },
        });
      }}
                      onClick={(e) => { e.stopPropagation(); }}
>
      <div className="w-full h-full overflow-hidden">
        <video
          ref={videoRef}
          src={activeVideoSrc || videoUrl}
          className="w-full h-full object-contain bg-black"
          style={{
            opacity: (seg.opacity ?? 100) / 100,
            clipPath: clip,
            transform: flip || 'none',
          }}
          onLoadedMetadata={handleVideoLoaded}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => {
            // IMPORTANT: don't stop the whole timeline when the underlying <video> ends.
            // The timeline can contain text/images/other video segments after this clip.
            if (!isPlayingRef.current) return;

            const t = exportStartPerfRef.current ? Math.max(0, (performance.now() - exportStartPerfRef.current) / 1000) : Number(currentTimeRef.current || 0);
            const next = findNextPlayableStart(t + 0.0005);
            if (next != null) {
              setCurrentTimeSynced(next);
              syncAudioAtProjectTime(next);
              ensureVideoAtProjectTime(next);
              return;
            }

            // No more video content — keep the playhead running until the end of the montage
            // (so gaps are respected and the timeline doesn't snap back).
            return;
          }}
          aria-label={seg.altText || ct('creator.preview.videoLabel', 'Vidéo')}
        />
      </div>

    </div>
  );
})()}
            {/* Image overlays (rendered above the video during playback) */}
                {imageTracks
                  .filter((img) => {
                    const st = img.startTime ?? img.timelineStart ?? 0;
                    const dur = img.duration ?? 0;
                    return currentTime >= st && currentTime <= st + dur;
                  })
                  .sort((a, b) => ((a.lane ?? 0) - (b.lane ?? 0)) || (((a.startTime ?? a.timelineStart ?? 0)) - ((b.startTime ?? b.timelineStart ?? 0))))
                  .map((img) => (
                    <div
                      key={img.id} data-clip="1"
                      className="absolute"
                      style={{
                        left: `${img.x ?? 50}%`,
                        top: `${img.y ?? 50}%`,
                        width: `${img.w ?? 35}%`,
                        height: `${img.h ?? 35}%`,
                        transform: 'translate(-50%, -50%)',
                        opacity: (img.opacity ?? 100) / 100,
                        pointerEvents: 'auto',
                        zIndex: (20 + (img.lane ?? 0)) + ((selectedTrack?.type === 'image' && selectedTrack?.id === img.id) ? 100 : 0),
                      }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        e.currentTarget?.setPointerCapture?.(e.pointerId);
                        const isSelected = selectedTrack?.type === 'image' && selectedTrack?.id === img.id;
                        if (!isSelected) setSelectedTrack({ id: img.id, type: 'image' });
                        revealClipInPreview(img.startTime ?? img.timelineStart ?? 0, img.duration);
                        // Drag sur l'image = déplacer (comme Canva)
                        beginPointerAction({
                          targetType: 'image',
                          type: 'move',
                          id: img.id,
                          startX: e.clientX,
                          startY: e.clientY,
                          startState: {
                            x: img.x ?? 50,
                            y: img.y ?? 50,
                            w: img.w ?? 35,
                            h: img.h ?? 35,
                            cropTop: img.cropTop ?? 0,
                            cropRight: img.cropRight ?? 0,
                            cropBottom: img.cropBottom ?? 0,
                            cropLeft: img.cropLeft ?? 0,
                          },
                        });
                      }}
                    >
                      <img
                        src={img.url}
                        alt=""
                        className="w-full h-full object-cover"
                        style={{
                          transform: `${img.flipH ? 'scaleX(-1)' : ''} ${img.flipV ? 'scaleY(-1)' : ''}`.trim() || 'none',
                          clipPath:
                            (img.cropTop || img.cropRight || img.cropBottom || img.cropLeft)
                              ? `inset(${img.cropTop || 0}% ${img.cropRight || 0}% ${img.cropBottom || 0}% ${img.cropLeft || 0}%)`
                              : 'none',
                        }}
                      />

                    </div>))}

            {/* Text overlays */}
                {textTracks
                  .filter((t) => {
                    const st = t.startTime ?? 0;
                    const dur = t.duration ?? 0;
                    return currentTime >= st && currentTime <= st + dur;
                  })
                  .sort((a, b) => ((a.lane ?? 0) - (b.lane ?? 0)) || ((a.startTime ?? 0) - (b.startTime ?? 0)))
                  .map((t) => {
                    const isSelected = selectedTrack?.type === 'text' && selectedTrack?.id === t.id;
                    const isEditing = editingTextId === t.id;
                    return (
                      <div
                        key={t.id}
                        data-clip="1"
                        className="absolute"
                        style={{
                          left: `${t.x ?? 50}%`,
                          top: `${t.y ?? 50}%`,
                          width: `${t.w ?? 70}%`,
                          height: `${t.h ?? 20}%`,
                          transform: 'translate(-50%, -50%)',
                          pointerEvents: 'auto',
                          zIndex: (30 + (t.lane ?? 0)) + (isSelected ? 200 : 0),
                        }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          // If we're editing or clicking inside the editable text area, don't start a drag.
                          if (isEditing) return;
e.preventDefault();
                          e.currentTarget?.setPointerCapture?.(e.pointerId);
                          if (!isSelected) setSelectedTrack({ id: t.id, type: 'text' });
                          revealClipInPreview(t.startTime ?? 0, t.duration);
                          if (t.locked) return;
                          // Drag to move (Canva-like)
                          beginPointerAction({
                            targetType: 'text',
                            type: 'move',
                            id: t.id,
                            startX: e.clientX,
                            startY: e.clientY,
                            startState: { x: t.x ?? 50, y: t.y ?? 50, w: t.w ?? 70, h: t.h ?? 20 },
                          });
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setEditingTextId(t.id);
                          setSelectedTrack({ id: t.id, type: 'text' });
                          revealClipInPreview(t.startTime ?? 0, t.duration);
                        }}
                      >
                        <div
                          className="w-full h-full flex items-center justify-center"
                          style={{
                            background: t.backgroundColor || 'transparent',
                            opacity: (t.opacity ?? 100) / 100,
                            textAlign: t.align || 'center',
                            color: t.color || '#FFFFFF',
                            fontFamily: t.fontFamily || 'Arial',
                            fontSize: `${t.fontSize || 28}px`,
                            fontWeight: t.fontWeight ?? 600,
                            fontStyle: t.italic ? 'italic' : 'normal',
                            textDecoration: t.underline ? 'underline' : 'none',
                            lineHeight: 1.1,
                            padding: '6px 10px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            outline: 'none',
                            overflow: 'hidden',
                            cursor: t.locked ? 'default' : 'text',
                          
                            pointerEvents: isEditing ? 'auto' : 'none',}}
                          data-text-editable="1"
                          contentEditable={isEditing && !t.locked}
                          suppressContentEditableWarning
                          ref={(el) => {
                            if (el) textEditableRefs.current[t.id] = el;
                            else delete textEditableRefs.current[t.id];
                          }}
                          onPointerDown={(e) => {
                            if (isEditing) {
                              // When editing, keep caret interactions inside.
                              e.stopPropagation();
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isSelected) setSelectedTrack({ id: t.id, type: 'text' });
                          revealClipInPreview(t.startTime ?? 0, t.duration);
                          }}
                          onInput={(e) => {
                            if (!isEditing) return;
                            const val = e.currentTarget?.innerText ?? '';
                            pendingTextEditsRef.current[t.id] = val;
                          }}
                          onKeyDown={(e) => {
                            if (!isEditing) return;
                            // Enter = validate (like Canva). Shift+Enter keeps a newline.
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              e.currentTarget?.blur?.();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              // Revert pending edits
                              delete pendingTextEditsRef.current[t.id];
                              setEditingTextId(null);
                              // restore DOM text from state
                              try {
                                const el = textEditableRefs.current?.[t.id];
                                if (el) el.innerText = t.text || '';
                              } catch (_) {}
                            }
                          }}
                          onBlur={(e) => {
                            const val = (pendingTextEditsRef.current[t.id] ?? e.currentTarget?.innerText ?? '');
                            delete pendingTextEditsRef.current[t.id];
                            updateTextTrack(t.id, { text: val });
                            setEditingTextId(null);
                          }}
                        >
                          {t.text || ''}
                        </div>

                        {isSelected && !isPreviewFullscreen && (
                          <div className="absolute inset-0 border-2 border-purple-500 rounded-md pointer-events-none" />
                        )}

                        {isSelected && !t.locked && (
                          <>
                            {[
                              ['nw', 'left-0 top-0 -translate-x-1/2 -translate-y-1/2', 'nwse-resize'],
                              ['ne', 'right-0 top-0 translate-x-1/2 -translate-y-1/2', 'nesw-resize'],
                              ['sw', 'left-0 bottom-0 -translate-x-1/2 translate-y-1/2', 'nesw-resize'],
                              ['se', 'right-0 bottom-0 translate-x-1/2 translate-y-1/2', 'nwse-resize'],
                            ].map(([handle, cls, cur]) => (
                              <div
                                key={handle}
                                className={`absolute w-3 h-3 bg-white rounded-full shadow transform ${cls}`}
                                style={{ pointerEvents: 'auto', cursor: cur }}
                                onPointerDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  beginPointerAction({
                                    targetType: 'text',
                                    type: 'resize',
                                    handle,
                                    id: t.id,
                                    startX: e.clientX,
                                    startY: e.clientY,
                                    startState: { x: t.x ?? 50, y: t.y ?? 50, w: t.w ?? 70, h: t.h ?? 20 },
                                  });
                                }}
                              />
                            ))}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Selection overlays (outside clip) */}
                {(() => {
                  if (isPreviewFullscreen) return null;

                  const selectedVideo = selectedTrack?.type === 'video-segment'
                    ? (videoSegments.find((s) => s.id === selectedTrack.id) || null)
                    : null;
                  const selectedImage = selectedTrack?.type === 'image'
                    ? (imageTracks.find((t) => t.id === selectedTrack.id) || null)
                    : null;

                  const renderFrame = (frame, type) => {
                    if (!frame) return null;
                    const x = frame.x ?? 50;
                    const y = frame.y ?? 50;
                    const w = frame.w ?? (type === 'image' ? 35 : 100);
                    const h = frame.h ?? (type === 'image' ? 35 : 100);
                    const cropTop = frame.cropTop ?? 0;
                    const cropRight = frame.cropRight ?? 0;
                    const cropBottom = frame.cropBottom ?? 0;
                    const cropLeft = frame.cropLeft ?? 0;

                    return (
                      <div
                        className="absolute pointer-events-none overflow-visible"
                        style={{
                          left: `${x}%`,
                          top: `${y}%`,
                          width: `${w}%`,
                          height: `${h}%`,
                          transform: 'translate(-50%, -50%)',
                          zIndex: 300,
                        }}
                      >
                        <div
                          className="absolute border-2 border-purple-500 rounded-md pointer-events-none"
                          style={{
                            top: `${cropTop || 0}%`,
                            right: `${cropRight || 0}%`,
                            bottom: `${cropBottom || 0}%`,
                            left: `${cropLeft || 0}%`,
                          }}
                        >
                          {[
                            ['nw', 'left-0 top-0 -translate-x-1/2 -translate-y-1/2', 'nwse-resize'],
                            ['ne', 'right-0 top-0 translate-x-1/2 -translate-y-1/2', 'nesw-resize'],
                            ['sw', 'left-0 bottom-0 -translate-x-1/2 translate-y-1/2', 'nesw-resize'],
                            ['se', 'right-0 bottom-0 translate-x-1/2 translate-y-1/2', 'nwse-resize'],
                          ].map(([handle, cls, cursor]) => (
                            <div
                              key={`${type}-${handle}`}
                              className={`absolute w-3 h-3 bg-white rounded-full shadow transform ${cls}`}
                              style={{ pointerEvents: 'auto', cursor }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                try {
                                  e.currentTarget?.setPointerCapture?.(e.pointerId);
                                } catch (_) {}
                                beginPointerAction({
                                  targetType: type,
                                  type: 'resize',
                                  handle,
                                  id: frame.id,
                                  startX: e.clientX,
                                  startY: e.clientY,
                                  startState: { x, y, w, h, cropTop, cropRight, cropBottom, cropLeft },
                                });
                              }}
                            />
                          ))}

                          {[
                            ['left', 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2', 'ew-resize'],
                            ['right', 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2', 'ew-resize'],
                            ['top', 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2', 'ns-resize'],
                            ['bottom', 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2', 'ns-resize'],
                          ].map(([handle, cls, cursor]) => (
                            <div
                              key={`${type}-${handle}`}
                              className={`absolute w-3 h-3 bg-purple-500 rounded-full shadow transform ${cls}`}
                              style={{ pointerEvents: 'auto', cursor }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                try {
                                  e.currentTarget?.setPointerCapture?.(e.pointerId);
                                } catch (_) {}
                                beginPointerAction({
                                  targetType: type,
                                  type: 'crop',
                                  handle,
                                  id: frame.id,
                                  startX: e.clientX,
                                  startY: e.clientY,
                                  startState: { x, y, w, h, cropTop, cropRight, cropBottom, cropLeft },
                                });
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  };

                  return (
                    <>
                      {renderFrame(selectedVideo, 'video')}
                      {renderFrame(selectedImage, 'image')}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center">
                <div
                  className={`mx-auto mb-4 border-2 border-dashed rounded-xl flex flex-col items-center justify-center transition-colors cursor-pointer overflow-hidden ${
                    isDropActive
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-white/20 bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}
                  style={dropzoneStyle}
                  onClick={() => mediaImportInputRef.current?.click()}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDropActive(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = 'copy';
                    setIsDropActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDropActive(false);
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDropActive(false);
                    await importDroppedFiles(e.dataTransfer?.files);
                  }}
                >
                  <Upload className="w-14 h-14 text-white/30 mb-3" />
                  <p className="text-white/60 mb-1">{ct('creator.import.dropTitle', 'Importer une vidéo ou une image')}</p>
                  <p className="text-white/30 text-sm">{ct('creator.import.dropHint', 'ou glissez-déposez ici')}</p>
                </div>
                <input
                  ref={mediaImportInputRef}
                  type="file"
                  accept="video/*,image/*"
                  multiple
                  onChange={handleMediaImport}
                  className="hidden"
                />
              </div>
            )}
          </div>

          {/* Time display */}
          <div className="relative flex items-center justify-center gap-4 py-2 flex-shrink-0 border-t border-white/5">
            <span className="text-white/60 text-sm font-mono w-16 text-right">{formatTime(currentTime)}</span>
            <button onClick={togglePlayPause} className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform">
              {isPlaying ? <Pause className="w-5 h-5 text-black" /> : <Play className="w-5 h-5 text-black ml-0.5" />}
            </button>
            <span className="text-white/60 text-sm font-mono w-16">{formatTime(montageDuration)}</span>
          </div>

          {/* Timeline */}
          <div className="bg-[#12121a] border-t border-white/10 flex-shrink-0">
            <div
              ref={timelineContainerRef}
              className="overflow-x-auto overflow-y-auto"
              style={{ height: 200, maxHeight: 200, scrollbarWidth: 'thin', scrollbarColor: '#4a4a5a #1a1a24' }}
              onWheel={handleTimelineWheel}
            >
              <div ref={timelineRef} className="relative select-none" style={{ width: `${timelineWidth + TRACK_LABEL_WIDTH + 100}px`, minWidth: '100%' }} onPointerDownCapture={handleTimelinePointerDownCapture} onClick={handleTimelineClick} onPointerDown={handleTimelineScrubPointerDown} onPointerMove={(e) => { const p = timelinePressRef.current; if (p?.active) { if (Math.abs(e.clientX - p.x0) > 3 || Math.abs(e.clientY - p.y0) > 3) p.moved = true; } }} onPointerUp={() => { if (timelinePressRef.current) timelinePressRef.current.active = false; }} onPointerCancel={() => { if (timelinePressRef.current) timelinePressRef.current.active = false; }}>
                {/* Time scale */}
                <div className="h-7 flex items-end border-b border-white/10 sticky top-0 bg-[#12121a] z-10">
                  <div className="flex-shrink-0" style={{ width: `${TRACK_LABEL_WIDTH}px` }}></div>
                  <div className="relative h-full flex-1">
                    {generateTimeMarkers().map((time) => (
                      <div key={time} className="absolute bottom-0 flex flex-col items-center" style={{ left: `${getPositionFromTime(time)}px` }}>
                        <span className="text-[10px] text-white/50 mb-0.5">{formatTime(time)}</span>
                        <div className="w-px h-2 bg-white/30"></div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Playhead */}
                <div
                  className="absolute top-0 bottom-0 z-30 group touch-none select-none"
                  style={{ left: `${playheadPosition + TRACK_LABEL_WIDTH}px`, width: 32, marginLeft: -16 }}
                  onPointerDown={handlePlayheadMouseDown}
                  data-no-scrub="1"
                >
                  {/* Big invisible hitbox */}
                  <div className="absolute inset-0 cursor-ew-resize" />
                  {/* Actual marker centered */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 flex flex-col items-center pointer-events-none">
                    <div className="w-3 h-3 bg-red-500 rounded-sm shadow-lg group-hover:scale-110 transition-transform" style={{ clipPath: 'polygon(50% 100%, 0 0, 100% 0)' }}></div>
                    <div className="w-0.5 flex-1 bg-red-500 shadow-lg"></div>
                  </div>
                </div>

                {/* Tracks */}
                <div className="py-2 min-h-[180px]">
                  {/* Visual lanes (mix vidéo + images). Aucun chevauchement autorisé sur une même ligne. */}
                  {(() => {
                    const visualItems = [
                      ...videoSegments.map((s) => ({ kind: 'video', lane: s.lane ?? 0, data: s })),
                      ...imageTracks.map((t) => ({ kind: 'image', lane: t.lane ?? 0, data: t })),
                      ...textTracks.map((t) => ({ kind: 'text', lane: t.lane ?? 0, data: t })),
                    ];
                    const maxLane = Math.max(0, ...visualItems.map((i) => i.lane ?? 0));
                    const lanes = Array.from({ length: maxLane + 1 }, (_, i) => i);

                    return (
                      // NOTE: We intentionally allow scrubbing when clicking anywhere on the timeline background.
                      // Scrubbing is already prevented on actual interactive elements via data-clip="1"
                      // and data-no-scrub="1" on specific controls (ex: the playhead itself).
                      <div ref={visualLanesRef} className="flex flex-col border border-white/10 rounded-lg overflow-hidden">
                        {lanes.map((laneIndex) => {
                          const laneItems = visualItems.filter((i) => (i.lane ?? 0) === laneIndex);

                          return (
                            <div
                              key={laneIndex}
                              className="flex items-center h-16 border-b border-white/10 last:border-b-0"
                              data-track-row="1"
                              data-track-type="visual"
                              data-track-id={`lane-${laneIndex}`}
                              data-track-index={laneIndex}
                            >
                              <div
                                className="flex-shrink-0 px-3 py-2 text-sm flex items-center gap-2"
                                style={{ width: `${TRACK_LABEL_WIDTH}px` }}
                              >
                                <div className="w-2 h-2 rounded-full bg-white/30"></div>
                                <span className="text-white/70 font-medium truncate text-xs">
                                  {ct('creator.timeline.lane', 'Ligne {{index}}').replace('{{index}}', String(laneIndex + 1))}
                                </span>
                              </div>

                              <div className="relative flex-1 h-full bg-white/[0.02]">
                                {laneItems.map((item) => {
                                  if (item.kind === 'video') {
                                    const segment = item.data;
                                    return (
                                      <div
                                        key={segment.id}
                                        data-clip="1"
                                        className={`absolute h-14 rounded-lg overflow-hidden cursor-move transition-all ${
                                          selectedTrack?.id === segment.id ? 'ring-2 ring-white shadow-lg z-10' : 'hover:ring-1 hover:ring-white/50'
                                        }`}
                                        style={{
                                          left: `${getPositionFromTime(segment.timelineStart)}px`,
                                          width: `${Math.max(getPositionFromTime(segment.duration), MIN_CLIP_WIDTH_PX)}px`,
                                          backgroundColor: '#22d3ee30',
                                          borderColor: selectedTrack?.id === segment.id ? '#22d3ee' : '#22d3ee60',
                                          borderWidth: '2px',
                                        }}
                                        onContextMenu={(e) => openVideoSegmentContextMenu(e, segment)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedTrack({ id: segment.id, type: 'video-segment' });
                                          revealClipInPreview(segment.timelineStart, segment.duration);
                                        }}
                                      >
                                        {/* Left resize handle */}
                                        <div
                                          data-no-scrub="1"
                                          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-30 bg-transparent hover:bg-white/10 flex items-center justify-center"
                                          onPointerDown={(e) => {
                                            if (segment.locked) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              return;
                                            }
                                            handleVideoResizeStart(e, segment);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <div className="w-0.5 h-10 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                                        </div>

                                        {/* Main draggable area (prevents resize handles from starting a drag) */}
                                        <div
                                          data-no-scrub="1"
                                          className="absolute left-1 right-1 top-0 bottom-0 cursor-move z-20 touch-none select-none"
                                          onPointerDown={(e) => {
                                            if (segment.locked) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              return;
                                            }
                                            handleSegmentDragStart(e, segment);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        />

                                        {/* Right resize handle */}
                                        <div
                                          data-no-scrub="1"
                                          className="absolute right-0 top-0 bottom-0 w-1 cursor-ew-resize z-30 bg-transparent hover:bg-white/10 flex items-center justify-center"
                                          onPointerDown={(e) => {
                                            if (segment.locked) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              return;
                                            }
                                            handleVideoResizeEnd(e, segment);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <div className="w-0.5 h-10 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                                        </div>

                                        <div className="absolute inset-0">
                                          <div className="h-full w-full flex overflow-hidden">
                                            {renderSegmentThumbnailStrip(segment)}
                                          </div>
                                        </div>
                                        <div className="absolute bottom-0.5 right-1 text-[10px] text-white/70 font-mono bg-black/30/50 px-1 rounded z-30">
                                          {formatTime(segment.duration)}
                                        </div>
                                      </div>
                                    );
                                  }


                                  if (item.kind === 'text') {
                                    const txt = item.data;
                                    return (
                                      <div
                                        key={txt.id}
                                        data-clip="1"
                                        className={`absolute h-12 rounded-lg overflow-visible group ${
                                          selectedTrack?.type === 'text' && selectedTrack?.id === txt.id ? 'ring-2 ring-white shadow-lg' : ''
                                        }`}
                                        style={{
                                          left: `${getPositionFromTime(txt.startTime ?? 0)}px`,
                                          width: `${Math.max(getPositionFromTime(txt.duration), MIN_CLIP_WIDTH_PX)}px`,
                                          boxSizing: 'border-box',
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedTrack({ id: txt.id, type: 'text' });
                                          revealClipInPreview(txt.startTime ?? 0, txt.duration);
                                        }}
                                      >
                                        <div className="absolute inset-0 bg-pink-500/20 rounded-lg border border-pink-500/60 overflow-hidden flex items-center">
                                          <div className="px-1 text-[11px] font-semibold text-pink-200 truncate w-full">
                                          {txt.text || ct('creator.text.label', 'Texte')}
                                          </div>
                                        </div>

                                        <div
                                          data-no-scrub="1"
                                          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-20 bg-transparent hover:bg-white/10 flex items-center justify-center"
                                          onPointerDown={(e) => {
                                            e.stopPropagation();
                                            handleTextResizeStart(e, txt);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <div className="w-0.5 h-8 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                                        </div>

                                        <div
                                          data-no-scrub="1"
                                          className="absolute left-1 right-1 top-0 bottom-0 cursor-move z-10 touch-none select-none"
                                          onPointerDown={(e) => handleTrackDragStart(e, txt, 'text')}
                                        />

                                        <div
                                          data-no-scrub="1"
                                          className="absolute right-0 top-0 bottom-0 w-1 cursor-ew-resize z-20 bg-transparent hover:bg-white/10 flex items-center justify-center"
                                          onPointerDown={(e) => {
                                            e.stopPropagation();
                                            handleTextResizeEnd(e, txt);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <div className="w-0.5 h-8 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                                        </div>

                                        <div className="absolute bottom-0.5 right-1 text-[10px] text-white/80 font-mono bg-black/30/50 px-1 rounded">
                                          {formatTime(txt.duration)}
                                        </div>
                                      </div>
                                    );
                                  }

                                  // image
                                  const img = item.data;
                                  return (
                                    <div
                                      key={img.id}
                                      data-clip="1"
                                      className={`absolute h-12 rounded-lg overflow-visible group ${
                                        selectedTrack?.id === img.id ? 'ring-2 ring-white shadow-lg' : ''
                                      }`}
                                      style={{
                                        left: `${getPositionFromTime(img.startTime ?? img.timelineStart ?? 0)}px`,
                                        width: `${Math.max(getPositionFromTime(img.duration), MIN_CLIP_WIDTH_PX)}px`,
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTrack({ id: img.id, type: 'image' });
                                        revealClipInPreview(img.startTime ?? img.timelineStart ?? 0, img.duration);
                                      }}
                                    >
                                      <div className="absolute inset-0 bg-yellow-400/20 rounded-lg border border-yellow-400/50 overflow-hidden">
                                        <img
                                          src={img.url}
                                          alt=""
                                          className="h-full w-full object-cover pointer-events-none"
                                          style={{
                                            opacity: (img.opacity ?? 100) / 100,
                                            transform: `${img.flipH ? 'scaleX(-1)' : ''} ${img.flipV ? 'scaleY(-1)' : ''}`.trim() || 'none',
                                          }}
                                        />
                                      </div>

                                      <div
                                        data-no-scrub="1"
                                          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-20 bg-transparent hover:bg-white/10 flex items-center justify-center"
                                        onPointerDown={(e) => {
                                          e.stopPropagation();
                                          handleImageResizeStart(e, img);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="w-0.5 h-8 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                                      </div>

                                      <div
                                        data-no-scrub="1"
                                          className="absolute left-1 right-1 top-0 bottom-0 cursor-move z-10 touch-none select-none"
                                        onPointerDown={(e) => handleTrackDragStart(e, img, 'image')}
                                      />

                                      <div
                                        data-no-scrub="1"
                                          className="absolute right-0 top-0 bottom-0 w-1 cursor-ew-resize z-20 bg-transparent hover:bg-white/10 flex items-center justify-center"
                                        onPointerDown={(e) => {
                                          e.stopPropagation();
                                          handleImageResizeEnd(e, img);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="w-0.5 h-8 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                                      </div>

                                      <div className="absolute bottom-0.5 right-1 text-[10px] text-white/80 font-mono bg-black/30/50 px-1 rounded">
                                        {formatTime(img.duration)}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

{/* Audio Tracks */}
                  {audioTracks.map((track, index) => (
                    <div key={track.id} className="flex items-center h-14" data-track-row="1" data-track-type="audio" data-track-id={track.id} data-track-index={index}>
                      <div className="flex-shrink-0 px-3 py-2 text-sm flex items-center gap-2" style={{ width: `${TRACK_LABEL_WIDTH}px` }}>
                        <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                        <Music className="w-3 h-3 text-purple-400" />
                        <span className="text-purple-300 truncate text-xs">{track.name}</span>
                      </div>
                      <div className="relative flex-1 h-full">
                        {/* Full waveform background (entire audio) */}
                        <div
                          className="absolute h-12 rounded-lg overflow-hidden bg-[#1a1a2e]"
                          style={{ 
                            left: `${getPositionFromTime(track.startTime - (track.trimStart || 0))}px`, 
                            width: `${Math.max(getPositionFromTime(track.originalDuration), 20)}px` 
                          }}
                          onClick={(e) => { e.stopPropagation(); setSelectedTrack({ id: track.id, type: 'audio' }); }}
                        >
                          {/* Waveform visualization (full track - dimmed) */}
                          <div className="flex items-center gap-px h-full w-full px-1 opacity-40">
                            {getWaveformHeights(track.id, Math.min(120, Math.ceil(track.originalDuration * 2))).map((h, i) => (
                              <div key={i} className="flex-1 bg-blue-400/60 rounded-full min-w-[1px]" style={{ height: `${h}%` }} />
                            ))}
                          </div>
                        </div>
                        
                        {/* Selection overlay (playback zone) */}
                        <div
                          data-clip="1"
                          className={`absolute h-12 rounded-lg overflow-visible group ${selectedTrack?.id === track.id ? 'ring-2 ring-white shadow-lg' : ''}`}
                          style={{ 
                            left: `${getPositionFromTime(track.startTime)}px`, 
                            width: `${Math.max(getPositionFromTime(track.duration), 20)}px` 
                          }}
                          onClick={(e) => { e.stopPropagation(); setSelectedTrack({ id: track.id, type: 'audio' }); }}
                        >
                          {/* Blue selection background */}
                          <div className="absolute inset-0 bg-blue-500/50 rounded-lg border-2 border-purple-500">
                            {/* Highlighted waveform in selection */}
                            <div className="flex items-center gap-px h-full w-full px-1 overflow-hidden">
                              {getWaveformHeights(track.id, Math.min(80, Math.ceil(track.duration * 2))).map((h, i) => (
                              <div key={i} className="flex-1 bg-white/70 rounded-full min-w-[1px]" style={{ height: `${h}%` }} />
                            ))}
                            </div>
                          </div>
                          
                          {/* Left trim handle */}
                          <div 
                            data-no-scrub="1"
                                          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize z-20 bg-transparent hover:bg-white/10 flex items-center justify-center"
                            onPointerDown={(e) => handleAudioTrimStart(e, track)}
                          >
                            <div className="w-0.5 h-8 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                          </div>
                          
                          {/* Main draggable area (invisible, covers the selection) */}
                          <div 
                            data-no-scrub="1"
                                          className="absolute left-1 right-1 top-0 bottom-0 cursor-move z-10 touch-none select-none"
                            onPointerDown={(e) => handleTrackDragStart(e, track, 'audio')}
                          />
                          
                          {/* Right trim handle */}
                          <div 
                            data-no-scrub="1"
                                          className="absolute right-0 top-0 bottom-0 w-1 cursor-ew-resize z-20 bg-transparent hover:bg-white/10 flex items-center justify-center"
                            onPointerDown={(e) => handleAudioTrimEnd(e, track)}
                          >
                            <div className="w-0.5 h-8 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                          </div>
                        </div>
                        
                        <audio 
                          crossOrigin="anonymous"
                          ref={el => audioRefs.current[track.id] = el} 
                          src={track.url} 
                          preload="auto"
                          onError={(e) => console.error('Audio load error:', track.url, e)}
                        />
                      </div>
                    </div>
                  ))}

                  {/* Add track */}
                  <div className="flex items-center h-10">
                    <div className="flex-shrink-0" style={{ width: `${TRACK_LABEL_WIDTH}px` }}></div>
                    <button onClick={(e) => { e.stopPropagation(); setShowMusicModal(true); }} className="flex items-center gap-2 px-3 py-1.5 text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-colors text-sm">
                      <Plus className="w-4 h-4" /><span>{ct('creator.audio.addTrack', 'Ajouter une piste')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom controls */}

            {/* Voice-over recording bar (appears above footer controls) */}
            {voiceoverArmed && !isPreviewFullscreen && (
              <div className="h-12 flex items-center justify-center border-t border-white/10 bg-black/20">
                <button
                  onClick={() => {
                    if (isVoiceoverRecording) {
                      stopVoiceoverRecording();
                    } else {
                      startVoiceoverCountdown();
                    }
                  }}
                  disabled={!hasTimelineElements && !isVoiceoverRecording}
                  className={`px-5 py-2 rounded-xl font-semibold transition flex items-center gap-2 ${
                    isVoiceoverRecording
                      ? "bg-red-600 hover:bg-red-500 text-white"
                      : (hasTimelineElements ? "bg-red-600 hover:bg-red-500 text-white" : "bg-white/5 text-white/30 cursor-not-allowed")
                  }`}
                  title={isVoiceoverRecording ? ct('creator.voiceover.stopTitle', "Arrêter l'enregistrement") : ct('creator.voiceover.startTitle', "Démarrer l'enregistrement de voix off")}
                >
                  <div className={`w-2 h-2 rounded-full ${isVoiceoverRecording ? "bg-white" : "bg-white/80"}`} />
                  {isVoiceoverRecording ? ct('creator.voiceover.stop', 'Arrêter') : ct('creator.voiceover.recording', 'Enregistrement')}
                </button>
              </div>
            )}

            <div className="h-10 flex items-center justify-between px-4 border-t border-white/10">
              <div className="flex items-center gap-2">
                <button className="p-1.5 text-white/60 hover:text-white" onClick={() => setZoom(Math.max(25, zoom - 25))}><Minus className="w-4 h-4" /></button>
                <input type="range" min="25" max="200" value={zoom} onChange={(e) => setZoom(parseInt(e.target.value))} className="w-20 accent-purple-500" />
                <button className="p-1.5 text-white/60 hover:text-white" onClick={() => setZoom(Math.min(200, zoom + 25))}><Plus className="w-4 h-4" /></button>
                <span className="text-xs text-white/50 ml-1">{zoom}%</span>
              </div>
              
              
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePreviewFullscreen}
                  className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors"
                  title={isPreviewFullscreen ? ct('creator.preview.exitFullscreen', 'Quitter le plein écran') : ct('creator.preview.fullscreen', 'Plein écran (aperçu)')}
                >
                  {isPreviewFullscreen ? <Minimize2 className="w-4 h-4 text-white/80" /> : <Maximize2 className="w-4 h-4 text-white/80" />}
                </button>
                <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-white/5 text-white/80 text-[11px] font-semibold pointer-events-none">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="hidden md:inline">{ct('creator.brand', 'Creator Studio')}</span>
                </div>
              </div>
</div>
          </div>
        </main>

        
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black/30/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4 modal-text" onClick={() => setShowCropModal(false)}>
          <div className="bg-[#1a1a24] border border-white/10 rounded-xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold">{ct('creator.crop.title', 'Rogner')}</h2>
              <button onClick={() => setShowCropModal(false)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <CropControls segment={selectedSegment} imageTracks={imageTracks} selectedTrack={selectedTrack} onApply={handleApplyCrop} />
          </div>
        </div>
      )}

      {/* Music Modal */}
      {showMusicModal && (
        <div className="fixed inset-0 bg-black/30/80 backdrop-blur-sm z-[110] flex items-center justify-center p-4 modal-text" onClick={() => setShowMusicModal(false)}>
          <div className="bg-[#1a1a24] border border-white/10 rounded-xl w-full max-w-2xl max-h-[70vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-bold">{ct('creator.music.choose', 'Choisir une musique')}</h2>
              <button onClick={() => setShowMusicModal(false)} className="text-white/60 hover:text-white p-1.5 hover:bg-white/10 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-3 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={ct('creator.music.searchPlaceholder', 'Rechercher une musique ou un mood')}
                  className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
            <div className="flex gap-2 px-4 py-2 border-b border-white/10">
              {['all', 'music', 'moods'].map((filter) => (
                <button key={filter} onClick={() => setMusicFilter(filter)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${musicFilter === filter ? 'bg-purple-600 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`}>
                  {filter === 'all'
                    ? ct('creator.music.filterAll', 'Toutes')
                    : filter === 'music'
                    ? ct('creator.music.filterMusic', 'Musiques')
                    : ct('creator.music.filterMoods', 'Moods')}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-1">
                {filteredTracks.map((track) => (
                  <button key={track.id} onClick={() => handleAddMusic(track)} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors text-left group">
                    <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-white/10">
                      {track.cover_image ? <img src={track.cover_image} alt={track.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-sm">{track.cover_emoji || '🎵'}</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate group-hover:text-purple-400 transition-colors">{track.title}</p>
                      <p className="text-xs text-white/50 truncate">{track.artist || ct('creator.music.artist', 'Artiste')}</p>
                    </div>
                    {!userIsPremium && track.is_premium && (
                      <div className="flex items-center text-yellow-400 text-xs font-semibold px-2 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/30">
                        <Crown className="w-3 h-3" />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); startPreview(track); }}
                        className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                        title={previewTrackId === track.id && previewIsPlaying ? ct('creator.music.pause', 'Pause') : ct('creator.music.listen', 'Écouter')}
                      >
                        {previewTrackId === track.id && previewIsPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                      </button>
                      {previewTrackId === track.id && previewIsPlaying && (
                        <div className="flex items-end gap-0.5">
                          <span className="w-1 h-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-sm animate-equalizer-1" />
                          <span className="w-1 h-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-sm animate-equalizer-2" />
                          <span className="w-1 h-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-sm animate-equalizer-3" />
                        </div>
                      )}
                      <span className="text-xs text-white/50 font-mono">{formatTime(track.duration)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Premium Modal (Creator Studio) */}
      {showPremiumModal && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 modal-text">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowPremiumModal(false)}
          />
          <div className="relative bg-gradient-to-br from-zinc-900 to-black rounded-2xl shadow-2xl max-w-md w-full p-8 border border-yellow-500/30">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 flex items-center justify-center">
              <Crown className="w-10 h-10 text-yellow-500" />
            </div>
            <h3 className="text-2xl font-bold text-white text-center mb-3">{ct('creator.premium.title', 'Téléchargement Premium')}</h3>
            {premiumTrack && (
              <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 mb-4">
                {premiumTrack.cover_image ? (
                  <img
                    src={premiumTrack.cover_image}
                    alt={premiumTrack.title}
                    className="w-12 h-12 rounded object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-xl">
                    {premiumTrack.cover_emoji || '🎵'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">{premiumTrack.title}</div>
                  <div className="text-sm text-white/60 truncate">{premiumTrack.artist || ct('creator.music.artist', 'Artiste')}</div>
                </div>
              </div>
            )}
            <p className="text-white/70 text-center mb-6">
              {ct('creator.premium.message', 'Le téléchargement de cette piste est réservé aux abonnés Premium.')}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowPremiumModal(false);
                  if (onOpenSubscription) onOpenSubscription();
                }}
                className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-black font-bold transition-all"
              >
                {ct('creator.premium.viewPlans', 'Voir les abonnements')}
              </button>
              <button
                onClick={() => setShowPremiumModal(false)}
                className="w-full px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
              >
                {ct('common.later', 'Plus tard')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Timeline context menu (video segments) */}
      {contextMenu && (() => {
        const seg = getVideoSegmentById(contextMenu.segmentId);
        if (!seg) return null;

        const baseStyle = contextMenuPos ? { left: contextMenuPos.left, top: contextMenuPos.top } : { left: 0, top: 0 };

        const itemCls = "flex items-center justify-between gap-6 px-4 py-3 text-[15px] text-black hover:bg-black/5 cursor-pointer";
        const keyCls = "text-xs text-black/40 bg-black/5 px-2 py-1 rounded-md";

        return (
          <div
            ref={contextMenuRef}
            className="fixed z-[200] w-[320px] rounded-2xl bg-white shadow-2xl border border-black/10 overflow-hidden"
            style={baseStyle}
            onPointerDown={(e) => e.stopPropagation()}
            onContextMenu={(e) => { e.preventDefault(); }}
          >
            <div className={itemCls} onClick={() => { copySelectedVideoSegment(seg.id); setContextMenu(null); }}>
              <span>{ct('creator.context.copy', 'Copier')}</span><span className={keyCls}>⌘C</span>
            </div>
            <div className={itemCls} onClick={() => { copyVideoSegmentStyle(seg.id); setContextMenu(null); }}>
              <span>{ct('creator.context.copyStyle', 'Copier le style')}</span><span className={keyCls}>⇧⌘C</span>
            </div>
            <div
              className={`${itemCls} ${(!timelineClipboard && !styleClipboard) ? 'opacity-40 pointer-events-none' : ''}`}
              onClick={() => { if (timelineClipboard) pasteToTimelineAtPlayhead(); else applyCopiedStyleToVideoSegment(seg.id); setContextMenu(null); }}
            >
              <span>{ct('creator.context.paste', 'Coller')}</span><span className={keyCls}>⌘V</span>
            </div>
            <div className={itemCls} onClick={() => { duplicateVideoSegment(seg.id); setContextMenu(null); }}>
              <span>{ct('creator.context.duplicate', 'Dupliquer')}</span><span className={keyCls}>⌘D</span>
            </div>
            <div className={`${itemCls} border-b border-black/10`} onClick={() => { deleteVideoSegment(seg.id); setContextMenu(null); }}>
              <span>{ct('creator.context.delete', 'Effacer')}</span><span className={keyCls}>DELETE</span>
            </div>

            <div className={`${itemCls} border-b border-black/10`} onClick={() => { toggleLockVideoSegment(seg.id); setContextMenu(null); }}>
              <span>{seg.locked ? ct('creator.context.unlock', 'Déverrouiller') : ct('creator.context.lock', 'Verrouiller')}</span><span className={keyCls}>⌥⇧L</span>
            </div>

            {/* Calque */}
            <div
              className={`${itemCls} relative`}
              onPointerEnter={() => setContextSubmenu('layer')}
              onPointerLeave={() => setContextSubmenu(null)}
            >
              <span>{ct('creator.context.layer', 'Calque')}</span><span className="text-black/50">›</span>
              {contextSubmenu === 'layer' && (
                <div className="absolute left-full top-0 ml-2 w-[240px] rounded-2xl bg-white shadow-2xl border border-black/10 overflow-hidden">
                  <div className={itemCls} onClick={() => { setVideoSegmentZ(seg.id, 'front'); setContextMenu(null); }}>
                    <span>{ct('creator.context.layerFront', 'Mettre devant')}</span>
                  </div>
                  <div className={itemCls} onClick={() => { setVideoSegmentZ(seg.id, 'back'); setContextMenu(null); }}>
                    <span>{ct('creator.context.layerBack', 'Mettre derrière')}</span>
                  </div>
                  <div className={itemCls} onClick={() => { setVideoSegmentZ(seg.id, 'forward'); setContextMenu(null); }}>
                    <span>{ct('creator.context.layerForward', "Avancer d'un cran")}</span>
                  </div>
                  <div className={itemCls} onClick={() => { setVideoSegmentZ(seg.id, 'behind'); setContextMenu(null); }}>
                    <span>{ct('creator.context.layerBehind', "Reculer d'un cran")}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Aligner */}
            <div
              className={`${itemCls} relative`}
              onPointerEnter={() => setContextSubmenu('align')}
              onPointerLeave={() => setContextSubmenu(null)}
            >
              <span>{ct('creator.context.align', 'Aligner')}</span><span className="text-black/50">›</span>
              {contextSubmenu === 'align' && (
                <div className="absolute left-full top-0 ml-2 w-[240px] rounded-2xl bg-white shadow-2xl border border-black/10 overflow-hidden">
                  <div className={itemCls} onClick={() => { alignVideoSegment(seg.id, 'left'); setContextMenu(null); }}>
                    <span>{ct('creator.context.alignLeft', 'Gauche')}</span>
                  </div>
                  <div className={itemCls} onClick={() => { alignVideoSegment(seg.id, 'center'); setContextMenu(null); }}>
                    <span>{ct('creator.context.alignCenter', 'Centre')}</span>
                  </div>
                  <div className={itemCls} onClick={() => { alignVideoSegment(seg.id, 'right'); setContextMenu(null); }}>
                    <span>{ct('creator.context.alignRight', 'Droite')}</span>
                  </div>
                  <div className={`${itemCls} border-t border-black/10`} onClick={() => { alignVideoSegment(seg.id, 'top'); setContextMenu(null); }}>
                    <span>{ct('creator.context.alignTop', 'Haut')}</span>
                  </div>
                  <div className={itemCls} onClick={() => { alignVideoSegment(seg.id, 'middle'); setContextMenu(null); }}>
                    <span>{ct('creator.context.alignMiddle', 'Milieu')}</span>
                  </div>
                  <div className={itemCls} onClick={() => { alignVideoSegment(seg.id, 'bottom'); setContextMenu(null); }}>
                    <span>{ct('creator.context.alignBottom', 'Bas')}</span>
                  </div>
                </div>
              )}
            </div>

            <div className={itemCls} onClick={() => { addCommentToVideoSegment(seg.id); setContextMenu(null); }}>
              <span>{ct('creator.context.comment', 'Commenter')}</span><span className={keyCls}>⌥⌘N</span>
            </div>
            <div className={`${itemCls} border-b border-black/10`} onClick={() => { setAltTextForVideoSegment(seg.id); setContextMenu(null); }}>
              <span>{ct('creator.context.altText', 'Texte alternatif')}</span>
            </div>

            <div className={itemCls} onClick={() => { splitVideoSegmentAtPlayhead(seg.id); setContextMenu(null); }}>
              <span>{ct('creator.context.split', 'Diviser')}</span><span className={keyCls}>S</span>
            </div>
            <div className={itemCls} onClick={() => { setVideoSegmentAsBackground(seg.id); setContextMenu(null); }}>
              <span>{ct('creator.context.setBackground', 'Définir la vidéo comme arrière-plan')}</span>
            </div>
            <div className={itemCls} onClick={() => { extractAudioFromVideoSegment(seg.id); setContextMenu(null); }}>
              <span>{ct('creator.context.extractAudio', "Extraire l'audio")}</span>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

// Crop Controls Component
function CropControls({ segment, imageTracks, selectedTrack, onApply }) {
  const [crop, setCrop] = useState({
    top: segment?.cropTop || 0,
    right: segment?.cropRight || 0,
    bottom: segment?.cropBottom || 0,
    left: segment?.cropLeft || 0
  });

  return (
    <div className="space-y-4">
      {['top', 'bottom', 'left', 'right'].map((side) => (
        <div key={side}>
          <label className="text-sm text-white/60 mb-2 block capitalize">{side === 'top' ? 'Haut' : side === 'bottom' ? 'Bas' : side === 'left' ? 'Gauche' : 'Droite'} ({crop[side]}%)</label>
          <input type="range" min="0" max="40" value={crop[side]} onChange={(e) => setCrop(prev => ({ ...prev, [side]: parseInt(e.target.value) }))} className="w-full accent-purple-500" />
        </div>
      ))}
      <div className="flex gap-3 mt-6">
        <button onClick={() => setCrop({ top: 0, right: 0, bottom: 0, left: 0 })} className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">{ct('creator.crop.reset', 'Réinitialiser')}</button>
        <button onClick={() => onApply(crop)} className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors font-semibold flex items-center justify-center gap-2">
          <Check className="w-4 h-4" />{ct('creator.crop.apply', 'Appliquer')}
        </button>
      </div>
    </div>
  );
}
